import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
// Use the configured API instance instead of direct axios import
import { api, getApiUrl } from '../utils/apiConfig';
import { getUserData, getUserId, USER_STORAGE_KEY } from '../utils/userUtils';
import JsonTable from './JsonTable';
import './Chat.css';
import { DROPDOWN_ITEMS, getDropdownItems, SUPPORTED_ACTIONS, FOLLOW_UP_MESSAGES } from '../utils/constants';
import BriefcasePanel from './BriefcasePanel';
import MessageActionButtons from './MessageActionButtons';
import { canSendMessage, canUploadAttachment } from '../utils/permissions';
import { isUploadCreativesHidden } from '../utils/uploadCreativesGating';
import withAccountSelection from '../hocs/withAccountSelection';
import { useEnabledChannels } from '../hooks/useEnabledChannels';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { useAccount } from '../contexts/AccountContext';
import UnifiedOrchestrationMessage from './UnifiedOrchestrationMessage';
import {
  getOrchestrationConfig,
  isUnifiedOrchestrationAction,
  getPollingMessage,
  getPollingCompleteMessage,
  getPollingFailedMessage,
  getPollingStoppedMessage
} from '../utils/orchestrationConfigs';
import {
  canApplyStructuredOptimizerSuggestion,
  getOptimizerAnalysisView,
  getOptimizerPlatformDisplayName,
  getOptimizerSuggestionView,
  isGoogleAdsOptimizerPlatform,
  usesStructuredOptimizerTemplate
} from '../utils/optimizerResultPresentation';

// Helper function to detect platform from filename
// Only validates if filename contains platform AND "launch" AND "template"
const detectPlatformFromFilename = (filename) => {
  const lowerName = filename.toLowerCase();

  // Must contain both "launch" and "template"
  if (!lowerName.includes('launch') || !lowerName.includes('template')) {
    return null;
  }

  // Check for platform
  if (lowerName.includes('dv360') || lowerName.includes('dv_360')) {
    return 'dv360';
  }
  if (lowerName.includes('ttd') || lowerName.includes('tradedesk')) {
    return 'ttd';
  }

  return null;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);

  // Format date: Month DD, YYYY
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  };

  return date.toLocaleString('en-US', options);
};

// Format timestamp for display in message headers
const formatMessageTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  // For messages from today, just show the time
  if (isToday) {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // For messages from yesterday
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday at ${date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })}`;
  }

  // For messages from this year
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // For older messages
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }) + ' at ' + date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const Chat = ({ className, selectedChat, currentChatId, onChatHistoryChange, user, isReady }) => {
  const location = useLocation();
  const { getAccountIdToUse } = useAccount();

  // Get current account ID for dynamic channel fetching
  const currentAccountId = getAccountIdToUse();

  // Use the new hook for dynamic channel fetching
  const { enabledChannels } = useEnabledChannels(currentAccountId, user);

  // Get account settings for validation
  const { settings: accountSettings, isLoading: accountSettingsLoading } = useAccountSettings(currentAccountId, user);

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filteredDropdownItems, setFilteredDropdownItems] = useState(DROPDOWN_ITEMS);
  const dropdownRef = useRef(null);
  const [rejectionDialog, setRejectionDialog] = useState({
    open: false,
    reason: '',
    isRegenerating: false,
    messageId: null,
    message: null,
    sourceContent: null,
    file_metadata: null,
    references: null
  });
  const [askAssistantDialog, setAskAssistantDialog] = useState({
    open: false,
    question: '',
    isAsking: false,
    messageId: null,
    message: null,
    sourceContent: null,
    references: null
  });
  const [collapsedTables, setCollapsedTables] = useState(() => {
    try {
      const saved = localStorage.getItem(`collapsed_tables_${currentChatId}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (e) {
      return new Set();
    }
  });

  const [collapsedFastTrack, setCollapsedFastTrack] = useState(() => {
    try {
      const saved = localStorage.getItem(`collapsed_fast_track_${currentChatId}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (e) {
      return new Set();
    }
  });

  const [collapsedSalesforceOrchestration, setCollapsedSalesforceOrchestration] = useState(() => {
    try {
      const saved = localStorage.getItem(`collapsed_salesforce_orchestration_${currentChatId}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (e) {
      return new Set();
    }
  });
  const [references, setReferences] = useState([]);
  const [exportingMessageId, setExportingMessageId] = useState(null);
  const messagesEndRef = useRef(null);
  const pollIntervalsRef = useRef(new Map());
  const skipScrollRef = useRef(false);
  const isNewChatWithFileRef = useRef(false);
  // Add a ref to track if we're in the middle of creating a new chat
  const isCreatingNewChatRef = useRef(false);

  // Briefcase related state
  const [briefcaseVisible, setBriefcaseVisible] = useState(false); // Always start closed
  const [briefcaseFiles, setBriefcaseFiles] = useState([]);

  // Apply changes state
  const [applyingChanges, setApplyingChanges] = useState(null);

  // Store the messages in a ref to prevent loss during polling
  const messagesRef = useRef([]);

  // Update the ref whenever messages change - but don't create a dependency on messages
  // to avoid infinite loops
  const updateMessagesRef = useCallback((newMessages) => {
    if (newMessages && newMessages.length > 0) {
      messagesRef.current = [...newMessages];
    }
  }, []);

  // Update the ref whenever messages change, but without creating a dependency loop
  useEffect(() => {
    if (messages.length > 0) {
      // Use a function to update the ref to avoid creating a dependency on messages
      updateMessagesRef(messages);
    }
  }, [updateMessagesRef, messages.length]); // Only depend on length, not the entire messages array

  const pollFastTrackStatus = useCallback(async (chatId, messageId, userId) => {
    try {
      // Get the account ID using the getAccountIdToUse function from AccountContext
      const accountIdToUse = getAccountIdToUse();

      if (!accountIdToUse) {
        console.error('No valid accountId found for user in fast track polling:', user);
        throw new Error('No valid accountId found for fast track polling');
      }

      console.log(`Polling fast track status for chatId: ${chatId}, messageId: ${messageId}`);

      // Explicitly add accountId to the request
      const response = await api.get(getApiUrl(`/api/chat/${chatId}/${messageId}/fast-track-status`), {
        params: {
          userId,
          accountId: accountIdToUse
        }
      });

      const orchestrationData = response.data;

      // Get a fresh copy of the current messages
      const currentMessages = messagesRef.current.length > 0 ? [...messagesRef.current] : [];

      // Find the message to update
      const msgIndex = currentMessages.findIndex(msg => msg.id === messageId);

      if (msgIndex !== -1) {
        // Update the message with progress information
        currentMessages[msgIndex] = {
          ...currentMessages[msgIndex],
          content: `${getPollingMessage('fast-track', orchestrationData, getCurrentStepDisplay)}<stop-fast-track>${getOrchestrationConfig('fast-track').buttons.stop}</stop-fast-track>`,
          fastTrackStatus: orchestrationData
        };

        // If the fast track is complete, update the message with the final result
        if (orchestrationData.status === 'completed') {
          // Clear polling interval
          if (pollIntervalsRef.current.has(messageId)) {
            clearInterval(pollIntervalsRef.current.get(messageId));
            pollIntervalsRef.current.delete(messageId);
          }

          // Format the message exactly like the chat history response
          // The backend now returns the api_result field with the jsonResponse from the message
          currentMessages[msgIndex] = {
            ...currentMessages[msgIndex],
            content: getPollingCompleteMessage('fast-track'),
            isProcessing: false,
            status: 'completed',
            fastTrackStatus: orchestrationData,
            jsonResponse: orchestrationData.api_result || orchestrationData,
            timestamp: orchestrationData.updated_at
          };

          // When a fast track message is completed, add it to the collapsed set by default
          // This will ensure the progress bar and flow chart are collapsed initially
          const newCollapsed = new Set(collapsedFastTrack);
          newCollapsed.add(messageId);
          setCollapsedFastTrack(newCollapsed);
          try {
            localStorage.setItem(`collapsed_fast_track_${chatId}`, JSON.stringify([...newCollapsed]));
          } catch (e) {
            console.error('Failed to save fast track collapse state:', e);
          }

          // Update the messages ref with our modified copy
          messagesRef.current = currentMessages;

          // Update the UI with a single atomic operation
          setMessages(currentMessages);

          // Reset the creating new chat flag after polling completes
          isCreatingNewChatRef.current = false;
          return;
        }

        // If the fast track has failed, update the message with the error
        if (orchestrationData.status === 'failed') {
          // Clear polling interval
          if (pollIntervalsRef.current.has(messageId)) {
            clearInterval(pollIntervalsRef.current.get(messageId));
            pollIntervalsRef.current.delete(messageId);
          }

          currentMessages[msgIndex] = {
            ...currentMessages[msgIndex],
            content: getPollingFailedMessage('fast-track', orchestrationData.error_message),
            isProcessing: false,
            isError: true,
            fastTrackStatus: orchestrationData
          };

          // Update the messages ref with our modified copy
          messagesRef.current = currentMessages;

          // Update the UI with a single atomic operation
          setMessages(currentMessages);

          // Reset the creating new chat flag after polling completes
          isCreatingNewChatRef.current = false;
          return;
        }

        // Update the messages ref with our modified copy
        messagesRef.current = currentMessages;

        // Set skipScrollRef to true to prevent scrolling when updating fast track messages
        skipScrollRef.current = true;

        // Update the UI with a single atomic operation
        setMessages(currentMessages);
      }
    } catch (error) {
      console.error('Error polling fast track status:', error);

      // If we get a 404, it means the fast track orchestration hasn't been created yet
      // Just continue polling
      if (error.response && error.response.status === 404) {
        console.log('Fast track orchestration not found yet, continuing to poll');
        return;
      }

      // For other errors, clear the polling interval
      if (pollIntervalsRef.current.has(messageId)) {
        clearInterval(pollIntervalsRef.current.get(messageId));
        pollIntervalsRef.current.delete(messageId);
      }

      // Reset the creating new chat flag after polling error
      isCreatingNewChatRef.current = false;
    }
  }, [getAccountIdToUse, user]);

  const pollSalesforceOrchestrationStatus = useCallback(async (chatId, messageId, userId) => {
    try {
      // Get the account ID using the getAccountIdToUse function from AccountContext
      const accountIdToUse = getAccountIdToUse();

      if (!accountIdToUse) {
        console.error('No valid accountId found for user in Salesforce orchestration polling:', user);
        throw new Error('No valid accountId found for Salesforce orchestration polling');
      }

      console.log(`Polling Salesforce orchestration status for chatId: ${chatId}, messageId: ${messageId}`);

      // Explicitly add accountId to the request
      const response = await api.get(getApiUrl(`/api/chat/${chatId}/${messageId}/salesforce-orchestration-status`), {
        params: {
          userId,
          accountId: accountIdToUse
        }
      });

      const orchestrationData = response.data;

      // Get a fresh copy of the current messages
      const currentMessages = messagesRef.current.length > 0 ? [...messagesRef.current] : [];

      // Find the message to update
      const msgIndex = currentMessages.findIndex(msg => msg.id === messageId);

      if (msgIndex !== -1) {
        // Update the message with progress information
        currentMessages[msgIndex] = {
          ...currentMessages[msgIndex],
          content: getPollingMessage('salesforce-orchestration', orchestrationData, getCurrentStepDisplay),
          salesforceOrchestrationStatus: orchestrationData
        };

        // If the Salesforce orchestration is complete, update the message with the final result
        if (orchestrationData.status === 'completed') {
          // Clear polling interval
          if (pollIntervalsRef.current.has(messageId)) {
            clearInterval(pollIntervalsRef.current.get(messageId));
            pollIntervalsRef.current.delete(messageId);
          }

          currentMessages[msgIndex] = {
            ...currentMessages[msgIndex],
            content: getPollingCompleteMessage('salesforce-orchestration'),
            isProcessing: false,
            status: 'completed',
            salesforceOrchestrationStatus: orchestrationData,
            jsonResponse: orchestrationData.api_result || orchestrationData,
            timestamp: orchestrationData.updated_at
          };

          // When a Salesforce orchestration message is completed, add it to the collapsed set by default
          setCollapsedSalesforceOrchestration(prev => new Set([...prev, messageId]));
        } else if (orchestrationData.status === 'failed') {
          // Clear polling interval
          if (pollIntervalsRef.current.has(messageId)) {
            clearInterval(pollIntervalsRef.current.get(messageId));
            pollIntervalsRef.current.delete(messageId);
          }

          currentMessages[msgIndex] = {
            ...currentMessages[msgIndex],
            content: getPollingFailedMessage('salesforce-orchestration', orchestrationData.error_message),
            isProcessing: false,
            isError: true,
            salesforceOrchestrationStatus: orchestrationData
          };

          // Update the messages ref with our modified copy
          messagesRef.current = currentMessages;

          // Set skipScrollRef to true to prevent scrolling when updating Salesforce orchestration messages during polling
          skipScrollRef.current = true;

          // Update the UI with a single atomic operation
          setMessages(currentMessages);
          return;
        } else if (orchestrationData.status === 'stopped') {
          // Clear polling interval
          if (pollIntervalsRef.current.has(messageId)) {
            clearInterval(pollIntervalsRef.current.get(messageId));
            pollIntervalsRef.current.delete(messageId);
          }

          currentMessages[msgIndex] = {
            ...currentMessages[msgIndex],
            content: getPollingStoppedMessage('salesforce-orchestration'),
            isProcessing: false,
            salesforceOrchestrationStatus: orchestrationData
          };
        }

        // Update the messages ref with our modified copy
        messagesRef.current = currentMessages;

        // Set skipScrollRef to true to prevent scrolling when updating Salesforce orchestration messages during polling
        skipScrollRef.current = true;

        // Update the UI with a single atomic operation
        setMessages(currentMessages);
      }
    } catch (error) {
      console.error('Error polling Salesforce orchestration status:', error);

      // If we get a 404, it means the Salesforce orchestration hasn't been created yet
      // Just continue polling
      if (error.response && error.response.status === 404) {
        console.log('Salesforce orchestration not found yet, continuing to poll');
        return;
      }

      // For other errors, stop polling and show error
      if (pollIntervalsRef.current.has(messageId)) {
        clearInterval(pollIntervalsRef.current.get(messageId));
        pollIntervalsRef.current.delete(messageId);
      }

      // Update the message to show error state
      const currentMessages = messagesRef.current.length > 0 ? [...messagesRef.current] : [];
      const msgIndex = currentMessages.findIndex(msg => msg.id === messageId);

      if (msgIndex !== -1) {
        currentMessages[msgIndex] = {
          ...currentMessages[msgIndex],
          content: `Error polling Salesforce orchestration status: ${error.message}`,
          isProcessing: false,
          isError: true
        };

        messagesRef.current = currentMessages;

        // Set skipScrollRef to true to prevent scrolling when updating Salesforce orchestration messages during polling
        skipScrollRef.current = true;

        setMessages(currentMessages);
      }
    }
  }, [getAccountIdToUse, user]);

  const pollMessageStatus = useCallback(async (chatId, messageId, userId) => {
    try {
      // Get the account ID using the getAccountIdToUse function from AccountContext
      // This ensures we always use the latest selected account
      const accountIdToUse = getAccountIdToUse();

      if (!accountIdToUse) {
        console.error('No valid accountId found for user in polling:', user);
        throw new Error('No valid accountId found for polling');
      }

      console.log(`Polling message status for chatId: ${chatId}, messageId: ${messageId}`);

      // Explicitly add accountId to the request
      const response = await api.get(getApiUrl(`/api/chat/${chatId}/${messageId}/status`), {
        params: {
          userId,
          accountId: accountIdToUse
        }
      });
      const { status, result } = response.data;

      if (status === 'failed') {
        // Clear polling interval
        if (pollIntervalsRef.current.has(messageId)) {
          clearInterval(pollIntervalsRef.current.get(messageId));
          pollIntervalsRef.current.delete(messageId);
        }

        console.log(`Polling failed for messageId: ${messageId}`);

        // Get a fresh copy of the current messages
        const currentMessages = messagesRef.current.length > 0 ? [...messagesRef.current] : [];

        // Find the message to update
        const msgIndex = currentMessages.findIndex(msg => msg.id === messageId);

        if (msgIndex !== -1) {
          // Update the message in our local copy
          currentMessages[msgIndex] = {
            ...currentMessages[msgIndex],
            isProcessing: false,
            isError: true,
            chatId,
            messageId,
            timestamp: new Date().toISOString()
          };

          // Update the messages ref with our modified copy
          messagesRef.current = currentMessages;

          // Set skipScrollRef to true to prevent scrolling when updating messages during polling
          skipScrollRef.current = true;

          // Update the UI with a single atomic operation
          setMessages(currentMessages);
        } else {
          console.error(`Message with ID ${messageId} not found for error update`);
        }

        // Reset the creating new chat flag after polling completes
        isCreatingNewChatRef.current = false;
        return;
      }

      if (result) {
        // Clear polling interval
        if (pollIntervalsRef.current.has(messageId)) {
          clearInterval(pollIntervalsRef.current.get(messageId));
          pollIntervalsRef.current.delete(messageId);
        }

        console.log(`Polling completed for messageId: ${messageId}`);

        // Get the timestamp from the response
        const updatedTimestamp = response.data.result?.timestamp;
        console.log('Received updated timestamp from server in polling:', updatedTimestamp);

        // Get a fresh copy of the current messages
        const currentMessages = messagesRef.current.length > 0 ? [...messagesRef.current] : [];

        // Find the message to update
        const msgIndex = currentMessages.findIndex(msg => msg.id === messageId);

        if (msgIndex !== -1) {
          // Update the message in our local copy with the timestamp from the server
          currentMessages[msgIndex] = {
            ...currentMessages[msgIndex],
            content: result.content,
            jsonResponse: result.jsonResponse,
            llmSummary: result.llmSummary || currentMessages[msgIndex].llmSummary || null,
            sourceMessageId: result.sourceMessageId,
            isProcessing: false,
            timestamp: updatedTimestamp || currentMessages[msgIndex].timestamp
          };

          // Update the messages ref with our modified copy
          messagesRef.current = currentMessages;

          // Set skipScrollRef to true to prevent scrolling when updating messages during polling
          skipScrollRef.current = true;

          // Update the UI with a single atomic operation
          setMessages(currentMessages);
        } else {
          console.error(`Message with ID ${messageId} not found for success update`);

          // If the message isn't in the current array, try to find it in the stored messages
          // and add it to the current messages
          const message = {
            id: messageId,
            chatId,
            content: result.content,
            jsonResponse: result.jsonResponse,
            sourceMessageId: result.sourceMessageId,
            isProcessing: false,
            type: 'bot',
            timestamp: updatedTimestamp || new Date().toISOString() // Use the timestamp from the server if available
          };

          // Add the message to our local copy and update
          const updatedMessages = [...currentMessages, message];
          messagesRef.current = updatedMessages;

          // Set skipScrollRef to true to prevent scrolling when updating messages during polling
          skipScrollRef.current = true;

          setMessages(updatedMessages);
        }

        // Reset the creating new chat flag after polling completes
        isCreatingNewChatRef.current = false;
      }
    } catch (error) {
      console.error('Error polling message status:', error);
      if (pollIntervalsRef.current.has(messageId)) {
        clearInterval(pollIntervalsRef.current.get(messageId));
        pollIntervalsRef.current.delete(messageId);
      }
      // Reset the creating new chat flag after polling error
      isCreatingNewChatRef.current = false;
    }
  }, [getAccountIdToUse, user]);

  const startPollingMessage = useCallback((chatId, messageId) => {
    // If we're already polling this message, clear the existing interval
    if (pollIntervalsRef.current.has(messageId)) {
      clearInterval(pollIntervalsRef.current.get(messageId));
      pollIntervalsRef.current.delete(messageId);
    }

    console.log(`Starting polling for chatId: ${chatId}, messageId: ${messageId}`);

    // Make sure we have valid IDs before starting polling
    if (!chatId || !messageId) {
      console.error('Invalid chatId or messageId for polling', { chatId, messageId });
      return;
    }

    // Set a flag to prevent the useEffect from clearing messages during polling
    isCreatingNewChatRef.current = true;

    // Store the current messages state before polling starts - use a local copy to avoid dependency on messages
    const currentMessages = [...messages];
    if (currentMessages.length > 0) {
      console.log(`Storing ${currentMessages.length} messages before polling starts`);
      updateMessagesRef(currentMessages);
    }

    // Create a backup of the message being polled
    const messageBeingPolled = currentMessages.find(msg => msg.id === messageId);
    if (messageBeingPolled) {
      console.log('Found and stored message being polled:', messageBeingPolled.id);
    } else {
      console.warn('Message being polled not found in current messages state');
    }

    // Store the polling state in a dedicated object to avoid race conditions
    const pollingState = {
      chatId,
      messageId,
      isActive: true,
      lastKnownMessages: [...currentMessages]
    };

    const pollInterval = setInterval(async () => {
      try {
        if (!pollingState.isActive) {
          // If this polling has been deactivated, clear the interval
          clearInterval(pollInterval);
          return;
        }

        const { userId } = getUserData();
        if (!userId) {
          console.error('No valid userId for polling');
          return;
        }

        // Get the message to check its action
        const messageToCheck = messagesRef.current.find(msg => msg.id === messageId);

        // If this is a fast-track or fast-track-shell message, use the fast track polling function
        if (messageToCheck && (messageToCheck.action === 'fast-track' || messageToCheck.action === 'fast-track-shell')) {
          await pollFastTrackStatus(chatId, messageId, userId);
        } else if (messageToCheck && messageToCheck.action === 'salesforce-orchestration') {
          // If this is a salesforce-orchestration message, use the Salesforce orchestration polling function
          await pollSalesforceOrchestrationStatus(chatId, messageId, userId);
        } else {
          // Otherwise, use the regular polling function
          await pollMessageStatus(chatId, messageId, userId);
        }
      } catch (error) {
        console.error('Error in polling interval:', error);
        // Don't clear the interval here, let the polling functions handle that
      }
    }, 2000);

    pollIntervalsRef.current.set(messageId, pollInterval);
  }, [pollMessageStatus, pollFastTrackStatus, pollSalesforceOrchestrationStatus, updateMessagesRef]);

  const toggleTableCollapse = (index) => {
    const newCollapsed = new Set(collapsedTables);
    if (newCollapsed.has(index)) {
      newCollapsed.delete(index);
    } else {
      newCollapsed.add(index);
    }
    setCollapsedTables(newCollapsed);
    try {
      localStorage.setItem(`collapsed_tables_${currentChatId}`, JSON.stringify([...newCollapsed]));
    } catch (e) {
      console.error('Failed to save collapse state:', e);
    }

    // Also toggle the fast track content collapse state for the message
    const message = messages[index];
    if (message && (message.action === 'fast-track' || message.action === 'fast-track-shell') && message.fastTrackStatus) {
      const newFastTrackCollapsed = new Set(collapsedFastTrack);
      if (newCollapsed.has(index)) {
        // If the table is collapsed, also collapse the fast track content
        newFastTrackCollapsed.add(message.id);
      } else {
        // If the table is expanded, also expand the fast track content
        newFastTrackCollapsed.delete(message.id);
      }
      setCollapsedFastTrack(newFastTrackCollapsed);
      try {
        localStorage.setItem(`collapsed_fast_track_${currentChatId}`, JSON.stringify([...newFastTrackCollapsed]));
      } catch (e) {
        console.error('Failed to save fast track collapse state:', e);
      }
    }
  };

  const toggleFastTrackCollapse = (messageId) => {
    const newCollapsed = new Set(collapsedFastTrack);
    if (newCollapsed.has(messageId)) {
      newCollapsed.delete(messageId);
    } else {
      newCollapsed.add(messageId);
    }
    setCollapsedFastTrack(newCollapsed);
    try {
      localStorage.setItem(`collapsed_fast_track_${currentChatId}`, JSON.stringify([...newCollapsed]));
    } catch (e) {
      console.error('Failed to save fast track collapse state:', e);
    }
  };

  const toggleSalesforceOrchestrationCollapse = (messageId) => {
    const newCollapsed = new Set(collapsedSalesforceOrchestration);
    if (newCollapsed.has(messageId)) {
      newCollapsed.delete(messageId);
    } else {
      newCollapsed.add(messageId);
    }
    setCollapsedSalesforceOrchestration(newCollapsed);
    try {
      localStorage.setItem(`collapsed_salesforce_orchestration_${currentChatId}`, JSON.stringify([...newCollapsed]));
    } catch (e) {
      console.error('Failed to save Salesforce orchestration collapse state:', e);
    }
  };


  // Reset collapsed state when chat changes
  useEffect(() => {
    if (currentChatId) {
      try {
        const savedTables = localStorage.getItem(`collapsed_tables_${currentChatId}`);
        setCollapsedTables(savedTables ? new Set(JSON.parse(savedTables)) : new Set());

        const savedFastTrack = localStorage.getItem(`collapsed_fast_track_${currentChatId}`);
        setCollapsedFastTrack(savedFastTrack ? new Set(JSON.parse(savedFastTrack)) : new Set());

        const savedSalesforceOrchestration = localStorage.getItem(`collapsed_salesforce_orchestration_${currentChatId}`);
        setCollapsedSalesforceOrchestration(savedSalesforceOrchestration ? new Set(JSON.parse(savedSalesforceOrchestration)) : new Set());
      } catch (e) {
        setCollapsedTables(new Set());
        setCollapsedFastTrack(new Set());
        setCollapsedSalesforceOrchestration(new Set());
      }
    }
  }, [currentChatId]);

  // Cleanup polling intervals on unmount and chat change
  useEffect(() => {
    // Copy the ref value to a variable inside the effect
    const pollIntervals = pollIntervalsRef.current;

    // Log active polling intervals
    if (pollIntervals.size > 0) {
      console.log(`Active polling intervals: ${pollIntervals.size}`);
    }

    return () => {
      console.log(`Cleaning up ${pollIntervals.size} polling intervals`);
      pollIntervals.forEach(interval => clearInterval(interval));
      pollIntervals.clear();

      // Reset the creating new chat flag when cleaning up
      isCreatingNewChatRef.current = false;
    };
  }, [selectedChat]);

  // Additional cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clear all intervals and reset flags
      pollIntervalsRef.current.forEach(interval => clearInterval(interval));
      pollIntervalsRef.current.clear();
      isCreatingNewChatRef.current = false;
      isLoadingMessagesRef.current = false;
      isFirstRenderRef.current = true;
      console.log('Component unmounting, all polling intervals cleared');
    };
  }, []);

  // Filter dropdown items based on dynamically fetched enabled channels
  // and the account-level hide-upload-creatives setting. While the settings
  // fetch is in flight, keep upload items hidden so a flagged account never
  // sees them flash in.
  useEffect(() => {
    console.log('Chat: Updating filtered dropdown items with enabledChannels:', enabledChannels);
    setFilteredDropdownItems(getDropdownItems(enabledChannels, {
      hideUploadCreatives: accountSettingsLoading || isUploadCreativesHidden(accountSettings)
    }));
  }, [enabledChannels, accountSettings, accountSettingsLoading]);

  // Add a ref to track the last loaded chat ID to prevent duplicate API calls
  const lastLoadedChatIdRef = useRef(null);
  // Add a ref to track if this is the first render after navigation
  const isFirstRenderRef = useRef(true);
  // Add a ref to track if we're currently loading messages
  const isLoadingMessagesRef = useRef(false);

  // Function to refresh expired files for a chat
  const refreshExpiredFiles = useCallback(async (chatId) => {
    try {
      const accountIdToUse = getAccountIdToUse();
      const { userId } = getUserData();

      if (!accountIdToUse) {
        console.error('No valid accountId found for file refresh');
        return false;
      }

      if (!userId) {
        console.error('No valid userId found for file refresh');
        return false;
      }

      console.log(`Checking for expired files in chat: ${chatId}`);

      const response = await api.post(getApiUrl(`/api/file-metadata/refresh-files/${chatId}`), {
        accountId: accountIdToUse,
        userId: userId,
        expirationHours: 72 // Extend for 3 days when visiting
      });

      if (response.data.success && response.data.data.refreshedFilesCount > 0) {
        console.log(`Refreshed ${response.data.data.refreshedFilesCount} expired files in chat ${chatId}`);
        return true;
      }
      return false;
    } catch (error) {
      // Don't log 404 errors as they're expected for chats without files
      if (error.response && error.response.status !== 404) {
        console.error('Error refreshing expired files:', error);
      }
      return false;
    }
  }, [getAccountIdToUse]);

  // Reset isFirstRenderRef when the component unmounts
  useEffect(() => {
    return () => {
      isFirstRenderRef.current = true;
      isLoadingMessagesRef.current = false;
    };
  }, []);

  useEffect(() => {
    // If no account is selected (isReady is false), don't show any messages
    if (!isReady) {
      setMessages([]);
      setIsLoading(false);
      return;
    }

    if (!selectedChat) {
      setMessages([]);
      return;
    }

    // If we're in the middle of creating a new chat or polling, don't clear messages
    if (isCreatingNewChatRef.current) {
      console.log('Skipping message clear due to isCreatingNewChatRef flag');
      // Don't reset the flag here, it will be reset after polling completes
      return;
    }

    // If this is a new chat, reset the briefcase panel and don't clear messages
    // This allows us to keep the messages when sending the first message in a new chat
    if (selectedChat.isNew) {
      console.log('New chat selected, keeping messages');
      setBriefcaseVisible(false);
      setBriefcaseFiles([]);
      // Don't clear messages or show loading spinner for new chats
      return;
    }

    // Check if we have any active polling intervals
    const hasActivePolling = pollIntervalsRef.current.size > 0;
    if (hasActivePolling) {
      console.log('Skipping message clear due to active polling');

      // Always ensure we have the latest messages from the ref
      // This is more reliable than checking if messages array is empty
      if (messagesRef.current.length > 0) {
        console.log('Ensuring messages are up to date during active polling');
        setMessages([...messagesRef.current]);
      }

      return;
    }

    // Check if we're switching to the same chat that we have messages for
    if (selectedChat.id && messages.length > 0 && messages[0].chatId === selectedChat.id) {
      console.log('Already showing messages for this chat, skipping reload');
      return;
    }

    console.log('Loading existing chat:', selectedChat.id);

    // Store current messages before clearing them
    if (messages.length > 0) {
      messagesRef.current = [...messages];
    }

    // For existing chats, clear messages and show loading spinner immediately
    setMessages([]);
    setIsLoading(true);

    // If this is the first render after navigation or we're coming from another page,
    // reset the lastLoadedChatIdRef to force a reload of the chat messages
    if ((isFirstRenderRef.current && selectedChat.id) ||
        (location.state && location.state.fromOtherPage)) {
      // Only reset if we're not already loading this chat
      if (lastLoadedChatIdRef.current !== selectedChat.id) {
        lastLoadedChatIdRef.current = null;
      }
      isFirstRenderRef.current = false;
    }

    // Don't close briefcase panel when switching chats

    const loadMessages = async (showLoadingSpinner = true) => {
      // If we're already loading messages, don't start another load
      if (isLoadingMessagesRef.current) {
        return;
      }

      try {
        // Set the loading flag to prevent duplicate API calls
        isLoadingMessagesRef.current = true;

        // Clear existing polling intervals when loading new messages
        // But only if we're not in the middle of creating a new chat
        if (!isCreatingNewChatRef.current) {
          pollIntervalsRef.current.forEach(interval => clearInterval(interval));
          pollIntervalsRef.current.clear();
        }

        // Only show loading spinner if requested
        if (showLoadingSpinner) {
          setIsLoading(true);
        }

        // If we have messages but no currentChatId, it's likely a new chat that hasn't been saved yet
        // In this case, we don't need to load messages from the server
        if (messages.length > 0 && !currentChatId) {
          setIsLoading(false);
          isLoadingMessagesRef.current = false;
          return;
        }

        // Only attempt to load messages if we have a valid currentChatId
        if (currentChatId) {
          // Skip if we've already loaded this chat to prevent duplicate API calls
          if (lastLoadedChatIdRef.current === currentChatId) {
            setIsLoading(false);
            isLoadingMessagesRef.current = false;
            return;
          }

          // Update the last loaded chat ID
          lastLoadedChatIdRef.current = currentChatId;

          const { userId } = getUserData();

          // Get the account ID using the getAccountIdToUse function from AccountContext
          // This ensures we always use the latest selected account
          const accountIdToUse = getAccountIdToUse();

          if (!accountIdToUse) {
            console.error('No valid accountId found for user in chat history:', user);
            throw new Error('No valid accountId found for chat history');
          }

          console.log(`Loading chat history for chatId: ${currentChatId}, accountId: ${accountIdToUse}`);

          // Explicitly add accountId to the request
          const response = await api.get(getApiUrl(`/api/chat-history/${currentChatId}`), {
            params: {
              userId,
              accountId: accountIdToUse
            }
          });

          const fetchedMessages = response.data.messages.map(msg => ({
            ...msg,
            isProcessing: msg.status === 'processing',
            chatId: msg.chatId || currentChatId,
            messageId: msg.messageId || msg.id
          }));

          // If we're just updating for briefcase, don't trigger a scroll
          if (!showLoadingSpinner) {
            skipScrollRef.current = true;
          }

          setMessages(fetchedMessages);

          // Start polling for any processing messages
          fetchedMessages.forEach(msg => {
            if (msg.isProcessing) {
              startPollingMessage(msg.chatId, msg.messageId);
            }
          });

          // After loading messages, refresh any expired files
          // Do this asynchronously to not block the UI
          setTimeout(async () => {
            try {
              const filesRefreshed = await refreshExpiredFiles(currentChatId);
              if (filesRefreshed) {
                console.log('Files were refreshed, reloading messages to get updated URLs');
                // Reload messages to get updated file URLs
                // Use a small delay to ensure the backend has processed the updates
                setTimeout(() => {
                  // Reset the last loaded chat ID to force a reload
                  lastLoadedChatIdRef.current = null;
                  loadMessages(false); // Reload without spinner
                }, 500);
              }
            } catch (error) {
              console.error('Error in background file refresh:', error);
            }
          }, 100);
        }
      } catch (error) {
        console.error('Error loading messages:', error);
      } finally {
        setIsLoading(false);
        isLoadingMessagesRef.current = false;
      }
    };

    // Use a small timeout to debounce multiple rapid calls
    const debounceTimeout = setTimeout(() => {
      loadMessages();
    }, 50);

    // Clean up the timeout on unmount or when dependencies change
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [selectedChat, currentChatId, startPollingMessage, isReady, refreshExpiredFiles]);

  const updateMessageStatus = async (messageId, status, feedback = null) => {
    try {
      skipScrollRef.current = true;
      const { userId } = getUserData();

      // Get the account ID using the getAccountIdToUse function from AccountContext
      // This ensures we always use the latest selected account
      const accountIdToUse = getAccountIdToUse();

      // Explicitly add accountId to the request
      await api.post(getApiUrl(`/api/chat/${currentChatId}/${messageId}/status`), {
        status,
        feedback,
        userId,
        accountId: accountIdToUse
      });

      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.id === messageId
            ? { ...msg, status, feedback }
            : msg
        )
      );
    } catch (error) {
      console.error('Error updating message status:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    // Only scroll to bottom if not skipping scroll
    if (!skipScrollRef.current) {
      scrollToBottom();
    }
    skipScrollRef.current = false;
  }, [messages]);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    // Don't allow file uploads if no account is selected
    if (!isReady) return;

    setIsUploading(true);
    try {
      // Process each file sequentially to avoid overwhelming the server
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        // Always send chatId if it exists
        if (currentChatId) {
          formData.append('chatId', currentChatId);
        } else {
          // Mark that we have a file but no chat ID yet
          isNewChatWithFileRef.current = true;
        }
        // Get the account ID using the getAccountIdToUse function from AccountContext
        // This ensures we always use the latest selected account
        const accountIdToUse = getAccountIdToUse();

        // Add accountId to the form data
        if (accountIdToUse) {
          formData.append('accountId', accountIdToUse);
        }

        // Add userId to the form data
        const userId = getUserId();
        if (userId) {
          formData.append('userId', userId);
        }

        const uploadResponse = await api.post(getApiUrl('/api/upload'), formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Add the uploaded file to the array
        const fileData = {
          name: file.name,
          size: file.size,
          gcsPath: uploadResponse.data.data.gcsPath,
          publicUrl: uploadResponse.data.data.publicUrl,
          expiresAt: uploadResponse.data.data.expiresAt,
          pinned: uploadResponse.data.data.pinned || false,
          type: file.type // Store the file type for later use
        };

        // Check if validation is enabled and file is Excel launch template
        const isExcel = file.name.match(/\.(xlsx|xls|xlsm)$/i);
        const platform = detectPlatformFromFilename(file.name);

        // Only validate if:
        // 1. File is Excel
        // 2. Account setting 'validate-excel' is 'true'
        // 3. Filename contains 'dv360' or 'ttd' (launch template)
        if (isExcel && platform && accountSettings?.['validate-excel'] === 'true') {
          try {
            console.log(`Running Excel validation for: ${file.name} (platform: ${platform})`);

            // Show validation in progress
            setIsValidating(true);

            const validationResponse = await api.post(
              getApiUrl('/api/upload/validate-excel'),
              {
                gcsPath: fileData.gcsPath,
                platform: platform,
                accountId: accountIdToUse
              }
            );

            if (validationResponse.data.validation) {
              fileData.validation = validationResponse.data.validation;

              // Show chat message for errors or warnings
              const { valid, errors, warnings } = validationResponse.data.validation;

              if (!valid && errors.length > 0) {
                console.error('Validation errors:', errors);
                setMessages(prev => [...prev, {
                  id: `validation_error_${Date.now()}`,
                  content: `❌ Validation failed for ${file.name}:\n${errors.join('\n')}`,
                  timestamp: new Date().toISOString(),
                  type: 'error'
                }]);
              } else if (warnings.length > 0) {
                console.warn('Validation warnings:', warnings);
                setMessages(prev => [...prev, {
                  id: `validation_warning_${Date.now()}`,
                  content: `⚠️ Validation warnings for ${file.name}:\n${warnings.join('\n')}`,
                  timestamp: new Date().toISOString(),
                  type: 'warning'
                }]);
              }
              // Don't show chat messages for success
            }

            setIsValidating(false);
          } catch (validationError) {
            console.error('Validation failed (non-blocking):', validationError);
            // Don't show chat message for timeout or network errors
            setIsValidating(false);
          }
        } else if (isExcel && !platform) {
          console.log(`Skipping validation for ${file.name} - not a launch template (no dv360/ttd in filename)`);
        }

        // Add or replace the file in the uploadedFiles array
        setUploadedFiles(prev => {
          const existingIndex = prev.findIndex(f => f.name === fileData.name);
          if (existingIndex !== -1) {
            // Replace existing file
            const newFiles = [...prev];
            newFiles[existingIndex] = fileData;
            return newFiles;
          } else {
            // Add new file
            return [...prev, fileData];
          }
        });
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        content: 'Error uploading files. Please try again.',
        timestamp: new Date().toISOString(),
        type: 'error'
      }]);
    } finally {
      setIsUploading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (e.ctrlKey) {
        // Allow Ctrl+Enter to insert a newline
        const cursorPosition = e.target.selectionStart;
        const textBeforeCursor = inputMessage.substring(0, cursorPosition);
        const textAfterCursor = inputMessage.substring(cursorPosition);
        setInputMessage(textBeforeCursor + '\n' + textAfterCursor);

        // Set cursor position after the inserted newline
        setTimeout(() => {
          e.target.selectionStart = cursorPosition + 1;
          e.target.selectionEnd = cursorPosition + 1;
        }, 0);
      } else if (!e.shiftKey) {
        // Enter without Shift or Ctrl sends the message
        e.preventDefault();
        handleSendMessage();
      }
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!inputMessage.trim() && uploadedFiles.length === 0) return;

    // Don't allow sending messages if no account is selected
    if (!isReady) return;

    // Reset the new chat with file upload flag when sending a message
    isNewChatWithFileRef.current = false;

    // Create a unique ID for the message
    const userMessageId = `user_${Date.now()}`;
    const chatId = currentChatId || `chat_${Date.now()}`;

    // Add user message to the chat
    const userMessage = {
      id: userMessageId,
      sender: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString(),
      type: 'user',
      chatId,
      messageId: userMessageId, // Set initial messageId to match id (will be updated later)
      file_metadata: uploadedFiles.length > 0 ? uploadedFiles : null, // Always use array for file_metadata
      references: references.length > 0 ? references : undefined
    };

    // Update the UI immediately with just the user message
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInputMessage('');

    // Reset textarea height to default
    const textarea = document.querySelector('.message-input');
    if (textarea) {
      textarea.style.height = '56px';
    }

    // Show loading spinner
    setIsSendingMessage(true);
    scrollToBottom(); // Explicitly scroll when sending a new message

    try {
      // Get the account ID using the getAccountIdToUse function from AccountContext
      // This ensures we always use the latest selected account
      const accountIdToUse = getAccountIdToUse();

      if (!accountIdToUse) {
        console.error('No valid accountId found for user:', user);
        throw new Error('No valid accountId found. Please select an account or contact support.');
      }

      console.log('Sending message with accountId:', accountIdToUse);

      // Prepare the request payload
      const payload = {
        message: inputMessage,
        chatId: currentChatId,
        userId: getUserId(),
        accountId: accountIdToUse,
        references: references.length > 0 ? references : undefined
      };

      // Include all uploaded files in file_metadata
      if (uploadedFiles.length > 0) {
        // Always use file_metadata as an array, even for a single file
        payload.file_metadata = uploadedFiles.length === 1 ? [uploadedFiles[0]] : uploadedFiles;
      }

      // Explicitly add accountId to the request
      const response = await api.post(getApiUrl('/api/chat'), payload);

      // Clear references after sending
      setReferences([]);

      const { message, action, subAction, chatId, messageId, sourceMessageId } = response.data;

      // Update chat ID if this is a new chat
      const isNewChat = !currentChatId;
      if (isNewChat) {
        console.log(`Creating new chat with ID: ${chatId}`);

        // Set the flag to indicate we're creating a new chat
        // This will prevent the useEffect from clearing messages when selectedChat changes
        isCreatingNewChatRef.current = true;

        // Update the last loaded chat ID to prevent duplicate API calls
        lastLoadedChatIdRef.current = chatId;

        // Create the bot response message
        const botResponseMessage = {
          id: messageId,
          chatId,
          messageId,
          content: message,
          timestamp: new Date().toISOString(),
          type: 'bot',
          action,
          subAction,
          sourceMessageId,
          isProcessing: SUPPORTED_ACTIONS.includes(action)
        };

        // Update our local messages state to use the new chatId
        setMessages(prevMessages => {
          // Make sure we have the user message
          if (prevMessages.length === 0) {
            console.error('Messages array is empty when creating new chat');
            // Create a fallback array with at least the user message
            prevMessages = [userMessage];
          }

          const updatedMessages = prevMessages.map(msg => {
            // For user messages, make sure to set both id and messageId to the server-generated ID if available
            if (msg.type === 'user' && msg.id === userMessageId && sourceMessageId) {
              return {
                ...msg,
                chatId: chatId,
                id: sourceMessageId,
                messageId: sourceMessageId // Explicitly set messageId to match the server-generated ID
              };
            }
            // For other messages, just update the chatId
            return {
              ...msg,
              chatId: chatId
            };
          });

          // Add the bot response
          const newMessages = [...updatedMessages, botResponseMessage];

          // Store the messages in the ref for backup
          messagesRef.current = newMessages;
          console.log(`Stored ${newMessages.length} messages in ref for new chat`);

          return newMessages;
        });

        // Notify the parent component to update its state
        // This will update currentChatId in the parent, which will then be passed back as a prop
        // Pass true as the second parameter to force a refresh of the chat history
        onChatHistoryChange(chatId, true);

        // Store the messageId for polling after the chat ID changes
        const messageIdToTrack = messageId;
        const actionToTrack = action;

        // Start polling if needed, but with a delay to ensure the chat ID is updated
        if (messageIdToTrack && SUPPORTED_ACTIONS.includes(actionToTrack)) {
          console.log(`Scheduling delayed polling for new chat: ${chatId}, message: ${messageIdToTrack}`);

          // Create a backup of the messages for polling
          const messagesBackup = [
            {...userMessage, chatId},
            botResponseMessage
          ];

          // Store the backup in the ref
          messagesRef.current = messagesBackup;

          // Use a longer delay for new chats to ensure everything is properly set up
          setTimeout(() => {
            console.log(`Starting delayed polling for new chat: ${chatId}, message: ${messageIdToTrack}`);

            // Check if messages array is empty but we have stored messages
            if (messages.length === 0 && messagesRef.current.length > 0) {
              console.warn('Messages array is empty before starting polling, restoring from backup');
              setMessages([...messagesRef.current]);
            }

            startPollingMessage(chatId, messageIdToTrack);
          }, 1000);
        }

        return; // Exit early to prevent the normal message update flow
      }

      // Update the user message with the server-generated ID
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        const userMessageIndex = updatedMessages.findIndex(msg => msg.id === userMessageId);

        if (userMessageIndex !== -1) {
          // Update the user message with the sourceMessageId
          // This is critical for the briefcase files to be updated correctly
          updatedMessages[userMessageIndex] = {
            ...updatedMessages[userMessageIndex],
            id: sourceMessageId,
            messageId: sourceMessageId // Explicitly set messageId to match the server-generated ID
          };
        }

        // Add the bot response
        return [...updatedMessages, {
          id: messageId,
          chatId,
          messageId,
          content: message,
          timestamp: new Date().toISOString(),
          type: 'bot',
          action,
          subAction,
          sourceMessageId,
          isProcessing: SUPPORTED_ACTIONS.includes(action)
        }];
      });

      // We only need to refresh the sidebar for new chats
      // For existing chats, we don't need to refresh as it causes unnecessary message reloading

      if (messageId && SUPPORTED_ACTIONS.includes(action)) {
        startPollingMessage(chatId, messageId);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorTimestamp = new Date().toISOString();
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        content: 'Sorry, there was an error processing your request.',
        timestamp: errorTimestamp,
        type: 'system',
        isError: true
      }]);
    } finally {
      setIsSendingMessage(false);
      resetAskAssistantDialog();
      resetFileInput();
    }
  };

  const handleDropdownItemClick = (item) => {
    const message = `${item.text}`;
    setInputMessage(message);
    setDropdownOpen(false);
  };

  const toggleReference = (messageId) => {
    const message = messages.find(msg => msg.id === messageId);
    if (!message) return;

    setReferences(prev => {
      const isReferenced = prev.some(ref => ref.messageId === messageId);
      if (isReferenced) {
        return prev.filter(ref => ref.messageId !== messageId);
      } else {
        return [...prev, {
          messageId,
          type: "message",
          timestamp: new Date().toISOString(),
          sourceMessageId: message.sourceMessageId,
          action: message.action,
          subAction: message.subAction
        }];
      }
    });
  };

  const handleExportToSheets = async (messageId, jsonData) => {
    try {
      setExportingMessageId(messageId);

      // Get user email from localStorage
      const userData = JSON.parse(localStorage.getItem(USER_STORAGE_KEY) || '{}');
      const userEmail = userData.email;
      const { userId } = getUserData();

      if (!userEmail) {
        console.error('User email not found');
        return;
      }

      // Get the account ID using the getAccountIdToUse function from AccountContext
      // This ensures we always use the latest selected account
      const accountIdToUse = getAccountIdToUse();

      // Explicitly add accountId to the request
      const response = await api.post(
        getApiUrl('/api/chat/export-to-sheets'),
        {
          data: jsonData,
          userEmail,
          chatId: currentChatId,
          messageId,
          userId,
          accountId: accountIdToUse
        }
      );

      // Open the created sheet in a new tab
      if (response.data?.spreadsheetUrl) {
        window.open(response.data.spreadsheetUrl, '_blank');
      }
    } catch (error) {
      console.error('Error exporting to Google Sheets:', error);
    } finally {
      setExportingMessageId(null);
    }
  };

  const handleApplyChanges = async (message, suggestion) => {
    try {
      // Extract campaign_id from the message's jsonResponse
      // Try multiple locations: root level, details, or details.campaign_id
      const campaignId = message.jsonResponse?.campaign_id ||
                        message.jsonResponse?.details?.campaign_id ||
                        message.json_response?.campaign_id ||
                        message.json_response?.details?.campaign_id;
      if (!campaignId) {
        console.error('No campaign_id found in message', message);
        alert('Unable to apply changes: Campaign ID not found');
        return;
      }

      const changeName = suggestion?.title || suggestion?.action_type || 'recommended change';
      if (!window.confirm(
        `Apply this ${String(changeName).replace(/_/g, ' ')} to the live campaign?`,
      )) return;

      // Set loading state
      const loadingKey = `${message.id}-${suggestion.action_type}`;
      setApplyingChanges(loadingKey);

      // Get the account ID
      const accountIdToUse = getAccountIdToUse();
      if (!accountIdToUse) {
        console.error('No valid accountId found for user:', user);
        throw new Error('No valid accountId found. Please select an account or contact support.');
      }

      // Get platform from message response
      const platform = message.jsonResponse?.platform || 'fb';
      const isGoogleAdsPlatform = isGoogleAdsOptimizerPlatform(platform);
      const platformName = platform === 'fb' || platform === 'facebook' ? 'FB' :
                          platform === 'ttd' ? 'TTD' :
                          platform === 'dv360' ? 'DV360' :
                          platform === 'reddit' ? 'Reddit' :
                          platform === 'tiktok' ? 'TikTok' :
                          getOptimizerPlatformDisplayName(platform);

      // Build the edit message based on platform format
      let editMessage;

      if (isGoogleAdsPlatform) {
        // Google Ads Search format: uses target_type, target_id, current_value, recommended_value
        const actionType = suggestion.action_type || 'update';
        const targetType = suggestion.target_type || 'campaign';
        const targetId = suggestion.target_id || campaignId;

        // Helper to format value that might be object/array (e.g., keyword targeting)
        const formatValue = (val) => {
          if (!val) return 'N/A';
          if (typeof val === 'object') {
            if (Array.isArray(val)) {
              return val.map(v => typeof v === 'object' ? (v.keyword || JSON.stringify(v)) : v).join(', ');
            }
            return JSON.stringify(val);
          }
          return val;
        };

        const currentValue = formatValue(suggestion.current_value) || 'current';
        const recommendedValue = formatValue(suggestion.recommended_value) || 'new value';

        editMessage = `Edit ${platformName} Campaign ${campaignId}: ${actionType} ${targetType} ${targetId} from "${currentValue}" to "${recommendedValue}"`;
      } else {
        // Other platforms format: uses description, target_entities, suggested_values
        const targetEntities = suggestion.target_entities?.join(', ') || 'unknown';
        const description = suggestion.description || suggestion.action_type || 'update';

        // Serialize suggested values as JSON
        let suggestedValuesText = '';
        if (suggestion.suggested_values) {
          suggestedValuesText = ` with suggested_values: ${JSON.stringify(suggestion.suggested_values)}`;
        }

        editMessage = `Edit ${platformName} Campaign ${campaignId} ${description} to ad group ${targetEntities}${suggestedValuesText}`;
      }

      console.log('Applying changes with message:', editMessage);

      // Create a new message via the chat API
      const payload = {
        message: editMessage,
        chatId: currentChatId,
        userId: getUserId(),
        accountId: accountIdToUse,
        metadata: {
          source: 'apply_actionable_suggestion',
          originalMessageId: message.id,
          suggestion: suggestion
        }
      };

      const response = await api.post(getApiUrl('/api/chat'), payload);
      const { message: botMessage, action, subAction, messageId, sourceMessageId } = response.data;

      // Add the user message to the UI
      const userMessage = {
        id: sourceMessageId,
        sender: 'user',
        content: editMessage,
        timestamp: new Date().toISOString(),
        type: 'user',
        chatId: currentChatId,
        messageId: sourceMessageId
      };

      // Add the bot response message
      const botResponseMessage = {
        id: messageId,
        chatId: currentChatId,
        messageId,
        content: botMessage,
        timestamp: new Date().toISOString(),
        type: 'bot',
        action,
        subAction,
        sourceMessageId,
        isProcessing: SUPPORTED_ACTIONS.includes(action)
      };

      // Update messages state
      setMessages(prevMessages => [...prevMessages, userMessage, botResponseMessage]);

      // Start polling if needed
      if (messageId && SUPPORTED_ACTIONS.includes(action)) {
        console.log(`Starting polling for edit-campaign message: ${messageId}`);
        startPollingMessage(currentChatId, messageId);
      }

      // Scroll to bottom to show new messages
      scrollToBottom();

    } catch (error) {
      console.error('Error applying changes:', error);
      alert('Failed to apply changes. Please try again.');
    } finally {
      setApplyingChanges(null);
    }
  };

  const handleRegenerateWithReason = async (reason) => {
    try {
      // When called directly from the Regenerate button, we need to get the message ID from rejectionDialog
      const dialogMessageId = rejectionDialog.messageId;
      if (!dialogMessageId) {
        console.error('No message ID found in rejection dialog');
        return;
      }

      const rejectedMessage = messages.find(m => m.id === dialogMessageId);
      if (!rejectedMessage) {
        console.error('Rejected message not found', dialogMessageId);
        return;
      }

      // Find the original user message that led to this response
      let originalMessage = messages.find(m => m.id === rejectedMessage.sourceMessageId);
      if (!originalMessage) {
        console.error('Original message not found for source ID:', rejectedMessage.sourceMessageId);
        // Use the rejected message as a fallback
        originalMessage = {
          content: rejectionDialog.sourceContent || "Please regenerate your response.",
          file_metadata: rejectionDialog.file_metadata || rejectedMessage.file_metadata,
          references: rejectionDialog.references || rejectedMessage.references || []
        };
      }

      // Extract just the original content without any feedback instructions
      let originalContent = originalMessage.content;
      const feedbackMarker = "\n\nFeedback on previous response:";
      if (originalContent.includes(feedbackMarker)) {
        originalContent = originalContent.substring(0, originalContent.indexOf(feedbackMarker));
      }

      // Update the rejected status in the backend
      await updateMessageStatus(rejectedMessage.id, 'rejected', reason);

      // Prepare the new message with rejection reason
      const regenerateMessage = `${originalContent}\n\nFeedback on previous response: \n\n${reason}\n\nPlease regenerate with the above feedback in mind.`;

      // Add file_metadata and references from original message
      const userMessageId = `user_${Date.now()}`;
      // Ensure file_metadata is always an array if present
      let fileMetadata = null;
      if (originalMessage.file_metadata) {
        fileMetadata = Array.isArray(originalMessage.file_metadata)
          ? originalMessage.file_metadata
          : [originalMessage.file_metadata];
      }

      const newMessage = {
        id: userMessageId,
        sender: 'user',
        content: regenerateMessage,
        timestamp: new Date().toISOString(),
        type: 'user',
        chatId: currentChatId,
        file_metadata: fileMetadata,
        references: originalMessage.references || [],
        messageId: userMessageId
      };

      // Update messages with both the rejected status and new message
      setMessages(prevMessages => {
        const updatedMessages = prevMessages.map(msg =>
          msg.id === rejectedMessage.id
            ? { ...msg, status: 'rejected', feedback: reason }
            : msg
        );
        return [...updatedMessages, newMessage];
      });
      setIsSendingMessage(true);

      // Get the account ID using the getAccountIdToUse function from AccountContext
      // This ensures we always use the latest selected account
      const accountIdToUse = getAccountIdToUse();

      if (!accountIdToUse) {
        console.error('No valid accountId found for user:', user);
        throw new Error('No valid accountId found. Please select an account or contact support.');
      }

      console.log('Sending regenerate message with accountId:', accountIdToUse);

      const response = await api.post(getApiUrl('/api/chat'), {
        message: regenerateMessage,
        chatId: currentChatId,
        file_metadata: fileMetadata, // Use the already processed fileMetadata that's always an array if present
        userId: getUserId(),
        accountId: accountIdToUse,
        references: originalMessage.references || []
      });

      const { message, action, subAction, chatId, messageId, sourceMessageId } = response.data;

      // Update the user message with the server-generated ID and add bot response
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        const userMessageIndex = updatedMessages.findIndex(msg => msg.id === userMessageId);

        if (userMessageIndex !== -1) {
          // Update the user message with the sourceMessageId
          updatedMessages[userMessageIndex] = {
            ...updatedMessages[userMessageIndex],
            id: sourceMessageId,
            messageId: sourceMessageId
          };
        }

        // Add the bot response
        return [...updatedMessages, {
          id: messageId,
          chatId,
          messageId,
          content: message,
          timestamp: new Date().toISOString(),
          type: 'bot',
          action,
          subAction,
          sourceMessageId,
          isProcessing: SUPPORTED_ACTIONS.includes(action)
        }];
      });

      if (messageId && SUPPORTED_ACTIONS.includes(action)) {
        startPollingMessage(chatId, messageId);
      }

      // No need to refresh chat history as we've already updated the messages state locally

    } catch (error) {
      console.error('Error regenerating:', error);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        content: 'Sorry, there was an error regenerating the response.',
        timestamp: new Date().toISOString(),
        type: 'system',
        isError: true
      }]);
    } finally {
      // Make sure we reset the dialog state completely
      resetRejectionDialog();
    }
  };

  const handleAskAssistant = async () => {
    if (!askAssistantDialog.question.trim()) {
      alert('Please provide a question for the assistant');
      return;
    }

    setAskAssistantDialog(prev => ({ ...prev, isAsking: true }));

    try {
      // Find the original message that generated this response
      const originalMessage = messages.find(m => m.id === askAssistantDialog.messageId);

      // Prepare the new message with the question
      const askMessage = `Ask Assistant: ${askAssistantDialog.question}`;

      // Create references array including the bot message being asked about
      const messageReferences = [
        {
          messageId: askAssistantDialog.messageId,
          type: "message",
          timestamp: new Date().toISOString(),
          sourceMessageId: originalMessage?.sourceMessageId,
          action: originalMessage?.action,
          subAction: originalMessage?.subAction
        },
        ...(askAssistantDialog.references || []),
        ...(originalMessage?.references || [])
      ];

      // Add file_metadata and references from original message if available
      const userMessageId = `user_${Date.now()}`;

      // Ensure file_metadata is always an array if present
      let fileMetadata = null;
      if (originalMessage?.file_metadata) {
        fileMetadata = Array.isArray(originalMessage.file_metadata)
          ? originalMessage.file_metadata
          : [originalMessage.file_metadata];
      }

      const newMessage = {
        id: userMessageId,
        sender: 'user',
        content: askMessage,
        timestamp: new Date().toISOString(),
        type: 'user',
        chatId: currentChatId,
        file_metadata: fileMetadata,
        references: messageReferences,
        messageId: userMessageId
      };

      // Update messages with both the rejected status and new message
      setMessages(prevMessages => [...prevMessages, newMessage]);
      setIsSendingMessage(true);

      // Get the account ID using the getAccountIdToUse function from AccountContext
      // This ensures we always use the latest selected account
      const accountIdToUse = getAccountIdToUse();

      if (!accountIdToUse) {
        console.error('No valid accountId found for user:', user);
        throw new Error('No valid accountId found. Please select an account or contact support.');
      }

      console.log('Sending ask assistant message with accountId:', accountIdToUse);

      const response = await api.post(getApiUrl('/api/chat'), {
        message: askMessage,
        chatId: currentChatId,
        file_metadata: fileMetadata, // Use the already processed fileMetadata that's always an array if present
        userId: getUserId(),
        accountId: accountIdToUse,
        references: messageReferences
      });

      const { message, action, subAction, chatId, messageId, sourceMessageId } = response.data;

      // Update the user message with the server-generated ID and add bot response
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        const userMessageIndex = updatedMessages.findIndex(msg => msg.id === userMessageId);

        if (userMessageIndex !== -1) {
          // Update the user message with the sourceMessageId
          updatedMessages[userMessageIndex] = {
            ...updatedMessages[userMessageIndex],
            id: sourceMessageId,
            messageId: sourceMessageId
          };
        }

        // Add the bot response
        return [...updatedMessages, {
          id: messageId,
          chatId,
          messageId,
          content: message,
          timestamp: new Date().toISOString(),
          type: 'bot',
          action,
          subAction,
          sourceMessageId,
          isProcessing: SUPPORTED_ACTIONS.includes(action)
        }];
      });

      if (messageId && SUPPORTED_ACTIONS.includes(action)) {
        startPollingMessage(chatId, messageId);
      }

      // No need to refresh chat history as we've already updated the messages state locally

    } catch (error) {
      console.error('Error asking assistant:', error);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        content: 'Sorry, there was an error asking the assistant.',
        timestamp: new Date().toISOString(),
        type: 'system',
        isError: true
      }]);
    } finally {
      setIsSendingMessage(false);
      resetAskAssistantDialog();
    }
  };

  const handleRejectAndRegenerate = async () => {
    if (!rejectionDialog.reason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    setRejectionDialog(prev => ({ ...prev, isRegenerating: true }));
    setIsSendingMessage(true);

    try {
      // Update the message status and save the feedback
      await updateMessageStatus(rejectionDialog.messageId, 'rejected', rejectionDialog.reason);
      await handleRegenerateWithReason(rejectionDialog.reason);
    } finally {
      setIsSendingMessage(false);
      resetRejectionDialog();
    }
  };

  // Handle edit campaign submission
  const handleEditCampaign = async () => {
    if (!editDialog.feedback.trim()) {
      alert('Please provide specific instructions for what to change');
      return;
    }
    if (!window.confirm('Apply these campaign changes?')) return;

    setEditDialog(prev => ({ ...prev, isProcessing: true }));
    setIsSendingMessage(true);

    try {
      // Find the original user message that led to this response
      const originalMessage = messages.find(m => m.id === editDialog.message.sourceMessageId);

      // We don't need the original content for edit requests, just the instructions
      // Prepare the new message with edit instructions
      const editMessage = `Update Campaign Details: \n\n${editDialog.feedback}`;

      // Create a new user message for the edit request
      const userMessageId = `user_${Date.now()}`;

      // Create references array with just the bot message being edited
      const messageReferences = [
        {
          messageId: editDialog.messageId,
          type: "message",
          timestamp: new Date().toISOString(),
          sourceMessageId: originalMessage?.sourceMessageId,
          action: editDialog.message?.action,
          subAction: editDialog.message?.subAction
        }
        // Don't include original message references to avoid duplicates
      ];

      const newMessage = {
        id: userMessageId,
        sender: 'user',
        content: editMessage,
        timestamp: new Date().toISOString(),
        type: 'user',
        chatId: currentChatId,
        file_metadata: null, // Don't attach the file again
        references: messageReferences, // Reference the previous message instead
        messageId: userMessageId
      };

      // Add the new message to the chat
      setMessages(prevMessages => [...prevMessages, newMessage]);

      // Get the account ID using the getAccountIdToUse function from AccountContext
      // This ensures we always use the latest selected account
      const accountIdToUse = getAccountIdToUse();

      if (!accountIdToUse) {
        console.error('No valid accountId found for user:', user);
        throw new Error('No valid accountId found. Please select an account or contact support.');
      }

      console.log('Sending edit campaign message with accountId:', accountIdToUse);

      // Send the edit request to the server
      const response = await api.post(getApiUrl('/api/chat'), {
        message: editMessage,
        chatId: currentChatId,
        file_metadata: null, // Don't attach the file again
        userId: getUserId(),
        accountId: accountIdToUse,
        references: messageReferences // Reference the previous message instead
      });

      const { message, action, subAction, chatId, messageId, sourceMessageId } = response.data;

      // Update the messages with the server response
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        const userMessageIndex = updatedMessages.findIndex(m => m.id === userMessageId);

        if (userMessageIndex !== -1) {
          // Update the user message with the sourceMessageId
          updatedMessages[userMessageIndex] = {
            ...updatedMessages[userMessageIndex],
            id: sourceMessageId,
            messageId: sourceMessageId
          };
        }

        // Add the bot response
        return [...updatedMessages, {
          id: messageId,
          chatId,
          messageId,
          content: message,
          timestamp: new Date().toISOString(),
          type: 'bot',
          action,
          subAction,
          sourceMessageId,
          isProcessing: SUPPORTED_ACTIONS.includes(action)
        }];
      });

      // Start polling if needed
      if (messageId && SUPPORTED_ACTIONS.includes(action)) {
        startPollingMessage(chatId, messageId);
      }
    } catch (error) {
      console.error('Error editing campaign:', error);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        content: 'Sorry, there was an error processing your edit request.',
        timestamp: new Date().toISOString(),
        type: 'system',
        isError: true
      }]);
    } finally {
      setIsSendingMessage(false);
      resetEditDialog();
    }
  };

  // Handle edit creative submission
  const handleEditCreative = async () => {
    if (!editDialog.feedback.trim()) {
      alert('Please provide specific instructions for what to change');
      return;
    }
    if (!window.confirm('Apply these creative changes?')) return;

    setEditDialog(prev => ({ ...prev, isProcessing: true }));
    setIsSendingMessage(true);

    try {
      // Find the original user message that led to this response
      const originalMessage = messages.find(m => m.id === editDialog.message.sourceMessageId);

      // We don't need the original content for edit requests, just the instructions
      // Prepare the new message with edit instructions
      const editMessage = `Update Creative Details: \n\n${editDialog.feedback}`;

      // Create a new user message for the edit request
      const userMessageId = `user_${Date.now()}`;

      // Create references array with just the bot message being edited
      const messageReferences = [
        {
          messageId: editDialog.messageId,
          type: "message",
          timestamp: new Date().toISOString(),
          sourceMessageId: originalMessage?.sourceMessageId,
          action: editDialog.message?.action,
          subAction: editDialog.message?.subAction
        }
        // Don't include original message references to avoid duplicates
      ];

      const newMessage = {
        id: userMessageId,
        sender: 'user',
        content: editMessage,
        timestamp: new Date().toISOString(),
        type: 'user',
        chatId: currentChatId,
        file_metadata: null, // Don't attach the file again
        references: messageReferences, // Reference the previous message instead
        messageId: userMessageId
      };

      // Add the new message to the chat
      setMessages(prevMessages => [...prevMessages, newMessage]);

      // Get the account ID using the getAccountIdToUse function from AccountContext
      // This ensures we always use the latest selected account
      const accountIdToUse = getAccountIdToUse();

      if (!accountIdToUse) {
        console.error('No valid accountId found for user:', user);
        throw new Error('No valid accountId found. Please select an account or contact support.');
      }

      console.log('Sending edit creative message with accountId:', accountIdToUse);

      // Send the edit request to the server
      const response = await api.post(getApiUrl('/api/chat'), {
        message: editMessage,
        chatId: currentChatId,
        file_metadata: null, // Don't attach the file again
        userId: getUserId(),
        accountId: accountIdToUse,
        references: messageReferences // Reference the previous message instead
      });

      const { message, action, subAction, chatId, messageId, sourceMessageId } = response.data;

      // Update the messages with the server response
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        const userMessageIndex = updatedMessages.findIndex(m => m.id === userMessageId);

        if (userMessageIndex !== -1) {
          // Update the user message with the sourceMessageId
          updatedMessages[userMessageIndex] = {
            ...updatedMessages[userMessageIndex],
            id: sourceMessageId,
            messageId: sourceMessageId
          };
        }

        // Add the bot response
        return [...updatedMessages, {
          id: messageId,
          chatId,
          messageId,
          content: message,
          timestamp: new Date().toISOString(),
          type: 'bot',
          action,
          subAction,
          sourceMessageId,
          isProcessing: SUPPORTED_ACTIONS.includes(action)
        }];
      });

      // Start polling if needed
      if (messageId && SUPPORTED_ACTIONS.includes(action)) {
        startPollingMessage(chatId, messageId);
      }
    } catch (error) {
      console.error('Error editing creative:', error);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        content: 'Sorry, there was an error processing your edit request.',
        timestamp: new Date().toISOString(),
        type: 'system',
        isError: true
      }]);
    } finally {
      setIsSendingMessage(false);
      resetEditDialog();
    }
  };

  // Handle edit launch configuration submission
  const handleEditLaunchConfiguration = async () => {
    if (!editDialog.feedback.trim()) {
      alert('Please provide specific instructions for what to change');
      return;
    }
    if (!window.confirm('Apply these launch configuration changes?')) return;

    setEditDialog(prev => ({ ...prev, isProcessing: true }));
    setIsSendingMessage(true);

    try {
      // Find the original user message that led to this response
      const originalMessage = messages.find(m => m.id === editDialog.message.sourceMessageId);

      // We don't need the original content for edit requests, just the instructions
      // Prepare the new message with edit instructions
      const editMessage = `Update Launch Configuration Details: \n\n${editDialog.feedback}`;

      // Create a new user message for the edit request
      const userMessageId = `user_${Date.now()}`;

      // Create references array with just the bot message being edited
      const messageReferences = [
        {
          messageId: editDialog.messageId,
          type: "message",
          timestamp: new Date().toISOString(),
          sourceMessageId: originalMessage?.sourceMessageId,
          action: editDialog.message?.action,
          subAction: editDialog.message?.subAction
        }
        // Don't include original message references to avoid duplicates
      ];

      const newMessage = {
        id: userMessageId,
        sender: 'user',
        content: editMessage,
        timestamp: new Date().toISOString(),
        type: 'user',
        chatId: currentChatId,
        file_metadata: null, // Don't attach the file again
        references: messageReferences, // Reference the previous message instead
        messageId: userMessageId
      };

      // Add the new message to the chat
      setMessages(prevMessages => [...prevMessages, newMessage]);

      // Get the account ID using the getAccountIdToUse function from AccountContext
      // This ensures we always use the latest selected account
      const accountIdToUse = getAccountIdToUse();

      if (!accountIdToUse) {
        console.error('No valid accountId found for user:', user);
        throw new Error('No valid accountId found. Please select an account or contact support.');
      }

      console.log('Sending edit launch configuration message with accountId:', accountIdToUse);

      // Send the edit request to the server
      const response = await api.post(getApiUrl('/api/chat'), {
        message: editMessage,
        chatId: currentChatId,
        file_metadata: null, // Don't attach the file again
        userId: getUserId(),
        accountId: accountIdToUse,
        references: messageReferences // Reference the previous message instead
      });

      const { message, action, subAction, chatId, messageId, sourceMessageId } = response.data;

      // Update the messages with the server response
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        const userMessageIndex = updatedMessages.findIndex(m => m.id === userMessageId);

        if (userMessageIndex !== -1) {
          // Update the user message with the sourceMessageId
          updatedMessages[userMessageIndex] = {
            ...updatedMessages[userMessageIndex],
            id: sourceMessageId,
            messageId: sourceMessageId
          };
        }

        // Add the bot response
        return [...updatedMessages, {
          id: messageId,
          chatId,
          messageId,
          content: message,
          timestamp: new Date().toISOString(),
          type: 'bot',
          action,
          subAction,
          sourceMessageId,
          isProcessing: SUPPORTED_ACTIONS.includes(action)
        }];
      });

      // Start polling if needed
      if (messageId && SUPPORTED_ACTIONS.includes(action)) {
        startPollingMessage(chatId, messageId);
      }
    } catch (error) {
      console.error('Error editing launch configuration:', error);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        content: 'Sorry, there was an error processing your edit request.',
        timestamp: new Date().toISOString(),
        type: 'system',
        isError: true
      }]);
    } finally {
      setIsSendingMessage(false);
      resetEditDialog();
    }
  };

  const handleRejectResultGlobal = async (msg, initialFeedback = '') => {
    if (msg.status === 'rejected') {
      // If already rejected, remove rejection
      await updateMessageStatus(msg.id, 'completed');
    } else {
      // Find the original message that generated this response
      const originalMessage = messages.find(m => m.id === msg.sourceMessageId);

      // If initialFeedback is empty and this is a regenerate action (not edit),
      // we'll implement direct regeneration without using the dialog
      if (initialFeedback === '' && (msg.action === 'campaign-processor' || msg.action === 'campaign-augmentor'
        || msg.action === 'creative-processor' || msg.action === 'creative-augmentor')) {
        // For the Regenerate button, we'll implement the regeneration logic directly here
        // instead of relying on the dialog state and handleRegenerateWithReason
        setIsSendingMessage(true);

        try {
          // Find the original user message that led to this response
          let originalContent = originalMessage ? originalMessage.content : "Please regenerate your response.";
          const feedbackMarker = "\n\nFeedback on previous response:";

          // Clean up any previous feedback
          if (originalContent.includes(feedbackMarker)) {
            originalContent = originalContent.substring(0, originalContent.indexOf(feedbackMarker));
          }

          // Create a simple regenerate message without any additional text
          const regenerateMessage = originalContent;

          // Create a new user message for the regeneration request
          const userMessageId = `user_${Date.now()}`;
          const newMessage = {
            id: userMessageId,
            sender: 'user',
            content: regenerateMessage,
            timestamp: new Date().toISOString(),
            type: 'user',
            chatId: currentChatId,
            file_metadata: originalMessage ? originalMessage.file_metadata : msg.file_metadata,
            references: originalMessage ? originalMessage.references : (msg.references || []),
            messageId: userMessageId
          };

          // Add the new message to the chat
          setMessages(prevMessages => [...prevMessages, newMessage]);

          // Get the account ID using the getAccountIdToUse function from AccountContext
          // This ensures we always use the latest selected account
          const accountIdToUse = getAccountIdToUse();

          if (!accountIdToUse) {
            console.error('No valid accountId found for user:', user);
            throw new Error('No valid accountId found. Please select an account or contact support.');
          }

          console.log('Sending direct regenerate message with accountId:', accountIdToUse);

          // Send the regeneration request to the server
          const response = await api.post(getApiUrl('/api/chat'), {
            message: regenerateMessage,
            chatId: currentChatId,
            file_metadata: originalMessage ? originalMessage.file_metadata : msg.file_metadata,
            userId: getUserId(),
            accountId: accountIdToUse,
            references: originalMessage ? originalMessage.references : (msg.references || [])
          });

          const { message, action, subAction, chatId, messageId, sourceMessageId } = response.data;

          // Update the messages with the server response
          setMessages(prevMessages => {
            const updatedMessages = [...prevMessages];
            const userMessageIndex = updatedMessages.findIndex(m => m.id === userMessageId);

            if (userMessageIndex !== -1) {
              // Update the user message with the sourceMessageId
              updatedMessages[userMessageIndex] = {
                ...updatedMessages[userMessageIndex],
                id: sourceMessageId,
                messageId: sourceMessageId
              };
            }

            // Add the bot response
            return [...updatedMessages, {
              id: messageId,
              chatId,
              messageId,
              content: message,
              timestamp: new Date().toISOString(),
              type: 'bot',
              action,
              subAction,
              sourceMessageId,
              isProcessing: SUPPORTED_ACTIONS.includes(action)
            }];
          });

          // Start polling if needed
          if (messageId && SUPPORTED_ACTIONS.includes(action)) {
            startPollingMessage(chatId, messageId);
          }
        } catch (error) {
          console.error('Error regenerating:', error);
          setMessages(prev => [...prev, {
            id: `error_${Date.now()}`,
            content: 'Sorry, there was an error regenerating the response.',
            timestamp: new Date().toISOString(),
            type: 'system',
            isError: true
          }]);
        } finally {
          setIsSendingMessage(false);
        }
      } else {
        // For edit button or other actions, show the dialog with pre-populated feedback
        setRejectionDialog({
          open: true,
          messageId: msg.id,
          message: msg,
          reason: initialFeedback,
          isRegenerating: false,
          sourceContent: originalMessage ? originalMessage.content : "Please regenerate your response.",
          file_metadata: originalMessage ? originalMessage.file_metadata : msg.file_metadata,
          references: originalMessage ? originalMessage.references : (msg.references || [])
        });
      }
    }
  };

  const handleAskAssistantClick = (messageId, sourceMessageId) => {
    // Find the original message that generated this response
    const originalMessage = messages.find(m => m.id === sourceMessageId);
    // Find the message being asked about
    const currentMessage = messages.find(m => m.id === messageId);

    setAskAssistantDialog({
      open: true,
      messageId: messageId,
      message: currentMessage,
      question: '',
      isAsking: false,
      sourceContent: originalMessage ? originalMessage.content : "",
      references: currentMessage?.references || []
    });
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [dropdownRef]);

  const resetRejectionDialog = () => {
    setRejectionDialog({
      open: false,
      reason: '',
      isRegenerating: false,
      messageId: null,
      message: null,
      sourceContent: '',
      file_metadata: null, // Will be set to an array when needed
      references: null
    });
  };

  // State for edit dialog (for campaign, creative, and launch configuration)
  const [editDialog, setEditDialog] = useState({
    open: false,
    messageId: null,
    message: null,
    feedback: '',
    isProcessing: false,
    sourceContent: '',
    file_metadata: null,
    references: null,
    editType: 'campaign' // Default to campaign
  });

  const resetEditDialog = () => {
    setEditDialog({
      open: false,
      messageId: null,
      message: null,
      feedback: '',
      isProcessing: false,
      sourceContent: '',
      file_metadata: null, // Will be set to an array when needed
      references: null,
      editType: 'campaign' // Reset to default
    });
  };

  const resetAskAssistantDialog = () => {
    setAskAssistantDialog({
      open: false,
      question: '',
      isAsking: false,
      messageId: null,
      message: null,
      sourceContent: '',
      references: null
    });
  };

  const resetFileInput = () => {
    setUploadedFiles([]);
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Helper function to get user-friendly step names for orchestration processes
  const getCurrentStepDisplay = (step, orchestrationConfig = null) => {
    // If orchestration config is provided, use its stepLabels
    if (orchestrationConfig && orchestrationConfig.stepLabels && orchestrationConfig.stepLabels[step]) {
      return orchestrationConfig.stepLabels[step];
    }

    // Fallback to hardcoded mappings for backward compatibility
    const stepDisplayNames = {
      'initializing': 'Initializing',
      'campaign-processing': 'Processing IO',
      'campaign-completed': 'IO Processed',
      'creative-processing': 'Processing Creatives',
      'creative-completed': 'Creatives Processed',
      'launcher-processing': 'Applying Configuration',
      'launcher-completed': 'Configuration Applied',
      'api-processing': 'Calling Channel Specific API',
      'completed': 'Completed',
      // Salesforce orchestration steps
      'campaign': 'Processing IO',
      'launcher': 'Configuring Campaign',
      'salesforce': 'Creating Opportunity'
    };

    return stepDisplayNames[step] || step;
  };

  // Helper function to determine message header based on action and success flags
  const getMessageHeader = (msg) => {
    if (msg.action && msg.action.includes('-api')) {
      // For API actions, check jsonResponse.success
      const isSuccess = msg.jsonResponse?.success === true;
      if (isSuccess) {
        return 'Campaign created successfully. Details below:';
      } else {
        return 'Campaign creation failed. Please check the details below and try again.';
      }
    } else if (msg.action && (msg.action === 'fast-track' || msg.action.includes('fast-track') || msg.action === 'salesforce-orchestration')) {
      // For orchestration actions, check appropriate success field
      const isSuccess = msg.jsonResponse?.api_result?.success === true;

      if (isSuccess) {
        return 'Your campaign has been successfully processed on the platform:';
      } else {
        return 'Campaign processing failed. Please check the details below and try again.';
      }
    } else if (msg.type === 'bot' && msg.jsonResponse && msg.jsonResponse.success === false) {
      // For all other bot responses with failed jsonResponse.success
      return 'Operation failed. Please check the details below and try again.';
    } else {
      // For all other actions, show success message if it's a bot response with jsonResponse
      if (msg.type === 'bot' && msg.jsonResponse) {
        return 'Operation completed successfully. Details below:';
      }
      // For other messages, use the original content
      return typeof msg.content === 'object' ? JSON.stringify(msg.content) : msg.content;
    }
  };

  // Check if any uploaded file has validation errors
  const hasValidationErrors = uploadedFiles.some(file =>
    file.validation && !file.validation.valid && file.validation.errors?.length > 0
  );

  // Disable send button if no message and no files, or if uploading/validating, or if validation failed
  const isSendMessageDisabled =
    (!inputMessage.trim() && uploadedFiles.length === 0) ||
    isSendingMessage ||
    isUploading ||
    isValidating ||
    hasValidationErrors ||
    !canSendMessage(user) ||
    !isReady;

  // Create a ref to store the current briefcase files
  const briefcaseFilesRef = useRef(briefcaseFiles);

  // Update the ref whenever briefcaseFiles changes
  useEffect(() => {
    briefcaseFilesRef.current = briefcaseFiles;
  }, [briefcaseFiles]);

  // Update briefcase files when messages change
  useEffect(() => {
    if (messages.length > 0) {
      // First, create a map of existing files to preserve custom names and pin states
      const existingFilesMap = new Map();
      briefcaseFilesRef.current.forEach(file => {
        if (file.messageId) {
          existingFilesMap.set(file.messageId, {
            name: file.name,
            pinned: file.pinned
          });
        }
      });

      // Extract files from messages
      const extractedFiles = [];

      // First pass: Create a mapping of temporary IDs to server-generated IDs
      const messageIdMap = new Map();
      messages.forEach(msg => {
        // If this is a user message with a server-generated ID (sourceMessageId)
        if (msg.type === 'user' && msg.id && msg.messageId && msg.id !== msg.messageId) {
          // Map the temporary ID to the server-generated ID
          messageIdMap.set(`uploaded_${msg.id}`, msg.messageId);
        }
      });

      messages.forEach(msg => {
        // Skip invalid messages
        if (!msg || !msg.id) return;

        // Extract uploaded files
        if (msg.file_metadata) {
          // Handle file_metadata as an array
          if (Array.isArray(msg.file_metadata)) {
            msg.file_metadata.forEach((fileMetadata, fileIndex) => {
              if (fileMetadata && fileMetadata.publicUrl) {
                const fileId = `uploaded_${msg.id}_${fileIndex}`;
                // Check if this file already exists in our extracted files
                const fileExists = extractedFiles.some(f =>
                  f.id === fileId || (f.url === fileMetadata.publicUrl && f.type === 'uploaded')
                );

                if (!fileExists) {
                  // Use the server-generated message ID if available, otherwise use the temporary ID
                  const messageId = msg.messageId || msg.id;

                  // Check if we have existing metadata for this file
                  const existingMetadata = existingFilesMap.get(messageId);

                  // Use existing name and pinned status if available, otherwise use defaults
                  const fileName = existingMetadata?.name ||
                                  fileMetadata.name ||
                                  `File ${fileIndex + 1} - ${new Date(msg.timestamp || Date.now()).toLocaleString()}`;

                  const isPinned = existingMetadata?.pinned !== undefined ?
                                  existingMetadata.pinned :
                                  (fileMetadata.pinned || false);

                  extractedFiles.push({
                    id: fileId,
                    name: fileName,
                    url: fileMetadata.publicUrl,
                    size: fileMetadata.size || 0,
                    type: 'uploaded',
                    messageId: messageId,
                    timestamp: msg.timestamp || Date.now(),
                    pinned: isPinned
                  });
                }
              }
            });
          }
          // Handle file_metadata as a single object (for backward compatibility)
          else if (msg.file_metadata.publicUrl) {
            const fileId = `uploaded_${msg.id}`;
            // Check if this file already exists in our extracted files
            const fileExists = extractedFiles.some(f =>
              f.id === fileId || (f.url === msg.file_metadata.publicUrl && f.type === 'uploaded')
            );

            if (!fileExists) {
              // Use the server-generated message ID if available, otherwise use the temporary ID
              const messageId = msg.messageId || msg.id;

              // Check if we have existing metadata for this file
              const existingMetadata = existingFilesMap.get(messageId);

              // Use existing name and pinned status if available, otherwise use defaults
              const fileName = existingMetadata?.name ||
                              msg.file_metadata.name ||
                              `File ${new Date(msg.timestamp || Date.now()).toLocaleString()}`;

              const isPinned = existingMetadata?.pinned !== undefined ?
                              existingMetadata.pinned :
                              (msg.file_metadata.pinned || false);

              extractedFiles.push({
                id: fileId,
                name: fileName,
                url: msg.file_metadata.publicUrl,
                size: msg.file_metadata.size || 0,
                type: 'uploaded',
                messageId: messageId,
                timestamp: msg.timestamp || Date.now(),
                pinned: isPinned
              });
            }
          }
        }

        // Extract generated files (from bot responses with jsonResponse)
        if (msg.type === 'bot' && msg.jsonResponse) {
          const fileId = `generated_${msg.id}`;
          const fileExists = extractedFiles.some(f =>
            f.id === fileId || (f.messageId === msg.id && f.type === 'generated')
          );

          if (!fileExists) {
            // Check if we have existing metadata for this file
            const existingMetadata = existingFilesMap.get(msg.id);

            // Create a more descriptive name for generated content
            const content = typeof msg.content === 'string' ?
                           msg.content.substring(0, 100) :
                           JSON.stringify(msg.jsonResponse).substring(0, 100);

            // Use existing name if available, otherwise use defaults
            // Handle file_metadata as an array or single object
            let fileName = null;
            if (Array.isArray(msg.file_metadata) && msg.file_metadata.length > 0) {
              fileName = msg.file_metadata[0].name;
            } else if (msg.file_metadata?.name) {
              fileName = msg.file_metadata.name;
            }

            // Use message header as the default name to be consistent
            const defaultName = getMessageHeader(msg);

            const generatedName = existingMetadata?.name ||
                                 fileName ||
                                 defaultName;

            // Use existing pinned status if available, otherwise use defaults
            // Handle file_metadata as an array or single object
            let isPinnedFromMetadata = false;
            if (Array.isArray(msg.file_metadata) && msg.file_metadata.length > 0) {
              isPinnedFromMetadata = msg.file_metadata[0].pinned || false;
            } else if (msg.file_metadata?.pinned) {
              isPinnedFromMetadata = msg.file_metadata.pinned;
            }

            const isPinned = existingMetadata?.pinned !== undefined ?
                            existingMetadata.pinned :
                            isPinnedFromMetadata;



            extractedFiles.push({
              id: fileId,
              name: generatedName,
              type: 'generated',
              content,
              messageId: msg.id,
              timestamp: msg.timestamp || Date.now(),
              pinned: isPinned,
              action: msg.action || null,
              status: msg.status || null
            });
          }
        }
      });

      // Second pass: Update any existing files with the correct message IDs from our mapping
      for (let i = 0; i < extractedFiles.length; i++) {
        const file = extractedFiles[i];
        // Check if we have a mapping for this file's ID
        if (messageIdMap.has(file.id)) {
          const newMessageId = messageIdMap.get(file.id);

          // Check if we have existing metadata for the new message ID
          const existingMetadata = existingFilesMap.get(newMessageId);

          // Update the file's messageId with the server-generated ID
          // and preserve any custom name and pin state
          extractedFiles[i] = {
            ...file,
            messageId: newMessageId,
            name: existingMetadata?.name || file.name,
            pinned: existingMetadata?.pinned !== undefined ? existingMetadata.pinned : file.pinned
          };
          console.log(`Updated file ${file.name} with server-generated messageId: ${newMessageId}`);
        }
      }

      // Sort files by pinned status and timestamp (oldest first)
      const sortedFiles = [...extractedFiles].sort((a, b) => {
        // First by pinned status
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        // Then by timestamp (oldest first)
        return new Date(a.timestamp) - new Date(b.timestamp);
      });

      // Save to state
      setBriefcaseFiles(sortedFiles);
    }
  }, [messages, briefcaseVisible]);

  // Ensure briefcase is closed on initial page load/refresh
  // and listen for custom events
  useEffect(() => {
    setBriefcaseVisible(false);

    // Ensure the with-briefcase class is removed from the chat container
    const chatContainer = document.querySelector('.chat-container');
    if (chatContainer) {
      chatContainer.classList.remove('with-briefcase');
    }

    // Add event listener for the custom closeBriefcase event
    const handleCloseBriefcase = () => {
      console.log('Closing briefcase panel due to chat removal');
      setBriefcaseVisible(false);

      // Also ensure the with-briefcase class is removed
      const chatContainer = document.querySelector('.chat-container');
      if (chatContainer) {
        chatContainer.classList.remove('with-briefcase');
      }
    };

    // Add event listener for the custom resetChat event
    const handleResetChat = () => {
      console.log('Resetting chat state due to New Canvas click');
      // Clear input message
      setInputMessage('');
      // Clear messages
      setMessages([]);
      // Clear uploaded files
      setUploadedFiles([]);
      // Clear references
      setReferences([]);
      // Close briefcase panel
      setBriefcaseVisible(false);
      // Reset any other state that needs to be cleared
      resetRejectionDialog();
      resetEditDialog();
      resetAskAssistantDialog();
      resetFileInput();
    };

    window.addEventListener('closeBriefcase', handleCloseBriefcase);
    window.addEventListener('resetChat', handleResetChat);

    // Clean up the event listeners on component unmount
    return () => {
      window.removeEventListener('closeBriefcase', handleCloseBriefcase);
      window.removeEventListener('resetChat', handleResetChat);
    };
  }, []);

  const handleToggleBriefcase = () => {
    const newVisibility = !briefcaseVisible;
    setBriefcaseVisible(newVisibility);

    // Toggle the with-briefcase class on the chat container
    const chatContainer = document.querySelector('.chat-container');
    if (chatContainer) {
      if (newVisibility) {
        chatContainer.classList.add('with-briefcase');
      } else {
        chatContainer.classList.remove('with-briefcase');
      }
    }
  };

  const handleRenameFile = async (fileId, newName) => {
    // Check if the new name already exists to prevent duplicates
    const isDuplicate = briefcaseFiles.some(file =>
      file.id !== fileId && file.name.toLowerCase() === newName.toLowerCase()
    );

    if (isDuplicate) {
      // If duplicate, add a suffix to make it unique
      const baseName = newName;
      let counter = 1;
      let uniqueName = `${baseName} (${counter})`;

      while (briefcaseFiles.some(file =>
        file.id !== fileId && file.name.toLowerCase() === uniqueName.toLowerCase()
      )) {
        counter++;
        uniqueName = `${baseName} (${counter})`;
      }

      newName = uniqueName;
    }

    // Find the file to get its messageId
    const file = briefcaseFiles.find(f => f.id === fileId);
    if (!file || !file.messageId) return;

    try {
      // Update file metadata on the server
      const response = await api.put(getApiUrl(`/api/file-metadata/${file.messageId}`), {
        name: newName,
        userId: getUserId(),
        accountId: getUserData().accountId
      });

      if (response.data.success) {
        // Update the file name in the local state
        setBriefcaseFiles(prevFiles =>
          prevFiles.map(file =>
            file.id === fileId
              ? { ...file, name: newName }
              : file
          )
        );
      } else {
        console.error('Failed to update file name on server');
      }
    } catch (error) {
      console.error('Error updating file name:', error);
    }
  };

  // Handle toggling pin status
  const handleTogglePin = async (fileId) => {
    // Find the file to get its messageId and current pinned status
    const file = briefcaseFiles.find(f => f.id === fileId);
    if (!file || !file.messageId) return;

    const newPinnedStatus = !file.pinned;

    try {
      // Update file metadata on the server
      const response = await api.put(getApiUrl(`/api/file-metadata/${file.messageId}`), {
        pinned: newPinnedStatus,
        userId: getUserId(),
        accountId: getUserData().accountId
      });

      if (response.data.success) {
        // Update the pinned status in the local state
        setBriefcaseFiles(prevFiles => {
          const updatedFiles = prevFiles.map(file =>
            file.id === fileId
              ? { ...file, pinned: newPinnedStatus }
              : file
          );

          // Sort files again to ensure pinned files appear at the top
          return [...updatedFiles].sort((a, b) => {
            // First by pinned status
            if (a.pinned && !b.pinned) return -1;
            if (!a.pinned && b.pinned) return 1;
            // Then by timestamp (oldest first)
            return new Date(a.timestamp) - new Date(b.timestamp);
          });
        });
      } else {
        console.error('Failed to update pinned status on server');
      }
    } catch (error) {
      console.error('Error updating pinned status:', error);
    }
  };

  const handleNavigateToMessage = (messageId) => {
    // Use requestAnimationFrame for smoother scrolling
    requestAnimationFrame(() => {
      // Find the message element
      const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
      if (messageElement) {
        // Scroll to the message with improved performance
        messageElement.scrollIntoView({ behavior: 'auto', block: 'center' });

        // Add a highlight class to make it easier to find
        messageElement.classList.add('highlighted');

        // Remove the highlight after a shorter delay (0.25 second instead of 1)
        setTimeout(() => {
          messageElement.classList.remove('highlighted');
        }, 250);
      }
    });
  };

  const handleReferenceInChat = (file) => {
    if (file && file.messageId) {
      // Reference the original message instead of just the file
      toggleReference(file.messageId);

      // Focus the input after adding the reference
      document.querySelector('.message-input')?.focus();
    }
  };

  // Handle stopping the fast track process
  const handleStopFastTrack = async (chatId, messageId) => {
    if (!chatId || !messageId) {
      console.error('Invalid chatId or messageId for stopping fast track');
      return;
    }

    try {
      // Get the account ID using the getAccountIdToUse function from AccountContext
      const accountIdToUse = getAccountIdToUse();

      if (!accountIdToUse) {
        console.error('No valid accountId found for user:', user);
        throw new Error('No valid accountId found. Please select an account or contact support.');
      }

      console.log(`Stopping fast track for chatId: ${chatId}, messageId: ${messageId}`);

      // Call the API to stop the fast track process
      const response = await api.post(getApiUrl(`/api/chat/${chatId}/${messageId}/stop-fast-track`), {
        userId: getUserId(),
        accountId: accountIdToUse
      });

      console.log('Fast track stop response:', response.data);

      // Update the message to show that the fast track process was stopped
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        const msgIndex = updatedMessages.findIndex(msg => msg.id === messageId);

        if (msgIndex !== -1) {
          // Update the message with the stopped status and the timestamp from the server
          updatedMessages[msgIndex] = {
            ...updatedMessages[msgIndex],
            content: getOrchestrationConfig('fast-track').messages.stopped,
            isProcessing: false,
            timestamp: new Date().toISOString(),
            // Update the fastTrackStatus to reflect that the process was stopped
            fastTrackStatus: {
              ...updatedMessages[msgIndex].fastTrackStatus,
              status: 'stopped',
              error_message: 'Stopped by user',
              updated_at: new Date().toISOString()
            }
          };

          // Clear polling interval if it exists
          if (pollIntervalsRef.current.has(messageId)) {
            clearInterval(pollIntervalsRef.current.get(messageId));
            pollIntervalsRef.current.delete(messageId);
          }
        }

        return updatedMessages;
      });
    } catch (error) {
      console.error('Error stopping fast track:', error);

      // Show an error message
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        content: 'Sorry, there was an error stopping the fast track process.',
        timestamp: new Date().toISOString(),
        type: 'system',
        isError: true
      }]);
    }
  };

  // Handle stopping the Salesforce orchestration process
  const handleStopSalesforceOrchestration = async (chatId, messageId) => {
    if (!chatId || !messageId) {
      console.error('Invalid chatId or messageId for stopping Salesforce orchestration');
      return;
    }

    try {
      // Get the account ID using the getAccountIdToUse function from AccountContext
      const accountIdToUse = getAccountIdToUse();

      if (!accountIdToUse) {
        console.error('No valid accountId found for user:', user);
        throw new Error('No valid accountId found. Please select an account or contact support.');
      }

      console.log(`Stopping Salesforce orchestration for chatId: ${chatId}, messageId: ${messageId}`);

      // Call the API to stop the Salesforce orchestration process
      const response = await api.post(getApiUrl(`/api/chat/${chatId}/${messageId}/stop-salesforce-orchestration`), {
        userId: getUserId(),
        accountId: accountIdToUse
      });

      console.log('Salesforce orchestration stop response:', response.data);

      // Update the message to show that the Salesforce orchestration process was stopped
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        const msgIndex = updatedMessages.findIndex(msg => msg.id === messageId);

        if (msgIndex !== -1) {
          // Update the message with the stopped status and the timestamp from the server
          updatedMessages[msgIndex] = {
            ...updatedMessages[msgIndex],
            content: getOrchestrationConfig('salesforce-orchestration').messages.stopped,
            isProcessing: false,
            timestamp: new Date().toISOString(),
            // Update the salesforceOrchestrationStatus to reflect that the process was stopped
            salesforceOrchestrationStatus: {
              ...updatedMessages[msgIndex].salesforceOrchestrationStatus,
              status: 'stopped',
              error_message: 'Stopped by user',
              updated_at: new Date().toISOString()
            }
          };

          // Clear polling interval if it exists
          if (pollIntervalsRef.current.has(messageId)) {
            clearInterval(pollIntervalsRef.current.get(messageId));
            pollIntervalsRef.current.delete(messageId);
          }
        }

        return updatedMessages;
      });
    } catch (error) {
      console.error('Error stopping Salesforce orchestration:', error);

      // Show an error message
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        content: 'Sorry, there was an error stopping the Salesforce orchestration process.',
        timestamp: new Date().toISOString(),
        type: 'system',
        isError: true
      }]);
    }
  };

  return (
    <div className={`chat-container ${briefcaseVisible ? 'with-briefcase' : ''} ${className || ''}`}>
      <div className="messages-container">
        {isLoading && messages.length === 0 && (
          <div className="loading-messages">
            {/* <div className="loading-spinner"></div> */}
            <p>Loading messages...</p>
          </div>
        )}

        {messages.map((msg, index) => {
          const handleApproveResult = async () => {
            if (msg.status === 'approved') {
              // If already approved, remove approval
              await updateMessageStatus(msg.id, 'completed');
            } else {
              // If not approved, set to approved
              await updateMessageStatus(msg.id, 'approved');
            }
          };

          const handleRejectResult = async (initialFeedback = '') => {
            handleRejectResultGlobal(msg, initialFeedback);
          };

          const handleJsonDownload = () => {
            if (msg.jsonResponse) {
              const jsonString = JSON.stringify(msg.jsonResponse, null, 2);
              const blob = new Blob([jsonString], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'data.json';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }
          };

          const handleOpenJsonInNewTab = () => {
            if (msg.jsonResponse) {
              const jsonString = JSON.stringify(msg.jsonResponse, null, 2);
              const blob = new Blob([jsonString], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
              // Clean up the URL after a delay to ensure the new tab has loaded
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }
          };

          const isReferenced = references.some(ref => ref.messageId === msg.id);
          const isExporting = exportingMessageId === msg.id;

          return (
            <div key={msg.id} className={`message ${msg.type} ${msg.isProcessing ? 'processing' : ''}`} data-message-id={msg.id}>
              <div className="message-timestamp">
                {formatMessageTimestamp(msg.timestamp)}
              </div>
              <div className="content">
                  <div className="message-content">
                    {/* Unified orchestration rendering */}
                    {isUnifiedOrchestrationAction(msg.action) ? (
                      <UnifiedOrchestrationMessage
                        message={msg}
                        config={getOrchestrationConfig(msg.action)}
                        isCollapsed={(msg.action === 'fast-track' || msg.action === 'fast-track-shell') ?
                          collapsedFastTrack.has(msg.id) :
                          collapsedSalesforceOrchestration.has(msg.id)}
                        onToggleCollapse={(msg.action === 'fast-track' || msg.action === 'fast-track-shell') ?
                          () => toggleFastTrackCollapse(msg.id) :
                          () => toggleSalesforceOrchestrationCollapse(msg.id)}
                        onStop={(msg.action === 'fast-track' || msg.action === 'fast-track-shell') ?
                          handleStopFastTrack :
                          handleStopSalesforceOrchestration}
                        getCurrentStepDisplay={getCurrentStepDisplay}
                        getMessageHeader={getMessageHeader}
                      />
                    ) : (
                      /* Regular messages (non-orchestration) */
                      !msg.isProcessing && !msg.isError && msg.status !== 'failed' && (
                        <div className="message-text">
                          {getMessageHeader(msg)}
                        </div>
                      )
                    )}
                    {msg.feedback && (
                      <div className="message-feedback">
                        <div className="feedback-header">
                          <span className="material-symbols-outlined">feedback</span>
                          <span>Feedback</span>
                        </div>
                        <div className="feedback-content">{msg.feedback}</div>
                      </div>
                    )}
                    {msg.type === 'bot' && msg.jsonResponse && !msg.isProcessing && !msg.isError && msg.status !== 'failed' && !collapsedTables.has(index) && (
                      // if msg.action is not 'ask-assistant' show json table
                      msg.action !== 'ask-assistant' ? (
                        <div className="json-content">
                          {msg.jsonResponse.validation ? (
                            <div className="json-tabs">
                              <div className="json-tab-headers">
                                <button
                                  className={`json-tab-button ${!msg.activeTab || msg.activeTab === 'response' ? 'json-tab-active' : ''}`}
                                  onClick={() => {
                                    // Set skipScrollRef to true to prevent scrolling when switching tabs
                                    skipScrollRef.current = true;
                                    const updatedMessages = [...messages];
                                    updatedMessages[index] = {...msg, activeTab: 'response'};
                                    setMessages(updatedMessages);
                                  }}
                                >
                                  Response
                                </button>
                                <button
                                  className={`json-tab-button ${msg.activeTab === 'validation' ? 'json-tab-active' : ''}`}
                                  onClick={() => {
                                    // Set skipScrollRef to true to prevent scrolling when switching tabs
                                    skipScrollRef.current = true;
                                    const updatedMessages = [...messages];
                                    updatedMessages[index] = {...msg, activeTab: 'validation'};
                                    setMessages(updatedMessages);
                                  }}
                                >
                                  Validation
                                </button>
                              </div>
                              <div className="json-tab-content">
                                {(!msg.activeTab || msg.activeTab === 'response') && (
                                  <JsonTable data={{...msg.jsonResponse, validation: undefined}} />
                                )}
                                {msg.activeTab === 'validation' && (() => {
                                  // Create a safe version of the validation data
                                  const validationData = msg.jsonResponse.validation;

                                  if (validationData === null || validationData === undefined) {
                                    return <div className="validation-message">No validation data available</div>;
                                  }

                                  if (typeof validationData !== 'object') {
                                    return <div className="validation-message">{String(validationData)}</div>;
                                  }

                                  // If it's an array, wrap it in an object for JsonTable
                                  if (Array.isArray(validationData)) {
                                    return <JsonTable data={{ items: validationData }} />;
                                  }

                                  // It's a safe object, render it with JsonTable
                                  // The JsonTable component will ensure 'summary' appears first
                                  return <JsonTable data={validationData} />;
                                })()}
                              </div>
                            </div>
                          ) : (
                            // Check if this is a campaign-optimizer, analyze-campaign, optimization-executor, qa-campaign, or allocate-budget action and render details directly
                            (msg.action === 'optimize-campaign' || msg.action === 'analyze-campaign' || msg.action === 'optimization-executor' || msg.action === 'qa-campaign' || msg.action === 'allocate-budget') && msg.jsonResponse.details ? (
                              <div className="campaign-optimizer-details">
                                {/* Check for error case first */}
                                {msg.jsonResponse.details.success === false && msg.jsonResponse.details.error ? (
                                  <div className="error-message" style={{
                                    padding: '15px',
                                    backgroundColor: '#fee',
                                    border: '1px solid #fcc',
                                    borderRadius: '4px',
                                    marginTop: '10px',
                                    display: 'block'
                                  }}>
                                    <h3 style={{ color: '#c00', marginTop: 0, marginBottom: '15px' }}>❌ Error</h3>
                                    {(() => {
                                      const errorText = msg.jsonResponse.details.error;

                                      // Try to extract the main error message from Facebook API errors
                                      try {
                                        // Look for the "message" field in the error JSON
                                        const messageMatch = errorText.match(/"message":\s*"([^"]+)"/);
                                        if (messageMatch) {
                                          const mainMessage = messageMatch[1];

                                          // Extract error code if present
                                          const codeMatch = errorText.match(/"code":\s*(\d+)/);
                                          const errorCode = codeMatch ? codeMatch[1] : null;

                                          return (
                                            <div style={{ display: 'block' }}>
                                              <div style={{ marginBottom: '12px' }}>
                                                <p style={{ margin: 0, fontSize: '1em', lineHeight: '1.5' }}>
                                                  {mainMessage}
                                                </p>
                                              </div>
                                              {errorCode && (
                                                <div style={{ marginBottom: '12px' }}>
                                                  <p style={{ margin: 0, fontSize: '0.9em', color: '#666' }}>
                                                    <strong>Error Code:</strong> {errorCode}
                                                  </p>
                                                </div>
                                              )}
                                              {msg.jsonResponse.details.campaign_id && (
                                                <div style={{ marginBottom: '12px' }}>
                                                  <p style={{ margin: 0, fontSize: '0.9em', color: '#666' }}>
                                                    <strong>Campaign ID:</strong> {msg.jsonResponse.details.campaign_id}
                                                  </p>
                                                </div>
                                              )}
                                              <details style={{ marginTop: '12px' }}>
                                                <summary style={{ cursor: 'pointer', color: '#666', fontSize: '0.9em', userSelect: 'none' }}>
                                                  Show full error details
                                                </summary>
                                                <pre style={{
                                                  marginTop: '10px',
                                                  padding: '10px',
                                                  backgroundColor: '#f5f5f5',
                                                  borderRadius: '4px',
                                                  fontSize: '0.85em',
                                                  overflow: 'auto',
                                                  whiteSpace: 'pre-wrap',
                                                  wordBreak: 'break-word'
                                                }}>
                                                  {errorText}
                                                </pre>
                                              </details>
                                            </div>
                                          );
                                        }
                                      } catch (e) {
                                        // If parsing fails, fall through to default rendering
                                      }

                                      // Default: show full error text
                                      return (
                                        <div style={{ display: 'block' }}>
                                          <p style={{ margin: '0 0 12px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {errorText}
                                          </p>
                                          {msg.jsonResponse.details.campaign_id && (
                                            <p style={{ margin: 0, fontSize: '0.9em', color: '#666' }}>
                                              <strong>Campaign ID:</strong> {msg.jsonResponse.details.campaign_id}
                                            </p>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <>
                                    {/* Platform-specific rendering
                                        - TTD: msg.jsonResponse.platform === 'ttd'
                                        - DV360: msg.jsonResponse.platform === 'dv360' OR missing platform field (backward compatibility)
                                        - Facebook: msg.jsonResponse.platform === 'fb' or 'facebook'
                                        - Future: TikTok, etc.
                                    */}
                                    {msg.jsonResponse.platform === 'cross-platform' ? (
                                  // Cross-Platform Budget Allocation Template
                                  <>
                                    {/* Executive Summary */}
                                    {msg.jsonResponse.details.executive_summary && (
                                      <div className="executive-summary-section">
                                        <h3>Executive Summary</h3>
                                        <div className="summary-content">
                                          {typeof msg.jsonResponse.details.executive_summary === 'string' ? (
                                            <p>{msg.jsonResponse.details.executive_summary}</p>
                                          ) : (
                                            <>
                                              {msg.jsonResponse.details.executive_summary.total_budget_analyzed && (
                                                <div className="budget-analyzed">
                                                  <strong>Total Budget Analyzed:</strong> {msg.jsonResponse.details.executive_summary.total_budget_analyzed}
                                                </div>
                                              )}
                                              {msg.jsonResponse.details.executive_summary.recommended_allocation && (
                                                <div className="recommended-allocation">
                                                  <strong>Recommended Allocation:</strong> {msg.jsonResponse.details.executive_summary.recommended_allocation}
                                                </div>
                                              )}
                                              {msg.jsonResponse.details.executive_summary.key_insights && Array.isArray(msg.jsonResponse.details.executive_summary.key_insights) && (
                                                <div className="key-insights">
                                                  <strong>Key Insights:</strong>
                                                  <ul>
                                                    {msg.jsonResponse.details.executive_summary.key_insights.map((insight, index) => (
                                                      <li key={index}>{insight}</li>
                                                    ))}
                                                  </ul>
                                                </div>
                                              )}
                                              {msg.jsonResponse.details.executive_summary.expected_impact && (
                                                <div className="expected-impact">
                                                  <strong>Expected Impact:</strong> {msg.jsonResponse.details.executive_summary.expected_impact}
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Platform Analysis */}
                                    {msg.jsonResponse.details.platform_analysis && (
                                      <div className="platform-analysis-section">
                                        <h3>Platform Analysis</h3>
                                        {Object.entries(msg.jsonResponse.details.platform_analysis).map(([platform, analysis], index) => (
                                          <div key={index} className="platform-analysis-item">
                                            <h4>{platform.toUpperCase()}</h4>
                                            <div className="platform-details">
                                              {analysis.current_performance && (
                                                <div className="current-performance">
                                                  <strong>Current Performance:</strong> {analysis.current_performance}
                                                </div>
                                              )}
                                              {analysis.strengths && Array.isArray(analysis.strengths) && (
                                                <div className="strengths">
                                                  <strong>Strengths:</strong>
                                                  <ul>
                                                    {analysis.strengths.map((strength, idx) => (
                                                      <li key={idx}>{strength}</li>
                                                    ))}
                                                  </ul>
                                                </div>
                                              )}
                                              {analysis.opportunities && Array.isArray(analysis.opportunities) && (
                                                <div className="opportunities">
                                                  <strong>Opportunities:</strong>
                                                  <ul>
                                                    {analysis.opportunities.map((opportunity, idx) => (
                                                      <li key={idx}>{opportunity}</li>
                                                    ))}
                                                  </ul>
                                                </div>
                                              )}
                                              {analysis.recommended_budget_share && (
                                                <div className="recommended-budget-share">
                                                  <strong>Recommended Budget Share:</strong> {analysis.recommended_budget_share}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Budget Allocation Overview */}
                                    {msg.jsonResponse.details.budget_allocation && (
                                      <div className="budget-allocation-section">
                                        <h3>Budget Allocation</h3>
                                        <div className="budget-overview">
                                          <div className="budget-header">
                                            <div className="total-budget">
                                              <span className="budget-label">Total Budget:</span>
                                              <span className="budget-amount">${msg.jsonResponse.total_budget?.toLocaleString() || 'N/A'}</span>
                                            </div>
                                            <div className="budget-period">
                                              <span className="period-label">Period:</span>
                                              <span className="period-value">{msg.jsonResponse.budget_period || 'N/A'}</span>
                                            </div>
                                          </div>

                                          {/* Platform Allocation */}
                                          {msg.jsonResponse.details.budget_allocation.platform_allocation && (
                                            <div className="platform-allocation">
                                              <h4>Platform Allocation</h4>
                                              {Object.entries(msg.jsonResponse.details.budget_allocation.platform_allocation).map(([platform, allocation], index) => (
                                                <div key={index} className="platform-item">
                                                  <div className="platform-header">
                                                    <span className="platform-name">{platform.toUpperCase()}</span>
                                                    <span className="platform-budget">${allocation.budget?.toLocaleString()}</span>
                                                    <span className="platform-percentage">({allocation.percentage}%)</span>
                                                  </div>
                                                  <div className="platform-rationale">
                                                    <p>{allocation.rationale}</p>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}

                                          {/* Campaign Allocation */}
                                          {msg.jsonResponse.details.budget_allocation.campaign_allocation && (
                                            <div className="campaign-allocation">
                                              <h4>Campaign Allocation</h4>
                                              {msg.jsonResponse.details.budget_allocation.campaign_allocation.map((campaign, index) => (
                                                <div key={index} className="campaign-item">
                                                  <div className="campaign-header">
                                                    <span className="campaign-name">{campaign.campaign_name || campaign.campaign_id}</span>
                                                    <span className="campaign-platform">{campaign.platform}</span>
                                                    <span className="campaign-budget">${campaign.allocated_budget?.toLocaleString()}</span>
                                                  </div>
                                                  <div className="campaign-details">
                                                    <p><strong>Current Performance:</strong> {campaign.current_performance}</p>
                                                    <p><strong>Allocation Rationale:</strong> {campaign.rationale}</p>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Campaign Recommendations */}
                                    {msg.jsonResponse.details.campaign_recommendations && Array.isArray(msg.jsonResponse.details.campaign_recommendations) && (
                                      <div className="campaign-recommendations-section">
                                        <h3>Campaign Recommendations</h3>
                                        <div className="recommendations-grid">
                                          {msg.jsonResponse.details.campaign_recommendations.map((recommendation, index) => (
                                            <div key={index} className={`recommendation-item ${recommendation.action_needed?.toLowerCase() === 'yes' ? 'action-needed' : recommendation.action_needed?.toLowerCase() === 'no' ? 'no-action-needed' : ''}`}>
                                              <div className="recommendation-header">
                                                <span className="campaign-name">{recommendation.campaign_name}</span>
                                                <div className="badges">
                                                  {recommendation.action_needed && (
                                                    <span className={`action-badge action-${recommendation.action_needed.toLowerCase()}`}>
                                                      {recommendation.action_needed.toLowerCase() === 'yes' ? 'Action Required' : 'No Action Needed'}
                                                    </span>
                                                  )}
                                                  <span className={`priority priority-${recommendation.priority?.toLowerCase()}`}>
                                                    {recommendation.priority}
                                                  </span>
                                                </div>
                                              </div>
                                              <div className="recommendation-details">
                                                <div className="budget-info">
                                                  <span className="current-budget">Current: {recommendation.current_budget}</span>
                                                  <span className="recommended-budget">Recommended: {recommendation.recommended_budget}</span>
                                                  <span className={`budget-change ${recommendation.budget_change?.startsWith('+') ? 'increase' : 'decrease'}`}>
                                                    {recommendation.budget_change}
                                                  </span>
                                                </div>
                                                <div className="reasoning">
                                                  <strong>Reasoning:</strong> {recommendation.reasoning}
                                                </div>
                                                {recommendation.expected_impact && (
                                                  <div className="expected-impact">
                                                    <strong>Expected Impact:</strong> {recommendation.expected_impact}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Performance Analysis */}
                                    {msg.jsonResponse.details.performance_analysis && (
                                      <div className="performance-analysis-section">
                                        <h3>Performance Analysis</h3>

                                        {/* DV360 Analysis */}
                                        {msg.jsonResponse.details.performance_analysis.dv360 && (
                                          <div className="platform-analysis">
                                            <h4>DV360 Analysis</h4>
                                            <p>{msg.jsonResponse.details.performance_analysis.dv360}</p>
                                          </div>
                                        )}

                                        {/* TTD Analysis */}
                                        {msg.jsonResponse.details.performance_analysis.ttd && (
                                          <div className="platform-analysis">
                                            <h4>TTD Analysis</h4>
                                            <p>{msg.jsonResponse.details.performance_analysis.ttd}</p>
                                          </div>
                                        )}

                                        {/* Cross-Platform Insights */}
                                        {msg.jsonResponse.details.performance_analysis.cross_platform_insights && (
                                          <div className="cross-platform-insights">
                                            <h4>Cross-Platform Insights</h4>
                                            <p>{msg.jsonResponse.details.performance_analysis.cross_platform_insights}</p>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Implementation Plan */}
                                    {msg.jsonResponse.details.implementation_plan && (
                                      <div className="implementation-plan-section">
                                        <h3>Implementation Plan</h3>
                                        {msg.jsonResponse.details.implementation_plan.immediate_actions && Array.isArray(msg.jsonResponse.details.implementation_plan.immediate_actions) && (
                                          <div className="immediate-actions">
                                            <h4>Immediate Actions</h4>
                                            <ul>
                                              {msg.jsonResponse.details.implementation_plan.immediate_actions.map((action, index) => (
                                                <li key={index}>{action}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                        {msg.jsonResponse.details.implementation_plan.monitoring_metrics && Array.isArray(msg.jsonResponse.details.implementation_plan.monitoring_metrics) && (
                                          <div className="monitoring-metrics">
                                            <h4>Monitoring Metrics</h4>
                                            <ul>
                                              {msg.jsonResponse.details.implementation_plan.monitoring_metrics.map((metric, index) => (
                                                <li key={index}>{metric}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                        {msg.jsonResponse.details.implementation_plan.review_schedule && (
                                          <div className="review-schedule">
                                            <h4>Review Schedule</h4>
                                            <p>{msg.jsonResponse.details.implementation_plan.review_schedule}</p>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Risk Assessment */}
                                    {msg.jsonResponse.details.risk_assessment && (
                                      <div className="risk-assessment-section">
                                        <h3>Risk Assessment</h3>
                                        {msg.jsonResponse.details.risk_assessment.high_risk_factors && Array.isArray(msg.jsonResponse.details.risk_assessment.high_risk_factors) && (
                                          <div className="high-risk-factors">
                                            <h4>High Risk Factors</h4>
                                            <ul>
                                              {msg.jsonResponse.details.risk_assessment.high_risk_factors.map((risk, index) => (
                                                <li key={index}>{risk}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                        {msg.jsonResponse.details.risk_assessment.mitigation_strategies && Array.isArray(msg.jsonResponse.details.risk_assessment.mitigation_strategies) && (
                                          <div className="mitigation-strategies">
                                            <h4>Mitigation Strategies</h4>
                                            <ul>
                                              {msg.jsonResponse.details.risk_assessment.mitigation_strategies.map((strategy, index) => (
                                                <li key={index}>{strategy}</li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                        {msg.jsonResponse.details.risk_assessment.confidence_level && (
                                          <div className="confidence-level">
                                            <h4>Confidence Level</h4>
                                            <span className={`confidence-badge confidence-${msg.jsonResponse.details.risk_assessment.confidence_level?.toLowerCase()}`}>
                                              {msg.jsonResponse.details.risk_assessment.confidence_level}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Recommendations */}
                                    {msg.jsonResponse.details.recommendations && msg.jsonResponse.details.recommendations.length > 0 && (
                                      <div className="recommendations-section">
                                        <h3>Recommendations</h3>
                                        {msg.jsonResponse.details.recommendations.map((rec, recIndex) => (
                                          <div key={recIndex} className="recommendation-item">
                                            <div className="recommendation-header">
                                              <h4>{rec.title}</h4>
                                              <span className={`impact-badge impact-${rec.expected_impact?.toLowerCase()}`}>
                                                {rec.expected_impact} IMPACT
                                              </span>
                                            </div>
                                            <p className="recommendation-description">{rec.description}</p>
                                            {rec.implementation_steps && rec.implementation_steps.length > 0 && (
                                              <div className="implementation-steps">
                                                <h5>Implementation Steps:</h5>
                                                <ol>
                                                  {rec.implementation_steps.map((step, stepIndex) => (
                                                    <li key={stepIndex}>{step}</li>
                                                  ))}
                                                </ol>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                ) : msg.jsonResponse.platform === 'ttd' ? (
                                  // TTD Platform Template
                                  <>
                                    {/* TTD Recommendations (for optimize-campaign) */}
                                    {msg.jsonResponse.details.recommendations && msg.jsonResponse.details.recommendations.length > 0 && (
                                      <div className="recommendations-section">
                                        <h3>Recommendations</h3>
                                        {msg.jsonResponse.details.recommendations.map((rec, recIndex) => (
                                          <div key={recIndex} className="recommendation-item">
                                            <div className="recommendation-header">
                                              <h4>{rec.title}</h4>
                                              <span className={`impact-badge impact-${rec.expected_impact?.toLowerCase()}`}>
                                                {rec.expected_impact} IMPACT
                                              </span>
                                            </div>
                                            <p className="recommendation-description">{rec.description}</p>
                                            {rec.implementation_steps && rec.implementation_steps.length > 0 && (
                                              <div className="implementation-steps">
                                                <h5>Implementation Steps:</h5>
                                                <ol>
                                                  {rec.implementation_steps.map((step, stepIndex) => (
                                                    <li key={stepIndex}>{step}</li>
                                                  ))}
                                                </ol>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* TTD Campaign Analysis */}
                                    {(msg.jsonResponse.details.campaign_analysis || msg.jsonResponse.details.overall_analysis) && (
                                      <div className="campaign-analysis-section">
                                        <h3>Campaign Analysis</h3>

                                        {/* TTD Optimize: Key Issues */}
                                        {msg.jsonResponse.details.campaign_analysis?.key_issues && msg.jsonResponse.details.campaign_analysis.key_issues.length > 0 && (
                                          <div className="key-issues">
                                            <h4>Key Issues</h4>
                                            {msg.jsonResponse.details.campaign_analysis.key_issues.map((issue, issueIndex) => (
                                              <div key={issueIndex} className="issue-item">
                                                <div className="issue-header">
                                                  <span className={`severity-badge severity-${issue.severity?.toLowerCase()}`}>
                                                    {issue.severity}
                                                  </span>
                                                </div>
                                                <p className="issue-description">{issue.issue}</p>
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {/* TTD Optimize: Overall Performance */}
                                        {msg.jsonResponse.details.campaign_analysis?.overall_performance && (
                                          <div className="overall-performance">
                                            <h4>Overall Performance</h4>
                                            <p>{msg.jsonResponse.details.campaign_analysis.overall_performance}</p>
                                          </div>
                                        )}

                                        {/* TTD Analyze: Overall Analysis */}
                                        {msg.jsonResponse.details.overall_analysis && (
                                          <div className="overall-analysis">
                                            <h4>Overall Analysis</h4>
                                            <p>{msg.jsonResponse.details.overall_analysis}</p>
                                            {msg.jsonResponse.details.action_needed && (
                                              <div className="action-needed-badge">
                                                <span className="material-symbols-outlined">warning</span>
                                                Action Required
                                              </div>
                                            )}
                                          </div>
                                        )}

                                        {/* TTD Analyze: Campaign-by-Campaign Analysis */}
                                        {msg.jsonResponse.details.campaign_analysis && Array.isArray(msg.jsonResponse.details.campaign_analysis) && (
                                          <div className="ttd-campaigns-analysis">
                                            <h4>Campaign Breakdown</h4>
                                            {msg.jsonResponse.details.campaign_analysis.map((campaign, campaignIndex) => (
                                              <div key={campaignIndex} className={`ttd-campaign-item ${campaign.action_needed?.toLowerCase() === 'yes' ? 'action-needed' : campaign.action_needed?.toLowerCase() === 'no' ? 'no-action-needed' : ''}`}>
                                                <div className="campaign-header">
                                                  <h5>Campaign ID: {campaign.campaign_id}</h5>
                                                  {campaign.action_needed && (
                                                    <span className={`action-badge action-${campaign.action_needed.toLowerCase()}`}>
                                                      {campaign.action_needed.toLowerCase() === 'yes' ? 'Action Required' : 'No Action Needed'}
                                                    </span>
                                                  )}
                                                </div>
                                                <div className="campaign-content">
                                                  <div className="campaign-section">
                                                    <h6>Analysis</h6>
                                                    <p>{campaign.analysis}</p>
                                                  </div>
                                                  {campaign.trends && (
                                                    <div className="campaign-section">
                                                      <h6>Trends</h6>
                                                      <p>{campaign.trends}</p>
                                                    </div>
                                                  )}

                                                  {/* TTD Ad Group Analysis */}
                                                  {campaign.adgroup_analysis && campaign.adgroup_analysis.length > 0 && (
                                                    <div className="adgroup-analysis">
                                                      <h6>Ad Group Analysis</h6>
                                                      {campaign.adgroup_analysis.map((adgroup, adgroupIndex) => (
                                                        <div key={adgroupIndex} className={`adgroup-item ${adgroup.action_needed?.toLowerCase() === 'yes' ? 'action-needed' : adgroup.action_needed?.toLowerCase() === 'no' ? 'no-action-needed' : ''}`}>
                                                          <div className="adgroup-header">
                                                            <span className="adgroup-id">Ad Group: {adgroup.adgroup_id}</span>
                                                            {adgroup.action_needed && (
                                                              <span className={`action-badge action-${adgroup.action_needed.toLowerCase()}`}>
                                                                {adgroup.action_needed.toLowerCase() === 'yes' ? 'Action Required' : 'No Action Needed'}
                                                              </span>
                                                            )}
                                                          </div>
                                                          <div className="adgroup-content">
                                                            <div className="adgroup-section">
                                                              <strong>Analysis:</strong>
                                                              <p>{adgroup.analysis}</p>
                                                            </div>
                                                            {adgroup.trends && (
                                                              <div className="adgroup-section">
                                                                <strong>Trends:</strong>
                                                                <p>{adgroup.trends}</p>
                                                              </div>
                                                            )}
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </>
                                ) : msg.jsonResponse.platform === 'fb' || msg.jsonResponse.platform === 'facebook' ? (
                                  // Facebook Platform Template
                                  (() => {
                                    // Handle both direct details structure and nested analysis structure
                                    const fbDetails = msg.jsonResponse.details.analysis || msg.jsonResponse.details;
                                    const fbCampaignAnalysis = fbDetails.campaign_analysis;

                                    return (
                                      <>
                                        {/* Facebook Optimization Summary */}
                                        {fbDetails.optimization_summary && (
                                          <div className="optimization-summary-section">
                                            <h3>Optimization Summary</h3>
                                            <p>{fbDetails.optimization_summary}</p>
                                          </div>
                                        )}

                                        {/* Facebook Recommendations (for optimize-campaign) */}
                                        {fbDetails.recommendations && fbDetails.recommendations.length > 0 && (
                                          <div className="recommendations-section">
                                            <h3>Recommendations</h3>
                                            {fbDetails.recommendations.map((rec, recIndex) => (
                                              <div key={recIndex} className="recommendation-item">
                                                <div className="recommendation-header">
                                                  <h4>{rec.title}</h4>
                                                  <span className={`priority-badge priority-${rec.priority?.toLowerCase()}`}>
                                                    {rec.priority} PRIORITY
                                                  </span>
                                                </div>
                                                <p className="recommendation-description">{rec.description}</p>
                                                {rec.expected_impact && (
                                                  <div className="expected-impact">
                                                    <h5>Expected Impact:</h5>
                                                    <p>{rec.expected_impact}</p>
                                                  </div>
                                                )}
                                                {rec.implementation_steps && rec.implementation_steps.length > 0 && (
                                                  <div className="implementation-steps">
                                                    <h5>Implementation Steps:</h5>
                                                    <ol>
                                                      {rec.implementation_steps.map((step, stepIndex) => (
                                                        <li key={stepIndex}>{step}</li>
                                                      ))}
                                                    </ol>
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {/* Facebook Actionable Suggestions */}
                                        {fbDetails.actionable_suggestions && fbDetails.actionable_suggestions.length > 0 && (
                                          <div className="actionable-suggestions-section">
                                            <h3>Actionable Suggestions</h3>
                                            {[...fbDetails.actionable_suggestions]
                                              .sort((a, b) => ((b?.scoring?.priority_score ?? -1) - (a?.scoring?.priority_score ?? -1)))
                                              .map((suggestion, sugIndex) => (
                                              <div key={sugIndex} className="suggestion-item">
                                                <div className="suggestion-header">
                                                  <h4>{suggestion.action_type}</h4>
                                                </div>
                                                <p className="suggestion-description">{suggestion.description}</p>
                                                {/* Optimizer scoring: lift range, confidence, priority.
                                                    basis/rationale ride in tooltips. Renders only when
                                                    the backend emitted scoring (graceful otherwise). */}
                                                {suggestion.scoring && (suggestion.scoring.priority_score != null || suggestion.scoring.expected_lift || suggestion.scoring.confidence != null) && (
                                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '6px 0' }}>
                                                    {suggestion.scoring.priority_score != null && (
                                                      <span
                                                        title="Composite score = expected value × confidence × reversibility. Higher means do it first."
                                                        style={{ padding: '2px 8px', fontSize: '0.7em', fontWeight: 600, borderRadius: '10px', background: '#eef2ff', color: '#4338ca' }}
                                                      >
                                                        ★ Priority {suggestion.scoring.priority_score}/100
                                                      </span>
                                                    )}
                                                    {suggestion.scoring.expected_lift && (() => {
                                                      const l = suggestion.scoring.expected_lift;
                                                      const arrow = l.direction === 'decrease' ? '↓' : '↑';
                                                      const { range_low_pct: lo, range_high_pct: hi, point_pct: pt } = l;
                                                      const mag = (lo != null && hi != null && Number(lo) !== Number(hi))
                                                        ? `${Math.abs(Number(lo))}–${Math.abs(Number(hi))}%`
                                                        : (pt != null ? `${Math.abs(Number(pt))}%` : null);
                                                      if (!mag) return null;
                                                      return (
                                                        <span
                                                          title={l.basis || 'Estimated lift'}
                                                          style={{ padding: '2px 8px', fontSize: '0.7em', fontWeight: 600, borderRadius: '10px', background: '#e7f5ff', color: '#1971c2' }}
                                                        >
                                                          Est. lift {arrow} {mag}{l.metric ? ' ' + String(l.metric).replace(/_/g, ' ') : ''}
                                                        </span>
                                                      );
                                                    })()}
                                                    {suggestion.scoring.confidence != null && (
                                                      <span
                                                        title={suggestion.scoring.confidence_rationale || 'Reliability of the lift estimate (data volume + how directly it is grounded).'}
                                                        style={{ padding: '2px 8px', fontSize: '0.7em', fontWeight: 600, borderRadius: '10px', background: '#fff3cd', color: '#856404' }}
                                                      >
                                                        Confidence {suggestion.scoring.confidence}/100
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                                {suggestion.target_entities && suggestion.target_entities.length > 0 && (
                                                  <div className="target-entities">
                                                    <small><strong>Target Entities:</strong> {suggestion.target_entities.join(', ')}</small>
                                                  </div>
                                                )}
                                                {suggestion.suggested_values && (
                                                  <div className="suggested-values">
                                                    <h5>Suggested Values:</h5>
                                                    <ul>
                                                      {Object.entries(suggestion.suggested_values).map(([key, value]) => (
                                                        <li key={key}>
                                                          <strong>{key}:</strong> {
                                                            typeof value === 'object' && value !== null
                                                              ? JSON.stringify(value, null, 2)
                                                              : typeof value === 'number' && key.includes('budget')
                                                                ? `$${(value / 100).toFixed(2)}`
                                                                : String(value)
                                                          }
                                                        </li>
                                                      ))}
                                                    </ul>
                                                  </div>
                                                )}
                                                {suggestion.reasoning && (
                                                  <div className="suggestion-reasoning">
                                                    <h5>Reasoning:</h5>
                                                    <p>{suggestion.reasoning}</p>
                                                  </div>
                                                )}
                                                <button
                                                  className="apply-changes-button"
                                                  onClick={() => handleApplyChanges(msg, suggestion)}
                                                  disabled={applyingChanges === `${msg.id}-${suggestion.action_type}`}
                                                >
                                                  {applyingChanges === `${msg.id}-${suggestion.action_type}` ? (
                                                    <>
                                                      <span className="material-symbols-outlined spinning">sync</span>
                                                      Applying...
                                                    </>
                                                  ) : (
                                                    <>
                                                      <span className="material-symbols-outlined">check_circle</span>
                                                      Apply Changes
                                                    </>
                                                  )}
                                                </button>
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {/* Facebook Campaign Analysis */}
                                        {(fbCampaignAnalysis || fbDetails.overall_analysis) && (
                                          <div className="campaign-analysis-section">
                                            <h3>Campaign Analysis</h3>

                                            {/* Facebook Optimize: Key Insights */}
                                            {fbCampaignAnalysis?.key_insights && fbCampaignAnalysis.key_insights.length > 0 && (
                                              <div className="key-issues">
                                                <h4>Key Insights</h4>
                                                {fbCampaignAnalysis.key_insights.map((insight, insightIndex) => (
                                                  <div key={insightIndex} className="issue-item">
                                                    <div className="issue-header">
                                                      <span className={`severity-badge severity-${insight.severity?.toLowerCase()}`}>
                                                        {insight.severity}
                                                      </span>
                                                    </div>
                                                    <p className="issue-description">{insight.insight}</p>
                                                    {insight.affected_entities && insight.affected_entities.length > 0 && (
                                                      <div className="affected-entities">
                                                        <small>Affected: {insight.affected_entities.join(', ')}</small>
                                                      </div>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            )}

                                            {/* Facebook Optimize: Key Issues (backward compatibility) */}
                                            {fbCampaignAnalysis?.key_issues && fbCampaignAnalysis.key_issues.length > 0 && (
                                              <div className="key-issues">
                                                <h4>Key Issues</h4>
                                                {fbCampaignAnalysis.key_issues.map((issue, issueIndex) => (
                                                  <div key={issueIndex} className="issue-item">
                                                    <div className="issue-header">
                                                      <span className={`severity-badge severity-${issue.severity?.toLowerCase()}`}>
                                                        {issue.severity}
                                                      </span>
                                                    </div>
                                                    <p className="issue-description">{issue.issue}</p>
                                                  </div>
                                                ))}
                                              </div>
                                            )}

                                            {/* Facebook Optimize: Overall Performance */}
                                            {fbCampaignAnalysis?.overall_performance && (
                                              <div className="overall-performance">
                                                <h4>Overall Performance</h4>
                                                <p>{fbCampaignAnalysis.overall_performance}</p>
                                              </div>
                                            )}

                                            {/* Facebook Optimize: Performance Metrics */}
                                            {fbCampaignAnalysis?.performance_metrics && (
                                              <div className="performance-metrics">
                                                <h4>Performance Metrics</h4>
                                                {Object.entries(fbCampaignAnalysis.performance_metrics).map(([metric, analysis]) => (
                                                  <div key={metric} className="metric-item">
                                                    <h5>{metric.toUpperCase()}</h5>
                                                    <p>{analysis}</p>
                                                  </div>
                                                ))}
                                              </div>
                                            )}

                                            {/* Facebook Analyze: Overall Analysis */}
                                            {fbDetails.overall_analysis && (
                                              <div className="overall-analysis">
                                                <h4>Overall Analysis</h4>
                                                <p>{fbDetails.overall_analysis}</p>
                                                {fbDetails.action_needed && (
                                                  <div className="action-needed-badge">
                                                    <span className="material-symbols-outlined">warning</span>
                                                    Action Required
                                                  </div>
                                                )}
                                              </div>
                                            )}

                                            {/* Facebook Analyze: Ad Set Analysis */}
                                            {fbDetails.adset_analysis && Array.isArray(fbDetails.adset_analysis) && (
                                              <div className="fb-adsets-analysis">
                                                <h4>Ad Set Breakdown</h4>
                                                {fbDetails.adset_analysis.map((adset, adsetIndex) => (
                                                  <div key={adsetIndex} className={`fb-adset-item ${adset.action_needed?.toLowerCase() === 'yes' ? 'action-needed' : adset.action_needed?.toLowerCase() === 'no' ? 'no-action-needed' : ''}`}>
                                                    <div className="adset-header">
                                                      <h5>Ad Set: {adset.adset_name || adset.adset_id}</h5>
                                                      {adset.action_needed && (
                                                        <span className={`action-badge action-${adset.action_needed.toLowerCase()}`}>
                                                          {adset.action_needed.toLowerCase() === 'yes' ? 'Action Required' : 'No Action Needed'}
                                                        </span>
                                                      )}
                                                    </div>
                                                    <div className="adset-content">
                                                      <div className="adset-section">
                                                        <h6>Analysis</h6>
                                                        <p>{adset.analysis}</p>
                                                      </div>
                                                      {adset.trends && (
                                                        <div className="adset-section">
                                                          <h6>Trends</h6>
                                                          <p>{adset.trends}</p>
                                                        </div>
                                                      )}
                                                      {adset.recommendations && (
                                                        <div className="adset-section">
                                                          <h6>Recommendations</h6>
                                                          <p>{adset.recommendations}</p>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()
                                ) : msg.jsonResponse.platform === 'reddit' ? (
                                  // Reddit Platform Template
                                  (() => {
                                    // Handle both direct details structure and nested analysis structure
                                    const redditDetails = msg.jsonResponse.details.analysis || msg.jsonResponse.details;
                                    const redditCampaignAnalysis = redditDetails.campaign_analysis;

                                    return (
                                      <>
                                        {/* Reddit Optimization Summary */}
                                        {redditDetails.optimization_summary && (
                                          <div className="optimization-summary-section">
                                            <h3>Optimization Summary</h3>
                                            <p>{redditDetails.optimization_summary}</p>
                                          </div>
                                        )}

                                        {/* Reddit Recommendations (for optimize-campaign) */}
                                        {redditDetails.recommendations && redditDetails.recommendations.length > 0 && (
                                          <div className="recommendations-section">
                                            <h3>Recommendations</h3>
                                            {redditDetails.recommendations.map((rec, recIndex) => (
                                              <div key={recIndex} className="recommendation-item">
                                                <div className="recommendation-header">
                                                  <h4>{rec.title}</h4>
                                                  <span className={`priority-badge priority-${rec.priority?.toLowerCase()}`}>
                                                    {rec.priority} PRIORITY
                                                  </span>
                                                </div>
                                                <p className="recommendation-description">{rec.description}</p>
                                                {rec.expected_impact && (
                                                  <div className="expected-impact">
                                                    <h5>Expected Impact:</h5>
                                                    <p>{rec.expected_impact}</p>
                                                  </div>
                                                )}
                                                {rec.implementation_steps && rec.implementation_steps.length > 0 && (
                                                  <div className="implementation-steps">
                                                    <h5>Implementation Steps:</h5>
                                                    <ol>
                                                      {rec.implementation_steps.map((step, stepIndex) => (
                                                        <li key={stepIndex}>{step}</li>
                                                      ))}
                                                    </ol>
                                                  </div>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {/* Reddit Actionable Suggestions */}
                                        {redditDetails.actionable_suggestions && redditDetails.actionable_suggestions.length > 0 && (
                                          <div className="actionable-suggestions-section">
                                            <h3>Actionable Suggestions</h3>
                                            {redditDetails.actionable_suggestions.map((suggestion, sugIndex) => (
                                              <div key={sugIndex} className="suggestion-item">
                                                <div className="suggestion-header">
                                                  <h4>{suggestion.action_type}</h4>
                                                </div>
                                                <p className="suggestion-description">{suggestion.description}</p>
                                                {suggestion.target_entities && suggestion.target_entities.length > 0 && (
                                                  <div className="target-entities">
                                                    <small><strong>Target Entities:</strong> {suggestion.target_entities.join(', ')}</small>
                                                  </div>
                                                )}
                                                {suggestion.suggested_values && (
                                                  <div className="suggested-values">
                                                    <h5>Suggested Values:</h5>
                                                    <ul>
                                                      {Object.entries(suggestion.suggested_values).map(([key, value]) => (
                                                        <li key={key}>
                                                          <strong>{key}:</strong> {
                                                            typeof value === 'object' && value !== null
                                                              ? JSON.stringify(value, null, 2)
                                                              : typeof value === 'number' && key.includes('budget')
                                                                ? `$${(value / 100).toFixed(2)}`
                                                                : String(value)
                                                          }
                                                        </li>
                                                      ))}
                                                    </ul>
                                                  </div>
                                                )}
                                                {suggestion.reasoning && (
                                                  <div className="suggestion-reasoning">
                                                    <h5>Reasoning:</h5>
                                                    <p>{suggestion.reasoning}</p>
                                                  </div>
                                                )}
                                                <button
                                                  className="apply-changes-button"
                                                  onClick={() => handleApplyChanges(msg, suggestion)}
                                                  disabled={applyingChanges === `${msg.id}-${suggestion.action_type}`}
                                                >
                                                  {applyingChanges === `${msg.id}-${suggestion.action_type}` ? (
                                                    <>
                                                      <span className="material-symbols-outlined spinning">sync</span>
                                                      Applying...
                                                    </>
                                                  ) : (
                                                    <>
                                                      <span className="material-symbols-outlined">check_circle</span>
                                                      Apply Changes
                                                    </>
                                                  )}
                                                </button>
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {/* Reddit Campaign Analysis */}
                                        {(redditCampaignAnalysis || redditDetails.overall_analysis) && (
                                          <div className="campaign-analysis-section">
                                            <h3>Campaign Analysis</h3>

                                            {/* Reddit Optimize: Key Insights */}
                                            {redditCampaignAnalysis?.key_insights && redditCampaignAnalysis.key_insights.length > 0 && (
                                              <div className="key-issues">
                                                <h4>Key Insights</h4>
                                                {redditCampaignAnalysis.key_insights.map((insight, insightIndex) => (
                                                  <div key={insightIndex} className="issue-item">
                                                    <div className="issue-header">
                                                      <span className={`severity-badge severity-${insight.severity?.toLowerCase()}`}>
                                                        {insight.severity}
                                                      </span>
                                                    </div>
                                                    <p className="issue-description">{insight.insight}</p>
                                                    {insight.affected_entities && insight.affected_entities.length > 0 && (
                                                      <div className="affected-entities">
                                                        <small>Affected: {insight.affected_entities.join(', ')}</small>
                                                      </div>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            )}

                                            {/* Reddit Overall Analysis */}
                                            {redditDetails.overall_analysis && (
                                              <div className="overall-analysis">
                                                <h4>Overall Analysis</h4>
                                                <p>{redditDetails.overall_analysis}</p>
                                              </div>
                                            )}

                                            {/* Reddit Ad Group Analysis */}
                                            {redditCampaignAnalysis?.ad_group_analysis && redditCampaignAnalysis.ad_group_analysis.length > 0 && (
                                              <div className="adset-analysis">
                                                <h4>Ad Group Analysis</h4>
                                                {redditCampaignAnalysis.ad_group_analysis.map((adgroup, adgroupIndex) => (
                                                  <div key={adgroupIndex} className="adset-item">
                                                    <div className="adset-header">
                                                      <h5>Ad Group: {adgroup.ad_group_name || adgroup.ad_group_id}</h5>
                                                      {adgroup.action_needed && (
                                                        <span className={`action-badge action-${adgroup.action_needed.toLowerCase()}`}>
                                                          {adgroup.action_needed.toLowerCase() === 'yes' ? 'Action Required' : 'No Action Needed'}
                                                        </span>
                                                      )}
                                                    </div>
                                                    <div className="adset-content">
                                                      <div className="adset-section">
                                                        <h6>Analysis</h6>
                                                        <p>{adgroup.analysis}</p>
                                                      </div>
                                                      {adgroup.trends && (
                                                        <div className="adset-section">
                                                          <h6>Trends</h6>
                                                          <p>{adgroup.trends}</p>
                                                        </div>
                                                      )}
                                                      {adgroup.recommendations && (
                                                        <div className="adset-section">
                                                          <h6>Recommendations</h6>
                                                          <p>{adgroup.recommendations}</p>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()
                                ) : usesStructuredOptimizerTemplate(msg.jsonResponse.platform) ? (
                                  // Structured optimizer results: Search/PMax/DemandGen and LinkedIn.
                                  (() => {
                                    const optimizerAnalysis = msg.jsonResponse.details.analysis;
                                    const optimizerView = getOptimizerAnalysisView(optimizerAnalysis);
                                    const optimizerCampaignAnalysis = optimizerView.campaignAnalysis;

                                    return (
                                      <>
                                        {optimizerView.summary && (
                                          <div className="optimization-summary-section">
                                            <h3>📊 Optimization Summary</h3>
                                            <div style={{
                                              padding: '15px',
                                              backgroundColor: '#f8f9fa',
                                              borderRadius: '6px',
                                              marginBottom: '15px'
                                            }}>
                                              {optimizerView.summary.total_recommendations && (
                                                <p style={{ margin: '5px 0' }}>
                                                  <strong>Total Recommendations:</strong> {optimizerView.summary.total_recommendations}
                                                </p>
                                              )}
                                              {optimizerView.summary.high_priority_actions && (
                                                <p style={{ margin: '5px 0' }}>
                                                  <strong>High Priority Actions:</strong> {optimizerView.summary.high_priority_actions}
                                                </p>
                                              )}
                                              {optimizerView.summary.expected_performance_impact && (
                                                <p style={{ margin: '5px 0' }}>
                                                  <strong>Expected Impact:</strong> {optimizerView.summary.expected_performance_impact}
                                                </p>
                                              )}
                                              {optimizerView.summary.next_steps && (
                                                <div style={{ marginTop: '10px' }}>
                                                  <strong>Next Steps:</strong>
                                                  <p style={{ margin: '5px 0', whiteSpace: 'pre-line' }}>{optimizerView.summary.next_steps}</p>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {optimizerView.actionableSuggestions.length > 0 && (
                                          <div className="recommendations-section">
                                            <h3>💡 Actionable Recommendations</h3>
                                            {[...optimizerView.actionableSuggestions]
                                              .sort((a, b) => ((b?.scoring?.priority_score ?? -1) - (a?.scoring?.priority_score ?? -1)))
                                              .map((suggestion, sugIndex) => {
                                              const suggestionView = getOptimizerSuggestionView(suggestion);
                                              return <div key={sugIndex} className="recommendation-item">
                                                <div className="recommendation-header">
                                                  <h4>
                                                    {suggestion.action_type === 'status_update' && '🔄 '}
                                                    {suggestion.action_type === 'budget_update' && '💰 '}
                                                    {suggestion.action_type === 'bid_update' && '🎯 '}
                                                    {suggestion.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                    {suggestion.target_type && ` - ${suggestion.target_type.replace(/\b\w/g, l => l.toUpperCase())}`}
                                                  </h4>
                                                  {suggestion.priority && (
                                                    <span className={`priority-badge priority-${suggestion.priority.toLowerCase()}`}>
                                                      {suggestion.priority.toUpperCase()} PRIORITY
                                                    </span>
                                                  )}
                                                </div>

                                                {/* Optimizer scoring: lift range, confidence, priority.
                                                    Shared by Search/PMax/DemandGen/LinkedIn; basis and
                                                    rationale ride in tooltips. Renders only when the
                                                    backend emitted scoring (graceful otherwise). */}
                                                {suggestion.scoring && (suggestion.scoring.priority_score != null || suggestion.scoring.expected_lift || suggestion.scoring.confidence != null) && (
                                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '6px 0 10px' }}>
                                                    {suggestion.scoring.priority_score != null && (
                                                      <span
                                                        title="Composite score = expected value × confidence × reversibility. Higher means do it first."
                                                        style={{ padding: '2px 8px', fontSize: '0.7em', fontWeight: 600, borderRadius: '10px', background: '#eef2ff', color: '#4338ca' }}
                                                      >
                                                        ★ Priority {suggestion.scoring.priority_score}/100
                                                      </span>
                                                    )}
                                                    {suggestion.scoring.expected_lift && (() => {
                                                      const l = suggestion.scoring.expected_lift;
                                                      const arrow = l.direction === 'decrease' ? '↓' : '↑';
                                                      const { range_low_pct: lo, range_high_pct: hi, point_pct: pt } = l;
                                                      const mag = (lo != null && hi != null && Number(lo) !== Number(hi))
                                                        ? `${Math.abs(Number(lo))}–${Math.abs(Number(hi))}%`
                                                        : (pt != null ? `${Math.abs(Number(pt))}%` : null);
                                                      if (!mag) return null;
                                                      return (
                                                        <span
                                                          title={l.basis || 'Estimated lift'}
                                                          style={{ padding: '2px 8px', fontSize: '0.7em', fontWeight: 600, borderRadius: '10px', background: '#e7f5ff', color: '#1971c2' }}
                                                        >
                                                          Est. lift {arrow} {mag}{l.metric ? ' ' + String(l.metric).replace(/_/g, ' ') : ''}
                                                        </span>
                                                      );
                                                    })()}
                                                    {suggestion.scoring.confidence != null && (
                                                      <span
                                                        title={suggestion.scoring.confidence_rationale || 'Reliability of the lift estimate (data volume + how directly it is grounded).'}
                                                        style={{ padding: '2px 8px', fontSize: '0.7em', fontWeight: 600, borderRadius: '10px', background: '#fff3cd', color: '#856404' }}
                                                      >
                                                        Confidence {suggestion.scoring.confidence}/100
                                                      </span>
                                                    )}
                                                  </div>
                                                )}

                                                {suggestionView.description && (
                                                  <p style={{ margin: '5px 0 10px', lineHeight: '1.5' }}>{suggestionView.description}</p>
                                                )}

                                                {suggestion.target_id && (
                                                  <div style={{
                                                    fontSize: '0.85em',
                                                    color: '#666',
                                                    marginBottom: '10px',
                                                    fontFamily: 'monospace'
                                                  }}>
                                                    Target ID: {suggestion.target_id}
                                                  </div>
                                                )}

                                                {suggestionView.targetEntities && (
                                                  <div style={{ marginBottom: '10px', fontFamily: 'monospace' }}>
                                                    Target IDs: {suggestionView.targetEntities}
                                                  </div>
                                                )}

                                                {suggestion.current_value && (
                                                  <div style={{ marginBottom: '10px' }}>
                                                    <strong>Current Value:</strong> <code>{
                                                      typeof suggestion.current_value === 'object'
                                                        ? (Array.isArray(suggestion.current_value)
                                                            ? suggestion.current_value.map(v => typeof v === 'object' ? (v.keyword || JSON.stringify(v)) : v).join(', ')
                                                            : JSON.stringify(suggestion.current_value))
                                                        : suggestion.current_value
                                                    }</code>
                                                  </div>
                                                )}

                                                {suggestionView.recommendedValue != null && (
                                                  <div style={{ marginBottom: '10px' }}>
                                                    <strong>Recommended Value:</strong> <code style={{
                                                      backgroundColor: '#e7f3ff',
                                                      padding: '2px 6px',
                                                      borderRadius: '3px'
                                                    }}>{
                                                      typeof suggestionView.recommendedValue === 'object'
                                                        ? (Array.isArray(suggestionView.recommendedValue)
                                                            ? suggestionView.recommendedValue.map(v => typeof v === 'object' ? (v.keyword || JSON.stringify(v)) : v).join(', ')
                                                            : JSON.stringify(suggestionView.recommendedValue))
                                                        : suggestionView.recommendedValue
                                                    }</code>
                                                  </div>
                                                )}

                                                {suggestionView.reasoning && (
                                                  <div style={{ marginBottom: '10px' }}>
                                                    <strong>Reasoning:</strong>
                                                    <p style={{ margin: '5px 0', lineHeight: '1.5' }}>{suggestionView.reasoning}</p>
                                                  </div>
                                                )}

                                                {suggestion.expected_impact && (
                                                  <div className="expected-impact" style={{
                                                    backgroundColor: '#f0f8ff',
                                                    padding: '10px',
                                                    borderRadius: '4px',
                                                    borderLeft: '3px solid #0066cc',
                                                    marginTop: '10px'
                                                  }}>
                                                    <strong>Expected Impact:</strong>
                                                    <p style={{ margin: '5px 0' }}>{suggestion.expected_impact}</p>
                                                  </div>
                                                )}

                                                {canApplyStructuredOptimizerSuggestion(msg.jsonResponse.platform) && suggestion.auto_applicable !== false && (
                                                  <button
                                                    className="apply-changes-button"
                                                    onClick={() => handleApplyChanges(msg, suggestion)}
                                                    disabled={applyingChanges === `${msg.id}-${suggestion.action_type}`}
                                                  >
                                                    {applyingChanges === `${msg.id}-${suggestion.action_type}` ? (
                                                      <>
                                                        <span className="material-symbols-outlined spinning">sync</span>
                                                        Applying...
                                                      </>
                                                    ) : (
                                                      <>
                                                        <span className="material-symbols-outlined">check_circle</span>
                                                        Apply Changes
                                                      </>
                                                    )}
                                                  </button>
                                                )}
                                              </div>;
                                            })}
                                          </div>
                                        )}

                                        {optimizerView.recommendations.length > 0 && (
                                          <div className="recommendations-section">
                                            <h3>Recommendations</h3>
                                            {optimizerView.recommendations.map((recommendation, index) => (
                                              <div key={recommendation.recommendation_id || index} className="recommendation-item">
                                                <div className="recommendation-header">
                                                  <h4>{recommendation.title}</h4>
                                                  {recommendation.priority && (
                                                    <span className={`priority-badge priority-${recommendation.priority.toLowerCase()}`}>
                                                      {recommendation.priority.toUpperCase()} PRIORITY
                                                    </span>
                                                  )}
                                                </div>
                                                {recommendation.description && <p>{recommendation.description}</p>}
                                                {recommendation.expected_impact && (
                                                  <p><strong>Expected Impact:</strong> {recommendation.expected_impact}</p>
                                                )}
                                                {recommendation.affected_entities?.length > 0 && (
                                                  <p style={{ fontFamily: 'monospace' }}>
                                                    Affected IDs: {recommendation.affected_entities.join(', ')}
                                                  </p>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {optimizerCampaignAnalysis && (
                                          <div className="campaign-analysis-section">
                                            <h3>📈 Campaign Analysis</h3>

                                            {/* Overall Performance */}
                                            {optimizerCampaignAnalysis.overall_performance && (
                                              <div style={{
                                                padding: '15px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: '6px',
                                                marginBottom: '15px'
                                              }}>
                                                <h4 style={{ marginTop: 0 }}>Overall Performance</h4>
                                                <p style={{ margin: 0, lineHeight: '1.6', color: '#333' }}>{optimizerCampaignAnalysis.overall_performance}</p>
                                              </div>
                                            )}

                                            {/* Key Metrics */}
                                            {optimizerView.keyMetrics && (
                                              <div style={{ marginBottom: '20px' }}>
                                                <h4>Key Metrics</h4>
                                                <div style={{
                                                  display: 'grid',
                                                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                                  gap: '15px'
                                                }}>
                                                  {Object.entries(optimizerView.keyMetrics).map(([key, value]) => (
                                                    <div key={key} style={{
                                                      padding: '12px',
                                                      backgroundColor: 'white',
                                                      border: '1px solid #e0e0e0',
                                                      borderRadius: '6px'
                                                    }}>
                                                      <div style={{
                                                        fontSize: '0.85em',
                                                        color: '#666',
                                                        textTransform: 'uppercase',
                                                        marginBottom: '5px',
                                                        fontWeight: '600'
                                                      }}>
                                                        {key.replace(/_/g, ' ')}
                                                      </div>
                                                      <div style={{ fontSize: '0.95em', lineHeight: '1.4', color: '#333' }}>{value}</div>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}

                                            {optimizerView.keyInsights.length > 0 && (
                                              <div style={{ marginBottom: '20px' }}>
                                                <h4>Key Insights</h4>
                                                {optimizerView.keyInsights.map((insight, index) => (
                                                  <div key={index} style={{ marginBottom: '10px' }}>
                                                    {insight.severity && <strong>{insight.severity}: </strong>}
                                                    {insight.insight}
                                                    {insight.affected_entities?.length > 0 && (
                                                      <div style={{ color: '#666', fontFamily: 'monospace', marginTop: '4px' }}>
                                                        Affected IDs: {insight.affected_entities.join(', ')}
                                                      </div>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            )}

                                            {optimizerView.campaignAssessments.map(([label, value]) => (
                                              <div key={label} style={{ marginBottom: '15px' }}>
                                                <strong>{label}:</strong>
                                                <p style={{ margin: '5px 0', lineHeight: '1.5', color: '#333' }}>{value}</p>
                                              </div>
                                            ))}

                                            {/* Performance Trends */}
                                            {optimizerCampaignAnalysis.performance_trends && (
                                              <div style={{
                                                padding: '15px',
                                                backgroundColor: '#fff9e6',
                                                borderRadius: '6px',
                                                borderLeft: '4px solid #ffc107',
                                                marginBottom: '15px'
                                              }}>
                                                <h4 style={{ marginTop: 0 }}>Performance Trends</h4>
                                                <p style={{ margin: 0, lineHeight: '1.6', color: '#333' }}>{optimizerCampaignAnalysis.performance_trends}</p>
                                              </div>
                                            )}

                                            {optimizerView.entitySections.map(section => (
                                              <div key={section.title} style={{ marginTop: '20px' }}>
                                                <h4>{section.title}</h4>
                                                {section.items.map((entity, entityIndex) => (
                                                  <div key={entity.id || entityIndex} style={{
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: '6px',
                                                    padding: '15px',
                                                    marginBottom: '15px',
                                                    backgroundColor: 'white'
                                                  }}>
                                                    <div style={{
                                                      display: 'flex',
                                                      justifyContent: 'space-between',
                                                      alignItems: 'center',
                                                      marginBottom: '10px',
                                                      paddingBottom: '10px',
                                                      borderBottom: '1px solid #e0e0e0'
                                                    }}>
                                                      <h5 style={{ margin: 0 }}>{entity.name}</h5>
                                                      {entity.id && (
                                                        <span style={{ fontSize: '0.75em', color: '#666', fontFamily: 'monospace' }}>
                                                          ID: {entity.id}
                                                        </span>
                                                      )}
                                                    </div>

                                                    {entity.metadata.map(([label, value]) => (
                                                      <div key={label} style={{ marginBottom: '10px' }}>
                                                        <strong>{label}:</strong> {value}
                                                      </div>
                                                    ))}

                                                    {entity.performance_summary && (
                                                      <div style={{ marginBottom: '10px' }}>
                                                        <strong>Performance Summary:</strong>
                                                        <p style={{ margin: '5px 0', lineHeight: '1.5', color: '#333' }}>{entity.performance_summary}</p>
                                                      </div>
                                                    )}

                                                    {entity.issues_identified?.length > 0 && (
                                                      <div style={{ marginBottom: '10px' }}>
                                                        <strong>Issues Identified:</strong>
                                                        <ul style={{ margin: '5px 0', paddingLeft: '20px', color: '#333' }}>
                                                          {entity.issues_identified.map((issue, issueIndex) => <li key={issueIndex}>{issue}</li>)}
                                                        </ul>
                                                      </div>
                                                    )}

                                                    {entity.recommendations?.length > 0 && (
                                                      <div style={{ backgroundColor: '#e7f3ff', padding: '10px', borderRadius: '4px' }}>
                                                        <strong>Recommendations:</strong>
                                                        <ul style={{ margin: '5px 0', paddingLeft: '20px', color: '#333' }}>
                                                          {entity.recommendations.map((recommendation, recommendationIndex) => (
                                                            <li key={recommendationIndex}>{recommendation}</li>
                                                          ))}
                                                        </ul>
                                                      </div>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()
                                ) : (
                                  // DV360 Platform Template (default for backward compatibility)
                                  <>
                                    {/* Render Pacing Recommendations (daily budget, frequency cap, audiences) */}
                                    {msg.jsonResponse.details.pacing_recommendations && msg.jsonResponse.details.pacing_recommendations.length > 0 && (
                                      <div className="recommendations-section pacing-recommendations-section">
                                        <h3>⏱️ Pacing Recommendations</h3>
                                        <p className="section-description">
                                          Actions to improve delivery pacing — adjust daily budgets, add frequency caps, or expand audiences.
                                        </p>
                                        {msg.jsonResponse.details.pacing_recommendations.map((pace, paceIndex) => (
                                          <div key={paceIndex} className="recommendation-item pacing-recommendation-item">
                                            <div className="recommendation-header">
                                              <h4>{(pace.action_type ? pace.action_type.replace(/_/g, ' ') : 'Pacing')} — {pace.line_item_name || pace.line_item_id}</h4>
                                              {pace.pacing_status && (
                                                <span className={`severity-badge pacing-status-${pace.pacing_status?.toLowerCase()}`}>
                                                  {pace.pacing_status.replace(/_/g, ' ')}
                                                </span>
                                              )}
                                              {pace.expected_impact && (
                                                <span className={`impact-badge impact-${pace.expected_impact?.toLowerCase()}`}>
                                                  {pace.expected_impact} IMPACT
                                                </span>
                                              )}
                                            </div>
                                            {(pace.current_value != null || pace.recommended_value != null) && (
                                              <div className="pacing-change">
                                                {pace.current_value != null && (
                                                  <span className="pacing-current">{pace.current_value}</span>
                                                )}
                                                {pace.current_value != null && pace.recommended_value != null && ' → '}
                                                {pace.recommended_value != null && (
                                                  <span className="pacing-recommended">{pace.recommended_value}</span>
                                                )}
                                              </div>
                                            )}
                                            {pace.rationale && <p className="recommendation-description">{pace.rationale}</p>}
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Render Recommendations */}
                                    {msg.jsonResponse.details.recommendations && msg.jsonResponse.details.recommendations.length > 0 && (
                                      <div className="recommendations-section">
                                        <h3>Recommendations</h3>
                                        {msg.jsonResponse.details.recommendations.map((rec, recIndex) => (
                                          <div key={recIndex} className="recommendation-item">
                                            <div className="recommendation-header">
                                              <h4>{rec.title}</h4>
                                              <span className={`impact-badge impact-${rec.expected_impact?.toLowerCase()}`}>
                                                {rec.expected_impact} IMPACT
                                              </span>
                                            </div>
                                            <p className="recommendation-description">{rec.description}</p>
                                            {rec.implementation_steps && rec.implementation_steps.length > 0 && (
                                              <div className="implementation-steps">
                                                <h5>Implementation Steps:</h5>
                                                <ol>
                                                  {rec.implementation_steps.map((step, stepIndex) => (
                                                    <li key={stepIndex}>{step}</li>
                                                  ))}
                                                </ol>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Render Campaign Analysis */}
                                    {msg.jsonResponse.details.campaign_analysis && (
                                      <div className="campaign-analysis-section">
                                        <h3>Campaign Analysis</h3>

                                        {/* For optimize-campaign: render key_issues */}
                                        {msg.jsonResponse.details.campaign_analysis.key_issues && msg.jsonResponse.details.campaign_analysis.key_issues.length > 0 && (
                                          <div className="key-issues">
                                            <h4>Key Issues</h4>
                                            {msg.jsonResponse.details.campaign_analysis.key_issues.map((issue, issueIndex) => (
                                              <div key={issueIndex} className="issue-item">
                                                <div className="issue-header">
                                                  <span className={`severity-badge severity-${issue.severity?.toLowerCase()}`}>
                                                    {issue.severity}
                                                  </span>
                                                </div>
                                                <p className="issue-description">{issue.issue}</p>
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {/* For analyze-campaign: render overall_analysis */}
                                        {msg.jsonResponse.details.campaign_analysis.overall_analysis && (
                                          <div className={`overall-analysis ${msg.jsonResponse.details.campaign_analysis.action_needed?.toLowerCase() === 'yes' ? 'action-needed' : msg.jsonResponse.details.campaign_analysis.action_needed?.toLowerCase() === 'no' ? 'no-action-needed' : ''}`}>
                                            <div className="analysis-header">
                                              <h4>Overall Analysis</h4>
                                              {msg.jsonResponse.details.campaign_analysis.action_needed && (
                                                <span className={`action-badge action-${msg.jsonResponse.details.campaign_analysis.action_needed.toLowerCase()}`}>
                                                  {msg.jsonResponse.details.campaign_analysis.action_needed.toLowerCase() === 'yes' ? 'Action Required' : 'No Action Needed'}
                                                </span>
                                              )}
                                            </div>
                                            <p>{msg.jsonResponse.details.campaign_analysis.overall_analysis}</p>
                                          </div>
                                        )}

                                        {/* For optimize-campaign: render overall_performance */}
                                        {msg.jsonResponse.details.campaign_analysis.overall_performance && (
                                          <div className="overall-performance">
                                            <h4>Overall Performance</h4>
                                            <p>{msg.jsonResponse.details.campaign_analysis.overall_performance}</p>
                                          </div>
                                        )}

                                        {/* For optimize-campaign: render pacing_summary */}
                                        {msg.jsonResponse.details.campaign_analysis.pacing_summary && (
                                          <div className="pacing-summary">
                                            <h4>Pacing Summary</h4>
                                            <p>{msg.jsonResponse.details.campaign_analysis.pacing_summary}</p>
                                          </div>
                                        )}

                                        {/* For analyze-campaign: render line_item_analysis */}
                                        {msg.jsonResponse.details.campaign_analysis.line_item_analysis && msg.jsonResponse.details.campaign_analysis.line_item_analysis.length > 0 && (
                                          <div className="line-item-analysis">
                                            <h4>Line Item Analysis</h4>
                                            {msg.jsonResponse.details.campaign_analysis.line_item_analysis.map((lineItem, lineItemIndex) => (
                                              <div key={lineItemIndex} className={`line-item-item ${lineItem.action_needed?.toLowerCase() === 'yes' ? 'action-needed' : lineItem.action_needed?.toLowerCase() === 'no' ? 'no-action-needed' : ''}`}>
                                                <div className="line-item-header">
                                                  <h5>Line Item ID: {lineItem.line_item_id}</h5>
                                                  {lineItem.action_needed && (
                                                    <span className={`action-badge action-${lineItem.action_needed.toLowerCase()}`}>
                                                      {lineItem.action_needed.toLowerCase() === 'yes' ? 'Action Required' : 'No Action Needed'}
                                                    </span>
                                                  )}
                                                </div>
                                                <div className="line-item-content">
                                                  <div className="line-item-section">
                                                    <h6>Analysis</h6>
                                                    <p>{lineItem.analysis}</p>
                                                  </div>
                                                  {lineItem.trends && (
                                                    <div className="line-item-section">
                                                      <h6>Trends</h6>
                                                      <p>{lineItem.trends}</p>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}

                                {/* Render Optimization Summary */}
                                {msg.jsonResponse.details.optimization_summary && (
                                  <div className="optimization-summary-section">
                                    <h3>Optimization Summary</h3>
                                    <p>{msg.jsonResponse.details.optimization_summary}</p>
                                  </div>
                                )}

                                {/* Render Audience Expansion Recommendations */}
                                {msg.jsonResponse.details.audience_expansion_recommendations && msg.jsonResponse.details.audience_expansion_recommendations.length > 0 && (
                                  <div className="audience-recommendations-section">
                                    <h3>🎯 Audience Expansion Recommendations</h3>
                                    <p className="section-description">
                                      Suggested audience expansions to improve campaign reach and performance based on current targeting analysis.
                                    </p>
                                    <div className="audience-recommendations-list">
                                      {msg.jsonResponse.details.audience_expansion_recommendations.map((recommendation, recIndex) => (
                                        <div key={recIndex} className="audience-recommendation-item">
                                          <div className="recommendation-header">
                                            <h4>Line Item: {recommendation.line_item_id}</h4>
                                          </div>
                                          <div className="recommendation-content">
                                            {recommendation.google_audience_to_expand && (
                                              <div className="audience-expansion-item">
                                                <div className="expansion-type">
                                                  <span className="material-symbols-outlined">groups</span>
                                                  <strong>Google Audience Expansion</strong>
                                                </div>
                                                <div className="expansion-details">
                                                  <span className="audience-name">{recommendation.google_audience_to_expand}</span>
                                                </div>
                                              </div>
                                            )}
                                            {recommendation.first_party_audience_to_expand && (
                                              <div className="audience-expansion-item">
                                                <div className="expansion-type">
                                                  <span className="material-symbols-outlined">person</span>
                                                  <strong>First-Party Audience Expansion</strong>
                                                </div>
                                                <div className="expansion-details">
                                                  <span className="audience-name">{recommendation.first_party_audience_to_expand}</span>
                                                </div>
                                              </div>
                                            )}
                                            {!recommendation.google_audience_to_expand && !recommendation.first_party_audience_to_expand && (
                                              <div className="no-expansion">
                                                <span className="material-symbols-outlined">info</span>
                                                <span>No specific audience expansion recommended for this line item</span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* QA Campaign Specific Sections */}
                                {msg.action === 'qa-campaign' && (
                                  <>
                                    {/* Render Validation Summary */}
                                    {msg.jsonResponse.details.validation_summary && (
                                      <div className="validation-summary-section">
                                        <h3>Validation Summary</h3>
                                        <div className="validation-stats">
                                          <div className="stat-item">
                                            <span className="stat-label">Validation Status:</span>
                                            <span className={`stat-value ${msg.jsonResponse.details.validation_summary.validation_passed ? 'success' : 'error'}`}>
                                              {msg.jsonResponse.details.validation_summary.validation_passed ? 'PASSED' : 'FAILED'}
                                            </span>
                                          </div>
                                          <div className="stat-item">
                                            <span className="stat-label">Line Items Expected:</span>
                                            <span className="stat-value">{msg.jsonResponse.details.validation_summary.total_line_items_expected || 0}</span>
                                          </div>
                                          <div className="stat-item">
                                            <span className="stat-label">Line Items Actual:</span>
                                            <span className="stat-value">{msg.jsonResponse.details.validation_summary.total_line_items_actual || 0}</span>
                                          </div>
                                          <div className="stat-item">
                                            <span className="stat-label">Total Discrepancies:</span>
                                            <span className={`stat-value ${msg.jsonResponse.details.validation_summary.total_discrepancies > 0 ? 'error' : 'success'}`}>
                                              {msg.jsonResponse.details.validation_summary.total_discrepancies || 0}
                                            </span>
                                          </div>
                                          {msg.jsonResponse.details.validation_summary.high_severity_issues > 0 && (
                                            <div className="stat-item">
                                              <span className="stat-label">High Severity Issues:</span>
                                              <span className="stat-value error">{msg.jsonResponse.details.validation_summary.high_severity_issues}</span>
                                            </div>
                                          )}
                                          {msg.jsonResponse.details.validation_summary.medium_severity_issues > 0 && (
                                            <div className="stat-item">
                                              <span className="stat-label">Medium Severity Issues:</span>
                                              <span className="stat-value warning">{msg.jsonResponse.details.validation_summary.medium_severity_issues}</span>
                                            </div>
                                          )}
                                          {msg.jsonResponse.details.validation_summary.low_severity_issues > 0 && (
                                            <div className="stat-item">
                                              <span className="stat-label">Low Severity Issues:</span>
                                              <span className="stat-value info">{msg.jsonResponse.details.validation_summary.low_severity_issues}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Render Line Item Comparison */}
                                    {msg.jsonResponse.details.line_item_comparison && (
                                      <div className="line-item-comparison-section">
                                        <h3>Line Item Comparison</h3>
                                        <div className="comparison-stats">
                                          <div className="stat-item">
                                            <span className="stat-label">Count Match:</span>
                                            <span className={`stat-value ${msg.jsonResponse.details.line_item_comparison.line_items.match ? 'success' : 'error'}`}>
                                              {msg.jsonResponse.details.line_item_comparison.line_items.match ? 'YES' : 'NO'}
                                            </span>
                                          </div>
                                          <div className="stat-item">
                                            <span className="stat-label">Expected Count:</span>
                                            <span className="stat-value">{msg.jsonResponse.details.line_item_comparison.line_items.expected_count}</span>
                                          </div>
                                          <div className="stat-item">
                                            <span className="stat-label">Actual Count:</span>
                                            <span className="stat-value">{msg.jsonResponse.details.line_item_comparison.line_items.actual_count}</span>
                                          </div>
                                        </div>

                                        {msg.jsonResponse.details.line_item_comparison.line_items.matched_names && msg.jsonResponse.details.line_item_comparison.line_items.matched_names.length > 0 && (
                                          <div className="matched-items">
                                            <h4>Matched Line Items ({msg.jsonResponse.details.line_item_comparison.line_items.matched_names.length})</h4>
                                            <div className="item-list">
                                              {msg.jsonResponse.details.line_item_comparison.line_items.matched_names.map((name, index) => (
                                                <div key={index} className="item-name success-item">
                                                  <span className="material-symbols-outlined">check_circle</span>
                                                  {name}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {msg.jsonResponse.details.line_item_comparison.line_items.mismatched_names && msg.jsonResponse.details.line_item_comparison.line_items.mismatched_names.length > 0 && (
                                          <div className="mismatched-items">
                                            <h4>Mismatched Line Items ({msg.jsonResponse.details.line_item_comparison.line_items.mismatched_names.length})</h4>
                                            <div className="item-list">
                                              {msg.jsonResponse.details.line_item_comparison.line_items.mismatched_names.map((name, index) => (
                                                <div key={index} className="item-name error-item">
                                                  <span className="material-symbols-outlined">error</span>
                                                  {name}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Render Targeting Comparison */}
                                    {msg.jsonResponse.details.targeting_comparison && msg.jsonResponse.details.targeting_comparison.line_items && (
                                      <div className="targeting-comparison-section">
                                        <h3>Targeting Comparison</h3>
                                        {msg.jsonResponse.details.targeting_comparison.line_items.map((lineItem, index) => (
                                          <div key={index} className="targeting-item">
                                            <div className="targeting-header">
                                              <h4>{lineItem.line_item_name}</h4>
                                            </div>
                                            <div className="targeting-details">
                                              <div className="targeting-row">
                                                <span className="targeting-label">First Party Audiences:</span>
                                                <span className={`targeting-status ${lineItem.first_party_match ? 'success' : 'error'}`}>
                                                  {lineItem.first_party_match ? 'MATCH' : 'MISMATCH'}
                                                </span>
                                                {!lineItem.first_party_match && (
                                                  <div className="targeting-mismatch-details">
                                                    <div>Expected: {lineItem.input_first_party_audience_ids?.join(', ') || 'None'}</div>
                                                    <div>Actual: {lineItem.prod_first_party_audience_ids?.join(', ') || 'None'}</div>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="targeting-row">
                                                <span className="targeting-label">Google Audiences:</span>
                                                <span className={`targeting-status ${lineItem.google_match ? 'success' : 'error'}`}>
                                                  {lineItem.google_match ? 'MATCH' : 'MISMATCH'}
                                                </span>
                                                {!lineItem.google_match && (
                                                  <div className="targeting-mismatch-details">
                                                    <div>Expected: {lineItem.input_google_audience_ids?.join(', ') || 'None'}</div>
                                                    <div>Actual: {lineItem.prod_google_audience_ids?.join(', ') || 'None'}</div>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="targeting-row">
                                                <span className="targeting-label">Geo Targeting:</span>
                                                <span className={`targeting-status ${lineItem.geo_match ? 'success' : 'error'}`}>
                                                  {lineItem.geo_match ? 'MATCH' : 'MISMATCH'}
                                                </span>
                                                {!lineItem.geo_match && (
                                                  <div className="targeting-mismatch-details">
                                                    <div>Expected: {lineItem.input_geo_targeting?.join(', ') || 'None'}</div>
                                                    <div>Actual: {lineItem.prod_geo_targeting?.join(', ') || 'None'}</div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Render Discrepancies */}
                                    {msg.jsonResponse.details.discrepancies && msg.jsonResponse.details.discrepancies.length > 0 && (
                                      <div className="discrepancies-section">
                                        <h3>Campaign Discrepancies</h3>
                                        {msg.jsonResponse.details.discrepancies.map((discrepancy, discIndex) => (
                                          <div key={discIndex} className="discrepancy-item">
                                            <div className="discrepancy-header">
                                              <span className={`severity-badge severity-${discrepancy.severity?.toLowerCase()}`}>
                                                {discrepancy.severity}
                                              </span>
                                              <span className="category-badge">{discrepancy.category}</span>
                                            </div>
                                            <div className="discrepancy-content">
                                              <p><strong>Issue:</strong> {discrepancy.issue}</p>
                                              {discrepancy.expected && (
                                                <p><strong>Expected:</strong> {typeof discrepancy.expected === 'object' ? JSON.stringify(discrepancy.expected) : discrepancy.expected}</p>
                                              )}
                                              {discrepancy.actual && (
                                                <p><strong>Actual:</strong> {typeof discrepancy.actual === 'object' ? JSON.stringify(discrepancy.actual) : discrepancy.actual}</p>
                                              )}
                                              {discrepancy.line_item_id && (
                                                <p><strong>Line Item ID:</strong> {discrepancy.line_item_id}</p>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Show success message when no discrepancies */}
                                    {msg.jsonResponse.details.validation_summary?.validation_passed && (!msg.jsonResponse.details.discrepancies || msg.jsonResponse.details.discrepancies.length === 0) && (
                                      <div className="qa-success-section">
                                        <div className="success-message">
                                          <span className="material-symbols-outlined">check_circle</span>
                                          <h4>Validation Passed!</h4>
                                          <p>Your DV360 campaign configuration matches the Excel launch template perfectly. No discrepancies were found.</p>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}

                                {/* Render Optimization Execution Results */}
                                {msg.action === 'optimization-executor' && (
                                  <>
                                    {/* Execution Summary */}
                                    <div className="optimization-execution-section">
                                      <h3>🎯 DV360 Campaign Optimization Execution Results</h3>

                                      {msg.jsonResponse.success ? (
                                        <div className="execution-success">
                                          <span className="material-symbols-outlined">check_circle</span>
                                          <h4>Overall Status: SUCCESS</h4>
                                          <p>The optimization recommendations have been processed and applied to your DV360 campaign.</p>
                                        </div>
                                      ) : (
                                        <div className="execution-error">
                                          <span className="material-symbols-outlined">error</span>
                                          <h4>Optimization Execution Failed</h4>
                                          <p>There was an issue applying the optimization recommendations to your DV360 campaign.</p>
                                        </div>
                                      )}

                                      {/* Show processed recommendations if available */}
                                      {msg.jsonResponse.details && msg.jsonResponse.details.processed_recommendations && msg.jsonResponse.details.processed_recommendations.length > 0 && (
                                        <div className="processed-recommendations">
                                          <h4>📋 Optimization Summary</h4>
                                          {msg.jsonResponse.details.processed_recommendations.map((rec, recIndex) => (
                                            <div key={recIndex} className="processed-recommendation-item">
                                              <div className="recommendation-header">
                                                <div className="recommendation-status">
                                                  <span className="recommendation-id">#{rec.recommendation_id}</span>
                                                  <span className="recommendation-title">{rec.title}</span>
                                                  <span className={`impact-badge impact-${rec.expected_impact?.toLowerCase()}`}>
                                                    {rec.expected_impact} IMPACT
                                                  </span>
                                                </div>
                                              </div>

                                              <div className="recommendation-description">
                                                <p>{rec.description}</p>
                                              </div>

                                              {rec.affected_line_items && rec.affected_line_items.length > 0 && (
                                                <div className="affected-line-items">
                                                  <p><strong>Affected Line Items:</strong> {rec.affected_line_items.length} line items</p>
                                                  <div className="line-items-list">
                                                    {rec.affected_line_items.slice(0, 5).map((lineItemId, idx) => (
                                                      <span key={idx} className="line-item-id">{lineItemId}</span>
                                                    ))}
                                                    {rec.affected_line_items.length > 5 && (
                                                      <span className="more-items">+{rec.affected_line_items.length - 5} more</span>
                                                    )}
                                                  </div>
                                                </div>
                                              )}

                                              {rec.implementation_steps && rec.implementation_steps.length > 0 && (
                                                <div className="implementation-steps">
                                                  <h5>Implementation Steps:</h5>
                                                  <ol>
                                                    {rec.implementation_steps.map((step, stepIdx) => (
                                                      <li key={stepIdx}>{step}</li>
                                                    ))}
                                                  </ol>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {/* Show update results if available */}
                                      {msg.jsonResponse.details && msg.jsonResponse.details.update_results && (
                                        <div className="update-results-section">
                                          <h4>🔧 Applied Changes by Line Item</h4>

                                          {/* Overall update status */}
                                          <div className="update-status-summary">
                                            <div className={`status-indicator ${msg.jsonResponse.details.update_results.success ? 'success' : 'failed'}`}>
                                              <span className="material-symbols-outlined">
                                                {msg.jsonResponse.details.update_results.success ? 'check_circle' : 'error'}
                                              </span>
                                              <span>
                                                Update Process: {msg.jsonResponse.details.update_results.success ? 'Completed' : 'Failed'}
                                              </span>
                                            </div>
                                          </div>

                                          {/* Line item updates */}
                                          {(() => {
                                            const lineItemUpdates = msg.jsonResponse.details.update_results.results?.line_item_updates ||
                                                                   msg.jsonResponse.details.update_results.line_item_updates || {};

                                            return Object.entries(lineItemUpdates).map(([lineItemId, updates]) => {
                                              const successCount = updates.filter(u => u.success).length;
                                              const failedCount = updates.filter(u => !u.success).length;
                                              const totalCount = updates.length;

                                              return (
                                                <div key={lineItemId} className="line-item-updates">
                                                  <div className="line-item-header">
                                                    <div className="line-item-info">
                                                      <h5>Line Item: {lineItemId}</h5>
                                                      <div className="update-summary">
                                                        <span className="total-updates">{totalCount} updates attempted</span>
                                                        <span className={`success-count ${successCount > 0 ? 'has-success' : ''}`}>
                                                          {successCount} successful
                                                        </span>
                                                        <span className={`failed-count ${failedCount > 0 ? 'has-failed' : ''}`}>
                                                          {failedCount} failed
                                                        </span>
                                                      </div>
                                                    </div>
                                                    <div className="line-item-status">
                                                      {failedCount === 0 ? (
                                                        <span className="status-badge success">All Updates Applied</span>
                                                      ) : successCount === 0 ? (
                                                        <span className="status-badge failed">All Updates Failed</span>
                                                      ) : (
                                                        <span className="status-badge partial">Partially Applied</span>
                                                      )}
                                                    </div>
                                                  </div>

                                                  <div className="updates-list">
                                                    {updates.map((update, updateIdx) => (
                                                      <div key={updateIdx} className={`update-item ${update.success ? 'success' : 'failed'}`}>
                                                        <span className={`status-icon material-symbols-outlined ${update.success ? 'success' : 'error'}`}>
                                                          {update.success ? 'check_circle' : 'error'}
                                                        </span>
                                                        <div className="update-details">
                                                          <div className="update-field">
                                                            <span className="update-key">{update.key}</span>
                                                            {update.value !== undefined && (
                                                              <span className="update-value">
                                                                {typeof update.value === 'object' ?
                                                                  JSON.stringify(update.value, null, 2) :
                                                                  String(update.value)
                                                                }
                                                              </span>
                                                            )}
                                                          </div>
                                                          {update.error && (
                                                            <div className="update-error">
                                                              <span className="error-label">Error:</span>
                                                              <span className="error-text">{update.error}</span>
                                                            </div>
                                                          )}
                                                          {update.reason && (
                                                            <div className="update-reason">
                                                              <span className="reason-label">Reason:</span>
                                                              <span className="reason-text">{update.reason}</span>
                                                            </div>
                                                          )}
                                                          {update.success && update.value !== undefined && (
                                                            <div className="update-success">
                                                              <span className="success-label">Applied:</span>
                                                              <span className="success-text">
                                                                Field updated successfully
                                                              </span>
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    ))}
                                                  </div>
                                                </div>
                                              );
                                            });
                                          })()}
                                        </div>
                                      )}

                                      {/* Show summary statistics */}
                                      {msg.jsonResponse.details && msg.jsonResponse.details.update_results && (
                                        <div className="execution-summary">
                                          <h4>📊 Execution Summary</h4>
                                          {(() => {
                                            const lineItemUpdates = msg.jsonResponse.details.update_results.results?.line_item_updates ||
                                                                   msg.jsonResponse.details.update_results.line_item_updates || {};
                                            const allUpdates = Object.values(lineItemUpdates).flat();
                                            const successCount = allUpdates.filter(u => u.success).length;
                                            const failedCount = allUpdates.filter(u => !u.success).length;
                                            const totalLineItems = Object.keys(lineItemUpdates).length;
                                            const totalUpdates = allUpdates.length;

                                            // Calculate line items with different statuses
                                            const lineItemStats = Object.entries(lineItemUpdates).reduce((acc, [, updates]) => {
                                              const lineItemSuccessCount = updates.filter(u => u.success).length;
                                              const lineItemFailedCount = updates.filter(u => !u.success).length;

                                              if (lineItemFailedCount === 0) {
                                                acc.fullySuccessful++;
                                              } else if (lineItemSuccessCount === 0) {
                                                acc.fullyFailed++;
                                              } else {
                                                acc.partiallySuccessful++;
                                              }
                                              return acc;
                                            }, { fullySuccessful: 0, fullyFailed: 0, partiallySuccessful: 0 });

                                            return (
                                              <div className="summary-stats">
                                                <div className="stat-item">
                                                  <span className="stat-label">Line Items Processed:</span>
                                                  <span className="stat-value">{totalLineItems}</span>
                                                </div>
                                                <div className="stat-item">
                                                  <span className="stat-label">Total Updates Attempted:</span>
                                                  <span className="stat-value">{totalUpdates}</span>
                                                </div>
                                                <div className="stat-item success">
                                                  <span className="stat-label">Successful Updates:</span>
                                                  <span className="stat-value">{successCount}</span>
                                                </div>
                                                <div className="stat-item failed">
                                                  <span className="stat-label">Failed Updates:</span>
                                                  <span className="stat-value">{failedCount}</span>
                                                </div>
                                                <div className="stat-item success">
                                                  <span className="stat-label">Fully Updated Line Items:</span>
                                                  <span className="stat-value">{lineItemStats.fullySuccessful}</span>
                                                </div>
                                                <div className="stat-item partial">
                                                  <span className="stat-label">Partially Updated Line Items:</span>
                                                  <span className="stat-value">{lineItemStats.partiallySuccessful}</span>
                                                </div>
                                                <div className="stat-item failed">
                                                  <span className="stat-label">Failed Line Items:</span>
                                                  <span className="stat-value">{lineItemStats.fullyFailed}</span>
                                                </div>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      )}

                                      {/* Show errors if any */}
                                      {msg.jsonResponse.errors && msg.jsonResponse.errors.length > 0 && (
                                        <div className="execution-errors">
                                          <h4>⚠️ Errors</h4>
                                          {msg.jsonResponse.errors.map((error, errorIndex) => (
                                            <div key={errorIndex} className="error-item">
                                              <span className="material-symbols-outlined">warning</span>
                                              <p>{error}</p>
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {/* Next Steps */}
                                      <div className="next-steps">
                                        <h4>🚀 Next Steps</h4>
                                        <div className="steps-list">
                                          <div className="step-item">
                                            <span className="step-number">1</span>
                                            <span className="step-text">Review the applied changes above</span>
                                          </div>
                                          <div className="step-item">
                                            <span className="step-number">2</span>
                                            <span className="step-text">Manually apply any failed updates in DV360 if needed</span>
                                          </div>
                                          <div className="step-item">
                                            <span className="step-number">3</span>
                                            <span className="step-text">Monitor campaign performance after optimization</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                )}
                                  </>
                                )}


                              </div>
                            ) : (
                              <JsonTable data={msg.jsonResponse} />
                            )
                          )}
                        </div>
                      ) : (
                        <div className="ask-assistant-response">
                          {msg.jsonResponse.response && msg.jsonResponse.response.split(/\b(confidence|score|accuracy|precision|recall)\b/gi).map((part, i) => {
                            const lowerPart = part.toLowerCase();
                            if (lowerPart === 'confidence' || lowerPart === 'score' || lowerPart === 'accuracy' ||
                                lowerPart === 'precision' || lowerPart === 'recall') {
                              return <strong key={i}>{part}</strong>;
                            }
                            return part;
                          })}
                        </div>
                      )
                    )}
                    {/* Only show processing indicator for non-orchestration messages */}
                    {msg.isProcessing && msg.status !== 'failed' && !isUnifiedOrchestrationAction(msg.action) && (
                      <div className="processing-indicator">
                        <div className="loading-dots">
                          <span key="dot-1"></span>
                          <span key="dot-2"></span>
                          <span key="dot-3"></span>
                        </div>
                        <div className="message-text">
                          <div>{msg.content}</div>
                          <div className="processing-id">Processing ID: {msg.chatId}_{msg.messageId}</div>
                        </div>
                      </div>
                    )}
                    {(msg.isError || msg.status === 'failed') && (
                      <div className="error-indicator">
                        {/* Rate-limit retry notices use status 'failed' to mean
                            "retries exhausted" — their content IS the error
                            (attempt count + quota message), so show it instead
                            of the generic timeout boilerplate. */}
                        <div className="message-text">
                          {msg.action === 'rate-limit-retry' && msg.content
                            ? msg.content
                            : <>The request has timed out. Please contact support referencing processing ID: {msg.chatId}_{msg.id}</>}
                        </div>
                      </div>
                    )}
                    {msg.type === 'user' && msg.file_metadata && (() => {
                      // Helper to check if file metadata is valid (has name and size)
                      const isValidFileMetadata = (fm) => fm && fm.name && typeof fm.size === 'number' && !isNaN(fm.size);

                      // Filter out invalid file metadata
                      const validMetadata = Array.isArray(msg.file_metadata)
                        ? msg.file_metadata.filter(isValidFileMetadata)
                        : (isValidFileMetadata(msg.file_metadata) ? msg.file_metadata : null);

                      // Don't render anything if no valid metadata
                      if (!validMetadata || (Array.isArray(validMetadata) && validMetadata.length === 0)) {
                        return null;
                      }

                      return (
                      <>
                        {Array.isArray(validMetadata) ? (
                          // Handle array of file metadata
                          validMetadata.map((fileMetadata, fileIndex) => (
                            <div className="message-file-preview" key={fileIndex}>
                              <span className="material-symbols-outlined">description</span>
                              <div className="message-file-name">
                                {fileMetadata.expiresAt && new Date(fileMetadata.expiresAt) < new Date() ? (
                                  <>
                                    <span className="file-name">
                                      {fileMetadata.name} ({Math.round(fileMetadata.size / 1024)}KB)
                                    </span>
                                    <span className="file-link-expired">
                                      - Preview link expired
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <a href={fileMetadata.publicUrl} target="_blank" rel="noopener noreferrer">
                                      {fileMetadata.name} ({Math.round(fileMetadata.size / 1024)}KB)
                                    </a>
                                    {fileMetadata.expiresAt && (
                                      <span className="file-expiry">
                                        (expires: {formatTimestamp(fileMetadata.expiresAt)})
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          // Handle single file metadata object
                          <div className="message-file-preview">
                            <span className="material-symbols-outlined">description</span>
                            <div className="message-file-name">
                              {validMetadata.expiresAt && new Date(validMetadata.expiresAt) < new Date() ? (
                                <>
                                  <span className="file-name">
                                    {validMetadata.name} ({Math.round(validMetadata.size / 1024)}KB)
                                  </span>
                                  <span className="file-link-expired">
                                    - Preview link expired
                                  </span>
                                </>
                              ) : (
                                <>
                                  <a href={validMetadata.publicUrl} target="_blank" rel="noopener noreferrer">
                                    {validMetadata.name} ({Math.round(validMetadata.size / 1024)}KB)
                                  </a>
                                  {validMetadata.expiresAt && (
                                    <span className="file-expiry">
                                      (expires: {formatTimestamp(validMetadata.expiresAt)})
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                      );
                    })()}
                    {msg.references?.length > 0 && (
                      <div className="message-references-preview">
                        <span className="material-symbols-outlined">link</span>
                        <div className="message-references-info">
                          <span className="references-count">
                            {msg.references.length === 1 ? '1 reference' : `${msg.references.length} references`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {msg.type === 'bot' && msg.jsonResponse && !msg.isProcessing && !msg.isError && msg.status !== 'failed' && (
                    <MessageActionButtons
                      message={msg}
                      isReferenced={isReferenced}
                      isExporting={isExporting}
                      user={user}
                      handleJsonDownload={handleJsonDownload}
                      handleOpenJsonInNewTab={handleOpenJsonInNewTab}
                      handleExportToSheets={handleExportToSheets}
                      handleApproveResult={handleApproveResult}
                      handleRejectResult={handleRejectResult}
                      handleAskAssistantClick={handleAskAssistantClick}
                      toggleReference={toggleReference}
                      toggleTableCollapse={() => toggleTableCollapse(index)}
                      isTableCollapsed={collapsedTables.has(index)}
                      handleEditCampaign={(messageId, message, feedback, originalMessage) => {
                        setEditDialog({
                          open: true,
                          messageId,
                          message,
                          feedback,
                          isProcessing: false,
                          sourceContent: originalMessage ? originalMessage.content : '',
                          file_metadata: originalMessage ? originalMessage.file_metadata : null,
                          references: originalMessage ? originalMessage.references : []
                        });
                      }}
                      handleEditCreative={(messageId, message, feedback, originalMessage) => {
                        setEditDialog({
                          open: true,
                          messageId,
                          message,
                          feedback,
                          isProcessing: false,
                          sourceContent: originalMessage ? originalMessage.content : '',
                          file_metadata: originalMessage ? originalMessage.file_metadata : null,
                          references: originalMessage ? originalMessage.references : [],
                          editType: 'creative'
                        });
                      }}
                      handleEditLaunchConfiguration={(messageId, message, feedback, originalMessage) => {
                        setEditDialog({
                          open: true,
                          messageId,
                          message,
                          feedback,
                          isProcessing: false,
                          sourceContent: originalMessage ? originalMessage.content : '',
                          file_metadata: originalMessage ? originalMessage.file_metadata : null,
                          references: originalMessage ? originalMessage.references : [],
                          editType: 'launch-configuration'
                        });
                      }}
                      handleGetOriginalMessage={(_, sourceMessageId) => {
                        return messages.find(m => m.id === sourceMessageId);
                      }}
                      setReferences={setReferences}
                      setInputMessage={setInputMessage}
                    />
                  )}
                </div>
                {/* Only render follow-up messages for bot messages */}
                {msg.type === 'bot' && msg.jsonResponse && !msg.isProcessing && !msg.isError && msg.action && (
                  <div className="follow-up-messages">
                    {(() => {
                      // Try to find follow-up messages using combined action and subAction
                      const combinedKey = msg.subAction ? `${msg.action}__${msg.subAction}` : null;
                      const followUpMessages = combinedKey && FOLLOW_UP_MESSAGES[combinedKey] ?
                        FOLLOW_UP_MESSAGES[combinedKey] :
                        FOLLOW_UP_MESSAGES[msg.action];

                      return followUpMessages && followUpMessages.length > 0 ? (
                        <React.Fragment key={`follow-up-${msg.id}`}>
                          <div className="follow-up-header">
                            <span>Suggested next steps:</span>
                          </div>
                          <div className="follow-up-suggestions">
                            {followUpMessages.map((suggestion, idx) => (
                              <div key={idx} className="follow-up-item">
                                {suggestion}
                              </div>
                            ))}
                          </div>
                        </React.Fragment>
                      ) : null;
                    })()}
                  </div>
                )}
            </div>
          );
        })}
        {isSendingMessage && (
          <div className="message system">
            <div className="message-timestamp">
              {formatMessageTimestamp(new Date().toISOString())}
            </div>
            <div className="message-content">
              <span className="loading-indicator">
                <span key="send-icon" className="material-symbols-outlined loading-spin">send</span>
                <span key="send-text" className="loading-text message-text">Sending message...</span>
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="input-container">
        {references.length > 0 && (
          <div className="references-indicator">
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>link</span>
            <span>{references.length} reference{references.length !== 1 ? 's' : ''}</span>
            <div className="reference-previews">
              {references.map(ref => {
                // Handle message references
                if (ref.type === "message") {
                  const referencedMessage = messages.find(msg => msg.id === ref.messageId);
                  if (!referencedMessage) return null;

                  const preview = typeof referencedMessage.content === 'string'
                    ? referencedMessage.content.substring(0, 40)
                    : JSON.stringify(referencedMessage.content).substring(0, 40);

                  return (
                    <div key={ref.messageId} className="reference-preview">
                      <span className="preview-text">{preview}{preview.length >= 40 ? '...' : ''}</span>
                      <button
                        onClick={() => toggleReference(ref.messageId)}
                        className="remove-reference"
                        title="Remove reference"
                      >
                        ×
                      </button>
                    </div>
                  );
                }
                return null;
              })}
            </div>
            <button onClick={() => setReferences([])} className="remove-all-references" title="Remove all references">
              Clear
            </button>
          </div>
          )}
        <textarea
          className={`message-input ${!isReady ? 'disabled' : ''}`}
          value={inputMessage}
          onChange={(e) => {
            if (!isReady) return;
            setInputMessage(e.target.value);
            // Auto-resize the textarea
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
          }}
          onKeyDown={isReady ? handleKeyPress : undefined}
          placeholder={
            !isReady
              ? "Select an account to start messaging"
              : isValidating
                ? "Validating..."
                : "Message"
          }
          rows={1}
          disabled={!isReady}
          style={{ minHeight: '56px', maxHeight: '200px', overflowY: 'auto' }}
        />
        <div className="button-row">
          <div className="dropdown-container" ref={dropdownRef}>
            <button
              className={`dropdown-button ${!isReady ? 'disabled' : ''}`}
              onClick={isReady ? () => setDropdownOpen(!dropdownOpen) : undefined}
            >
              <span className="material-symbols-outlined">more_horiz</span>
            </button>
            {dropdownOpen && (
              <div className="dropdown-menu">
                {filteredDropdownItems.map(item => (
                  <button
                    key={item.id}
                    className="dropdown-item"
                    onClick={() => handleDropdownItemClick(item)}
                  >
                    <span className="material-symbols-outlined">{item.icon}</span>
                    {item.text}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* File uploads */}
          {uploadedFiles.length > 0 && (
            <div className="files-preview">
              <div className="files-preview-row">
                {uploadedFiles.map((file, index) => {
                  const hasErrors = file.validation && !file.validation.valid && file.validation.errors?.length > 0;
                  const hasWarnings = file.validation && file.validation.warnings?.length > 0;

                  return (
                    <div key={index} className="file-preview" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <a
                          href={file.publicUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="file-name"
                        >
                          {file.name}
                        </a>
                        <span
                          className="remove-file"
                          onClick={() => removeFile(index)}
                        >
                          x
                        </span>
                      </div>
                      {hasErrors && (
                        <div style={{
                          marginTop: '6px',
                          fontSize: '12px',
                          color: '#d32f2f',
                          backgroundColor: '#ffebee',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          borderLeft: '3px solid #d32f2f'
                        }}>
                          {file.validation.errors.map((error, i) => (
                            <div key={i} style={{ marginBottom: i < file.validation.errors.length - 1 ? '4px' : '0', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                              <span style={{ flexShrink: 0 }}>❌</span>
                              <span>{error}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {hasWarnings && !hasErrors && (
                        <div style={{
                          marginTop: '6px',
                          fontSize: '12px',
                          color: '#f57c00',
                          backgroundColor: '#fff3e0',
                          padding: '6px 10px',
                          borderRadius: '4px',
                          borderLeft: '3px solid #f57c00'
                        }}>
                          {file.validation.warnings.map((warning, i) => (
                            <div key={i} style={{ marginBottom: i < file.validation.warnings.length - 1 ? '4px' : '0', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                              <span style={{ flexShrink: 0 }}>⚠️</span>
                              <span>{warning}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {uploadedFiles.length >= 2 && (
                  <button className="clear-all-button" onClick={resetFileInput}>
                    Clear all
                  </button>
                )}
              </div>
            </div>
          )}
          <input
            type="file"
            id="file-upload"
            className="hidden"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files);
              if (files.length > 0) {
                handleFileSelect(files);
              }
              // Reset the input value to allow re-uploading the same file
              e.target.value = '';
            }}
            disabled={isUploading || !canUploadAttachment(user) || !isReady}
          />
          <label
            htmlFor="file-upload"
            className={`icon-button file-upload-button ${isUploading || isValidating ? 'uploading' : ''} ${(!canUploadAttachment(user) || !isReady) ? 'disabled' : ''}`}
          >
            <span className="material-symbols-outlined">
              {isUploading || isValidating ? 'sync' : 'attach_file'}
            </span>
          </label>
          {isValidating && (
            <span style={{ fontSize: '14px', color: '#666', display: 'inline-flex', alignItems: 'center' }}>
              Validating...
            </span>
          )}
          <button
            className={`icon-button briefcase-toggle ${briefcaseVisible ? 'active' : ''} ${(!canSendMessage(user) || !isReady) ? 'disabled' : ''}`}
            onClick={isReady ? handleToggleBriefcase : undefined}
            title={briefcaseVisible ? "Hide briefcase" : "Show briefcase"}
          >
            <span className="material-symbols-outlined">
              {briefcaseVisible ? 'chevron_right' : 'work'}
            </span>
          </button>
          <button
            className={`icon-button send-button ${isSendMessageDisabled ? 'disabled' : ''}`}
            onClick={handleSendMessage}
            disabled={isSendMessageDisabled}
          >
            <span className="material-symbols-outlined">send</span>
          </button>
        </div>
      </div>

      {/* Briefcase Panel as a Workflow Tab */}
      {briefcaseVisible && (
        <div className="workflow-tab-container">
          <BriefcasePanel
            files={briefcaseFiles}
            onNavigateToMessage={handleNavigateToMessage}
            onReferenceInChat={handleReferenceInChat}
            onRenameFile={handleRenameFile}
            onTogglePin={handleTogglePin}
          />
        </div>
      )}

      {rejectionDialog.open && ReactDOM.createPortal(
        <div className="rejection-dialog-overlay" key="rejection-dialog-overlay">
          <div className="rejection-dialog">
            <h3>Help Us Improve</h3>
            <p>Please help us understand what was wrong with the response so we can improve:</p>
            <ul className="rejection-guidelines">
              <li key="guideline-1">Point out any errors or inaccuracies in the response</li>
              <li key="guideline-2">Explain what information or relationships were missing or incorrect</li>
              <li key="guideline-3">Suggest what the correct values or format should be</li>
              <li key="guideline-4">Provide any additional context that would help generate a better response</li>
            </ul>
            <textarea
              className="rejection-reason"
              placeholder="Please provide specific feedback about what was wrong with the response..."
              rows={4}
              value={rejectionDialog.reason}
              onChange={(e) => setRejectionDialog(prev => ({ ...prev, reason: e.target.value }))}
              disabled={rejectionDialog.isRegenerating}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  e.preventDefault();
                  if (rejectionDialog.reason.trim() && !rejectionDialog.isRegenerating) {
                    handleRejectAndRegenerate();
                  }
                }
              }}
            />
            <div className="rejection-dialog-buttons">
              <button
                className="cancel-button"
                onClick={() => resetRejectionDialog()}
                disabled={rejectionDialog.isRegenerating}
              >
                Cancel
              </button>
              <button
                className="reject-button"
                onClick={handleRejectAndRegenerate}
                disabled={!rejectionDialog.reason.trim() || rejectionDialog.isRegenerating}
              >
                {rejectionDialog.isRegenerating ? (
                  <div className="loading-indicator" key="loading-indicator">
                    <span className="material-symbols-outlined loading-spin">progress_activity</span>
                  </div>
                ) : (
                  <span key="regenerate-text">Regenerate with Feedback</span>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {askAssistantDialog.open && ReactDOM.createPortal(
        <div className="rejection-dialog-overlay" key="ask-assistant-dialog-overlay">
          <div className="rejection-dialog">
            <h3>Ask Assistant</h3>
            <p>What would you like to ask the assistant?</p>
            <textarea
              className="rejection-reason"
              placeholder="Type your question here..."
              rows={4}
              value={askAssistantDialog.question}
              onChange={(e) => setAskAssistantDialog(prev => ({ ...prev, question: e.target.value }))}
              disabled={askAssistantDialog.isAsking}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  e.preventDefault();
                  if (askAssistantDialog.question.trim() && !askAssistantDialog.isAsking) {
                    handleAskAssistant();
                  }
                }
              }}
            />
            <div className="rejection-dialog-buttons">
              <button
                className="cancel-button"
                onClick={resetAskAssistantDialog}
                disabled={askAssistantDialog.isAsking}
              >
                Cancel
              </button>
              <button
                className="ask-button"
                onClick={handleAskAssistant}
                disabled={!askAssistantDialog.question.trim() || askAssistantDialog.isAsking}
              >
                {askAssistantDialog.isAsking ? (
                  <div className="loading-indicator" key="loading-indicator">
                    <span className="material-symbols-outlined loading-spin">progress_activity</span>
                  </div>
                ) : (
                  <span key="ask-assistant-text">Ask Assistant</span>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Dialog for Campaign, Creative, or Launch Configuration */}
      {editDialog.open && ReactDOM.createPortal(
        <div className="rejection-dialog-overlay" key="edit-dialog-overlay">
          <div className="rejection-dialog">
            <h3>
              {editDialog.editType === 'creative'
                ? 'Update Creative Details'
                : editDialog.editType === 'launch-configuration'
                  ? 'Update Launch Configuration Details'
                  : 'Update Campaign Details'}
            </h3>
            <p>Please provide specific instructions for what to change:</p>
            <textarea
              className="rejection-reason"
              placeholder={`Specify what changes you'd like to make to the ${
                editDialog.editType === 'creative'
                  ? 'creative'
                  : editDialog.editType === 'launch-configuration'
                    ? 'launch configuration'
                    : 'campaign'
              }...`}
              rows={6}
              value={editDialog.feedback}
              onChange={(e) => setEditDialog(prev => ({ ...prev, feedback: e.target.value }))}
              disabled={editDialog.isProcessing}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  e.preventDefault();
                  if (editDialog.feedback.trim() && !editDialog.isProcessing) {
                    handleEditCampaign();
                  }
                }
              }}
            />
            <div className="rejection-dialog-buttons">
              <button
                className="cancel-button"
                onClick={resetEditDialog}
                disabled={editDialog.isProcessing}
              >
                Cancel
              </button>
              <button
                className="edit-button"
                onClick={() => {
                  if (editDialog.editType === 'creative') {
                    handleEditCreative();
                  } else if (editDialog.editType === 'launch-configuration') {
                    handleEditLaunchConfiguration();
                  } else {
                    handleEditCampaign();
                  }
                }}
                disabled={!editDialog.feedback.trim() || editDialog.isProcessing}
              >
                {editDialog.isProcessing ? (
                  <div className="loading-indicator" key="loading-indicator">
                    <span className="material-symbols-outlined loading-spin">progress_activity</span>
                  </div>
                ) : (
                  <span key="edit-text">Apply Changes</span>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Export the component wrapped with the account selection HOC
export default withAccountSelection(Chat);
